import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  MapPin,
  Phone,
  Briefcase,
  Users,
  Baby,
  PenTool,
  CheckCircle,
  AlertCircle,
  Trash2,
  Plus,
  ChevronRight,
  ChevronLeft,
  FileText,
  DollarSign,
  Home,
  Car,
  TrendingUp,
  CreditCard,
  Sun,
  Plane,
  Upload,
  ShieldAlert,
  Download,
  Eye,
  EyeClosed,
  X
} from 'lucide-react';
import { Button, Card, Input, InputLabel, Select, Modal, StatusBadge } from './DesignSystem';
import { useShellProfile } from '../../../state/useShellProfile';
import { useGetSettingsModuleConfigQuery } from '../../../state/domainRouterApi';
import { parseSettingsState } from '../../../settings/settingsState';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import {
    useClaimFinancialAidInvite,
    useConfirmFinancialAidDocumentUpload,
    useCreateFinancialAidApplication,
    useFinancialAidApplication,
    useFinancialAidByApplicant,
    useFinancialAidDocumentStatus,
    useFinancialAidOpenCycle,
    useFinancialAidParentStatus,
    useGenerateFinancialAidApplicationReport,
    useGetFinancialAidApplicationReportDownloadUrl,
    useRecordFinancialAidInviteOpen,
    useRespondToInfoRequest,
    useSubmitFinancialAidApplication,
    useUpdateFinancialAidApplication,
    useUploadFinancialAidDocument,
} from '../../../financial-aid/hooks/useFinancialAidApi';
import {
    clearParentFinancialAidPointerStorageFrom,
    clampParentFinancialAidStep,
    readParentFinancialAidPointer,
    readParentFinancialAidPointerWithFallback,
    readParentFinancialAidStoredAttestations,
    resolveLegacyParentFinancialAidPointerStorageKey,
    resolveParentFinancialAidPointerStorageKey,
    selectParentFinancialAidPointerStorage,
    writeParentFinancialAidPointer,
    writeParentFinancialAidStoredAttestations,
} from '../../../financial-aid/parentFinancialAidStorage';
import {
    isParentFinancialAidExistingApplicationStateStatus,
    isParentFinancialAidApplicationResumable,
    canDownloadParentFinancialAidApplicationCopy,
    isParentFinancialAidAcademicYearAfter,
    formatParentFinancialAidStatusLabel,
    normalizeParentFinancialAidStatusToken,
    resolveCurrentParentFinancialAidApplicationSummary,
    resolveParentFinancialAidExistingApplicationState,
    selectCurrentParentFinancialAidApplication,
    summarizeParentFinancialAidApplications,
    summarizeParentFinancialAidApplication,
    type ParentFinancialAidApplicationLike,
    type ParentFinancialAidApplicationSummary,
} from '../../../financial-aid/parentFinancialAidApplicationSummary';
import {
    doFinancialAidAcademicYearsMatch,
    resolveEffectiveFinancialAidAcademicYear,
    resolveFinancialAidDisplayAcademicYear,
    resolveFinancialAidDisplayYears,
    resolveFinancialAidCycleStatusFromSettings,
} from '../../../financial-aid/financialAidAcademicYear';
import { buildBrowserUploadHeaders } from '../../../financial-aid/utils/uploadHeaders';
import {
    getParentFinancialAidFirstIssueStep,
    getParentFinancialAidStepValidationIssues,
    getParentFinancialAidValidationIssues,
    isParentFinancialAidParentInfoComplete,
    shouldParentFinancialAidParentRequireSignature,
    shouldParentFinancialAidParentShowRequiredLabels,
    shouldParentFinancialAidShowConfiguredField,
    shouldParentFinancialAidShowConfiguredRequiredLabel,
    type ParentFinancialAidValidationIssue,
} from './financialAidValidation';
import { resolveParentFinancialAidAttestationText } from './financialAidAttestations';
import {
    createFinancialAidActionErrorReport,
    formatParentFinancialAidSubmitErrorHeadline,
    readFinancialAidActionErrorReport,
    readParentFinancialAidSubmitErrorReport,
    type FinancialAidActionErrorReport,
} from './financialAidActionErrors';
import {
    getFinancialAidUploadFileValidationError,
    inferFinancialAidUploadFileType,
} from '../../../financial-aid/financialAidUploadFile';
import {
    hasUnsavedFormData,
    useUnsavedBrowserNavigationGuard,
} from '../../../shared/forms/useUnsavedBrowserNavigationGuard';
import {
    areParentFinancialAidDocumentsEqual,
    areRequiredParentFinancialAidDocumentsUploaded,
    buildConfiguredParentFinancialAidDocuments,
    buildParentFinancialAidDocumentsFromRequiredTypes,
    getDefaultParentFinancialAidDocuments,
    normalizeParentFinancialAidDocumentId,
    reconcileParentFinancialAidDocuments,
    resolveParentFinancialAidDocumentName,
    type ParentFinancialAidDocumentStatus,
} from './financialAidDocuments';
import {
    hasHydrationBlockingUnsavedParentFinancialAidData,
    resolveHydratedParentFinancialAidAttestations,
    resolveParentFinancialAidApplicationHydrationKey,
    shouldPreserveMoreCompleteLocalFinancialAidData,
} from './financialAidHydrationGuards';
import {
    didParentFinancialAidCopyCompleteAttestations,
    formatParentFinancialAidCopyCertificationDate,
    formatParentFinancialAidCopyCertificationStatus,
    formatParentFinancialAidCopySignatureStatus,
    resolveParentFinancialAidCopyCertificationName,
    resolveParentFinancialAidCopySignatureStatus,
    type ParentFinancialAidCopyCertificationContext,
} from './financialAidCopyCertification';
import { clearParentFinancialAidHiddenRequirementValues } from './financialAidVisibilityPolicy';
import {
    openPdfUrlInFallbackShell,
    openSectionedPdfInNewTab,
    type SectionedPdfRow,
    type SectionedPdfSection,
} from '../../../utils/pdf';
import {
    FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE,
    FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_MESSAGE,
    formatFinancialAidSocialSecurityNumberInput,
    isFinancialAidInviteConfirmationUnavailableCode,
    isValidFinancialAidSocialSecurityNumber,
    readFinancialAidSocialSecurityLast4,
} from '@triedge/financial-aid-domain/value-objects';
import {
    PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS,
    DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION,
    isParentGuardianFormKeyActive,
    normalizeParentGuardianHouseholdComposition,
    resolveParentGuardianActiveKeys,
    resolveParentGuardianDisplayLabel,
    resolveParentGuardianHouseholdComposition,
    resolveParentGuardianPersistenceRelationship,
    type ParentGuardianDisplayRole,
    type ParentGuardianFormKey,
    type ParentGuardianHouseholdComposition,
    type ParentGuardianPersistenceRelationship,
} from '../../../shared/parentGuardianHousehold';

// --- Types ---

type FinancialAidCopyOpenMode = 'download' | 'printable';
type CompletedMoreInfoResponseMarker = {
    phase: 'pending_return' | 'returned';
    completedAtMs: number;
    returnedAtMs?: number;
    answeredRequestKey?: string;
};
type StoredCompletedMoreInfoResponseMarker = CompletedMoreInfoResponseMarker & {
    version: 1;
};
type FinancialAidStatusObservation = {
    applicationId: string;
    statusToken: string;
    observedAtMs: number;
    moreInfoRequestKey: string;
};
type FinancialAidStatusCandidateSource =
    | 'parent-status'
    | 'status-view-application'
    | 'loaded-application-detail'
    | 'application-summary';
type FinancialAidStatusCandidate = {
    source: FinancialAidStatusCandidateSource;
    statusToken: string;
    observedAtMs: number;
    allowTimestamplessOverride?: boolean;
    preferOnEqualTimestamp?: boolean;
    hasMoreInfoRequestContext?: boolean;
};
const FINANCIAL_AID_STATUS_CANDIDATE_SOURCE_RANK: Record<FinancialAidStatusCandidateSource, number> = {
    'parent-status': 4,
    'status-view-application': 3,
    'loaded-application-detail': 2,
    'application-summary': 1,
};
const COMPLETED_MORE_INFO_RESPONSE_STORAGE_PREFIX = 'ystnj:parent-financial-aid:more-info-response:v1';
const COMPLETED_MORE_INFO_RESPONSE_STORAGE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const COMPLETED_MORE_INFO_RESPONSE_STALE_RETURN_GRACE_MS = 5 * 60 * 1000;
const COMPLETED_MORE_INFO_RESPONSE_STATUS_TOKENS = new Set([
    'under_review',
    'approved',
    'denied',
    'award_accepted',
    'award_declined',
    'accepted',
    'declined',
    'closed',
]);
const COMPLETED_MORE_INFO_RESPONSE_CLEAR_STATUS_TOKENS = new Set([
    'approved',
    'denied',
    'award_accepted',
    'award_declined',
    'accepted',
    'declined',
    'closed',
]);

interface ParentInfo {
    title: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    cellPhone: string;
    homePhone: string;
    email: string;
    occupation: string;
    employer: string;
    businessName: string;
    shul: string;
    rabbi: string;
    isUnemployed: boolean;
    unemploymentBenefits: string;
    isDivorced: boolean;
    ssn: string;
    ssnLast4OnFile: string;
    ssnLast4ClearRequested: boolean;
}

interface IncomeInfo {
    gross2024: string;
    parsonage: string;
    fedTax: string;
    stateTax: string;
    ficaTax: string;
    refunds: string;
    estimated2025: string;
}

interface BenefitsInfo {
    healthInsurance: string;
    tuition: string;
    auto: string;
    other: string;
    gifts: boolean;
    giftsAmount: string;
}

interface AssetInfo {
    housingType: 'own' | 'rent';
    purchasePrice: string;
    purchaseDate: string;
    mortgageBalance: string;
    mortgagePayment: string;
    mortgageYears: string;
    propertyTaxes: string;
    interestRate: string;
    rentPayment: string;
    utilities: string;
    additionalProperties: string;
}

interface Vehicle {
    id: string;
    year: string;
    make: string;
    model: string;
    value: string;
    debt: string;
    payment: string;
}

interface Investment {
    id: string;
    description: string;
    value: string;
}

interface BankAccount {
    bank: string;
    accountNumber: string;
    balance: string;
}

interface ExpenseInfo {
    housing: string;
    taxesIncluded: boolean;
    utilities: string;
    studentLoans: string;
    food: string;
    tuition: string;
    bankLoans: string;
    lifeInsurance: string;
    shulDues: string;
    carInsurance: string;
    healthInsurance: string;
    cleaningHelp: string;
    other: string;
    medicalCoverage: string;
    medicalCoverageDetails: string;
}

interface OtherChild {
    id: string;
    name: string;
    age: string;
    grade: string;
    school: string;
    fullTuition: string;
    scholarship: string;
}

interface MajorExpenditure {
    homeImprovements: string;
    homeImprovementsDetails: string;
    largePurchases: string;
    largePurchasesDetails: string;
    extraordinaryObligations: string;
    simchaType: string;
    simchaCost: string;
    servicesToYST: boolean;
    servicesDescription: string;
    loanAmount: string;
}

interface Camp {
    id: string;
    childName: string;
    campName: string;
    fee: string;
}

interface Vacation {
    traveled: boolean;
    destinations: string;
    cost: string;
    parentsTraveled: boolean;
    childrenTraveled: boolean;
}

interface ContractInfo {
    childrenCount: number;
    totalTuition: string;
    totalFees: string;
    preschoolTuition: string;
    eligibleTuition: string; // Calc
}

interface ScholarshipRequest {
    requestedAmount: string;
    offeredTuition: string;
    offeredFees: string;
}

type DocumentStatus = ParentFinancialAidDocumentStatus;

const SETTINGS_MODULE_KEY = 'staff_settings';
const PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY = 'parentCollectedFinancialAidSnapshot';
const PARENT_FINANCIAL_AID_STEP_LABELS = ["Parents", "Income", "Assets", "Expenses", "Siblings", "Lifestyle", "Tuition", "Docs", "Review"] as const;
const DOCUMENTS_STEP_INDEX = PARENT_FINANCIAL_AID_STEP_LABELS.indexOf("Docs");


type FinancialAidUploadInfo = {
    uploadUrl?: string;
    s3Key?: string;
    documentId?: string;
    uploadHeaders?: Record<string, string>;
};

type FinancialAidDocumentStatusItem = {
    documentType?: string;
    isUploaded?: boolean;
    fileName?: string | null;
};

type FinancialAidDocumentStatusResult = {
    items?: FinancialAidDocumentStatusItem[];
    allRequiredUploaded?: boolean;
};

interface FinancialAidData {
    householdComposition: ParentGuardianHouseholdComposition;
    father: ParentInfo;
    mother: ParentInfo;
    incomeFather: IncomeInfo;
    incomeMother: IncomeInfo;
    benefits: BenefitsInfo;
    assets: AssetInfo;
    vehicles: Vehicle[];
    investments: Investment[];
    savings: BankAccount;
    checking: BankAccount;
    childrenAssets: Investment[];
    expenses: ExpenseInfo;
    otherChildren: OtherChild[];
    majorExp: MajorExpenditure;
    camps2025: Camp[];
    camps2024: Camp[];
    vacation: Vacation;
    contract: ContractInfo;
    request: ScholarshipRequest;
    documents: DocumentStatus[];
    signatures: {
        father: string | null;
        mother: string | null;
        fatherCaptured?: boolean;
        motherCaptured?: boolean;
        date: string;
        attestations: { [key: string]: boolean };
    };
}

const buildParentFinancialAidRawSnapshot = (
    data: FinancialAidData,
    certification?: ParentFinancialAidCopyCertificationContext | null,
): Record<string, unknown> => {
    const householdComposition = normalizeParentGuardianHouseholdComposition(data.householdComposition);
    const activeParentKeys = resolveParentGuardianActiveKeys(householdComposition);
    const isFatherActive = activeParentKeys.includes('father');
    const isMotherActive = activeParentKeys.includes('mother');

    return {
        version: 1,
        householdComposition,
        ...(isFatherActive ? {
            father: {
                title: data.father.title,
                firstName: data.father.firstName,
                lastName: data.father.lastName,
                address: data.father.address,
                city: data.father.city,
                state: data.father.state,
                zip: data.father.zip,
                cellPhone: data.father.cellPhone,
                homePhone: data.father.homePhone,
                email: data.father.email,
                occupation: data.father.occupation,
                employer: data.father.employer,
                businessName: data.father.businessName,
                shul: data.father.shul,
                rabbi: data.father.rabbi,
                isUnemployed: data.father.isUnemployed,
                isDivorced: data.father.isDivorced,
                unemploymentWeeklyAmount: data.father.unemploymentBenefits,
            },
            incomeFather: { ...data.incomeFather },
        } : {}),
        ...(isMotherActive ? {
            mother: {
                title: data.mother.title,
                firstName: data.mother.firstName,
                lastName: data.mother.lastName,
                address: data.mother.address,
                city: data.mother.city,
                state: data.mother.state,
                zip: data.mother.zip,
                cellPhone: data.mother.cellPhone,
                homePhone: data.mother.homePhone,
                email: data.mother.email,
                occupation: data.mother.occupation,
                employer: data.mother.employer,
                businessName: data.mother.businessName,
                shul: data.mother.shul,
                rabbi: data.mother.rabbi,
                isUnemployed: data.mother.isUnemployed,
                isDivorced: data.mother.isDivorced,
                unemploymentWeeklyAmount: data.mother.unemploymentBenefits,
            },
            incomeMother: { ...data.incomeMother },
        } : {}),
        benefits: { ...data.benefits },
        assets: {
            housingType: data.assets.housingType,
            purchasePrice: data.assets.purchasePrice,
            purchaseDate: data.assets.purchaseDate,
            mortgageBalance: data.assets.mortgageBalance,
            mortgagePayment: data.assets.mortgagePayment,
            mortgageYears: data.assets.mortgageYears,
            propertyTaxes: data.assets.propertyTaxes,
            interestRate: data.assets.interestRate,
            rentPayment: data.assets.rentPayment,
            utilities: data.assets.utilities,
            additionalProperties: data.assets.additionalProperties,
        },
        vehicles: data.vehicles.map((vehicle) => ({ ...vehicle })),
        investments: data.investments.map((investment) => ({ ...investment })),
        savings: { ...data.savings },
        checking: { ...data.checking },
        childrenAssets: data.childrenAssets.map((asset) => ({ ...asset })),
        expenses: {
            housing: data.expenses.housing,
            taxesIncluded: data.expenses.taxesIncluded,
            utilities: data.expenses.utilities,
            studentLoans: data.expenses.studentLoans,
            food: data.expenses.food,
            tuition: data.expenses.tuition,
            bankLoans: data.expenses.bankLoans,
            lifeInsurance: data.expenses.lifeInsurance,
            shulDues: data.expenses.shulDues,
            carInsurance: data.expenses.carInsurance,
            healthInsurance: data.expenses.healthInsurance,
            cleaningHelp: data.expenses.cleaningHelp,
            other: data.expenses.other,
            medicalCoverage: data.expenses.medicalCoverage,
            medicalCoverageDetails: data.expenses.medicalCoverageDetails,
        },
        majorExp: {
            homeImprovements: data.majorExp.homeImprovements,
            homeImprovementsDetails: data.majorExp.homeImprovementsDetails,
            largePurchases: data.majorExp.largePurchases,
            largePurchasesDetails: data.majorExp.largePurchasesDetails,
            extraordinaryObligations: data.majorExp.extraordinaryObligations,
            simchaType: data.majorExp.simchaType,
            simchaCost: data.majorExp.simchaCost,
            servicesToYST: data.majorExp.servicesToYST,
            servicesDescription: data.majorExp.servicesDescription,
            loanAmount: data.majorExp.loanAmount,
        },
        otherChildren: data.otherChildren.map((child) => ({ ...child })),
        camps2025: data.camps2025.map((camp) => ({ ...camp })),
        camps2024: data.camps2024.map((camp) => ({ ...camp })),
        contract: {
            childrenCount: data.contract.childrenCount,
            totalTuition: data.contract.totalTuition,
            totalFees: data.contract.totalFees,
            preschoolTuition: data.contract.preschoolTuition,
            eligibleTuition: data.contract.eligibleTuition,
        },
        request: { ...data.request },
        vacation: { ...data.vacation },
        signatures: {
            ...(isFatherActive ? {
                fatherCaptured: Boolean(data.signatures.father),
                fatherCapturedAt: data.signatures.father ? readText(certification?.certificationDate) : '',
            } : {}),
            ...(isMotherActive ? {
                motherCaptured: Boolean(data.signatures.mother),
                motherCapturedAt: data.signatures.mother ? readText(certification?.certificationDate) : '',
            } : {}),
            date: data.signatures.date,
            attestations: { ...data.signatures.attestations },
        },
    };
};

// --- Default Form State ---
// Protocol: production build must not ship with seeded personal data.
// The camp sections always show one starter row (school office request):
// parents see the expected columns and the school can mark the first row
// required via the requirement-policy settings.
const createEmptyCampRow = (): Camp => ({
    id: crypto.randomUUID(),
    childName: '',
    campName: '',
    fee: '',
});

const ensureFirstCampRow = (camps: Camp[]): Camp[] => (
    camps.length > 0 ? camps : [createEmptyCampRow()]
);

const createEmptyFinancialAidData = (): FinancialAidData => ({
    householdComposition: DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION,
    father: {
        title: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        cellPhone: '',
        homePhone: '',
        email: '',
        occupation: '',
        employer: '',
        businessName: '',
        shul: '',
        rabbi: '',
        isUnemployed: false,
        unemploymentBenefits: '',
        isDivorced: false,
        ssn: '',
        ssnLast4OnFile: '',
        ssnLast4ClearRequested: false,
    },
    mother: {
        title: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        cellPhone: '',
        homePhone: '',
        email: '',
        occupation: '',
        employer: '',
        businessName: '',
        shul: '',
        rabbi: '',
        isUnemployed: false,
        unemploymentBenefits: '',
        isDivorced: false,
        ssn: '',
        ssnLast4OnFile: '',
        ssnLast4ClearRequested: false,
    },
    incomeFather: { gross2024: '', parsonage: '', fedTax: '', stateTax: '', ficaTax: '', refunds: '', estimated2025: '' },
    incomeMother: { gross2024: '', parsonage: '', fedTax: '', stateTax: '', ficaTax: '', refunds: '', estimated2025: '' },
    benefits: { healthInsurance: '', tuition: '', auto: '', other: '', gifts: false, giftsAmount: '' },
    assets: {
        housingType: 'own',
        purchasePrice: '',
        purchaseDate: '',
        mortgageBalance: '',
        mortgagePayment: '',
        mortgageYears: '',
        propertyTaxes: '',
        interestRate: '',
        rentPayment: '',
        utilities: '',
        additionalProperties: ''
    },
    vehicles: [{ id: crypto.randomUUID(), year: '', make: '', model: '', value: '', debt: '', payment: '' }],
    investments: [{ id: crypto.randomUUID(), description: '', value: '' }],
    savings: { bank: '', accountNumber: '', balance: '' },
    checking: { bank: '', accountNumber: '', balance: '' },
    childrenAssets: [{ id: crypto.randomUUID(), description: '', value: '' }],
    expenses: {
        housing: '',
        taxesIncluded: false,
        utilities: '',
        studentLoans: '',
        food: '',
        tuition: '',
        bankLoans: '',
        lifeInsurance: '',
        shulDues: '',
        carInsurance: '',
        healthInsurance: '',
        cleaningHelp: '',
        other: '',
        medicalCoverage: '',
        medicalCoverageDetails: ''
    },
    otherChildren: [],
    majorExp: {
        homeImprovements: '',
        homeImprovementsDetails: '',
        largePurchases: '',
        largePurchasesDetails: '',
        extraordinaryObligations: '',
        simchaType: '',
        simchaCost: '',
        servicesToYST: false,
        servicesDescription: '',
        loanAmount: ''
    },
    camps2025: [createEmptyCampRow()],
    camps2024: [createEmptyCampRow()],
    vacation: { traveled: false, destinations: '', cost: '', parentsTraveled: false, childrenTraveled: false },
    contract: { childrenCount: 0, totalTuition: '', totalFees: '', preschoolTuition: '', eligibleTuition: '' },
    request: { requestedAmount: '', offeredTuition: '', offeredFees: '' },
    documents: [],
    signatures: { father: null, mother: null, date: new Date().toISOString(), attestations: {} }
});

// --- Utilities ---
const cleanNumber = (val: string): number => {
    if (!val) return 0;
    const num = parseFloat(val.replace(/,/g, ''));
    return isNaN(num) ? 0 : num;
};

const readText = (value: unknown): string => String(value ?? '').trim();

const toEpochMilliseconds = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value > 1_000_000_000_000 ? value : value * 1000;
    }
    const text = readText(value);
    if (!text) return 0;
    const numeric = Number(text);
    if (Number.isFinite(numeric)) {
        return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
    }
    const parsed = Date.parse(text);
    return Number.isFinite(parsed) ? parsed : 0;
};

const readFinancialAidStatusObservationTimestamp = (
    record: Record<string, unknown>,
): number => (
    toEpochMilliseconds(record.lastUpdatedAt)
    || toEpochMilliseconds(record.updatedAt)
    || toEpochMilliseconds(record.lastActivityAt)
);

const selectNewestFinancialAidStatusCandidate = (
    candidates: Array<{
        source: FinancialAidStatusCandidateSource;
        statusToken: unknown;
        observedAtMs: number;
        allowTimestamplessOverride?: boolean;
        preferOnEqualTimestamp?: boolean;
        hasMoreInfoRequestContext?: boolean;
    }>,
): FinancialAidStatusCandidate | null => {
    const normalizedCandidates = candidates
        .map((candidate) => ({
            source: candidate.source,
            statusToken: normalizeParentFinancialAidStatusToken(candidate.statusToken),
            observedAtMs: Number.isFinite(candidate.observedAtMs) ? candidate.observedAtMs : 0,
            allowTimestamplessOverride: candidate.allowTimestamplessOverride === true,
            preferOnEqualTimestamp: candidate.preferOnEqualTimestamp === true,
            hasMoreInfoRequestContext: candidate.hasMoreInfoRequestContext === true,
        }))
        .filter((candidate) => Boolean(candidate.statusToken));
    const selected = normalizedCandidates.reduce<FinancialAidStatusCandidate | null>((current, candidate) => {
        if (!current) return candidate;
        if (candidate.observedAtMs > current.observedAtMs) return candidate;
        if (candidate.observedAtMs < current.observedAtMs) return current;
        if (candidate.preferOnEqualTimestamp && !current.preferOnEqualTimestamp) return candidate;
        if (!candidate.preferOnEqualTimestamp && current.preferOnEqualTimestamp) return current;
        const candidateCanRepresentActiveMoreInfo =
            candidate.statusToken === 'more_info_needed'
            && candidate.allowTimestamplessOverride
            && candidate.hasMoreInfoRequestContext;
        const currentCanRepresentActiveMoreInfo =
            current.statusToken === 'more_info_needed'
            && current.allowTimestamplessOverride
            && current.hasMoreInfoRequestContext;
        if (
            candidate.statusToken === 'more_info_needed'
            && current.statusToken === 'under_review'
        ) {
            return candidateCanRepresentActiveMoreInfo ? candidate : current;
        }
        if (
            current.statusToken === 'more_info_needed'
            && candidate.statusToken === 'under_review'
        ) {
            return currentCanRepresentActiveMoreInfo ? current : candidate;
        }
        return FINANCIAL_AID_STATUS_CANDIDATE_SOURCE_RANK[candidate.source]
            > FINANCIAL_AID_STATUS_CANDIDATE_SOURCE_RANK[current.source]
            ? candidate
            : current;
    }, null);
    const timestamplessMoreInfoCandidate = normalizedCandidates.find((candidate) => (
        candidate.statusToken === 'more_info_needed'
        && candidate.observedAtMs === 0
        && candidate.allowTimestamplessOverride
        && candidate.hasMoreInfoRequestContext
    ));
    if (
        selected?.statusToken === 'under_review'
        && timestamplessMoreInfoCandidate
    ) {
        return timestamplessMoreInfoCandidate;
    }
    return selected;
};

const normalizeFinancialAidMoreInfoRequestKeyText = (value: unknown): string =>
    readText(value).replace(/\s+/g, ' ');

const normalizeFinancialAidMoreInfoRequestKeyDeadline = (value: unknown): string => {
    const epochMilliseconds = toEpochMilliseconds(value);
    return epochMilliseconds ? String(epochMilliseconds) : normalizeFinancialAidMoreInfoRequestKeyText(value);
};

const fingerprintFinancialAidMoreInfoRequestKey = (value: string): string => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return `key:${(hash >>> 0).toString(36)}`;
};

const buildFinancialAidMoreInfoRequestKeyToken = (
    type: 'id' | 'content' | 'event',
    rawValue: string,
): string[] => {
    const legacyToken = fingerprintFinancialAidMoreInfoRequestKey(rawValue);
    return [`${type}:${legacyToken}`, legacyToken];
};

const readFinancialAidMoreInfoRequestKeyTokens = (requestKey: string): string[] =>
    readText(requestKey).split('|').map((token) => token.trim()).filter(Boolean);

const hasFinancialAidMoreInfoRequestContextToken = (requestKey: string): boolean =>
    readFinancialAidMoreInfoRequestKeyTokens(requestKey)
        .some((token) => token.startsWith('id:') || token.startsWith('content:'));

const doFinancialAidMoreInfoRequestKeysMatch = (
    answeredRequestKey: string,
    requestKey: string,
): boolean => {
    const answeredTokens = readFinancialAidMoreInfoRequestKeyTokens(answeredRequestKey);
    const requestTokens = readFinancialAidMoreInfoRequestKeyTokens(requestKey);
    if (answeredTokens.length === 0 || requestTokens.length === 0) return false;

    const answeredIdTokens = answeredTokens.filter((token) => token.startsWith('id:'));
    const requestIdTokens = requestTokens.filter((token) => token.startsWith('id:'));
    if ((answeredIdTokens.length > 0) !== (requestIdTokens.length > 0)) {
        return false;
    }
    if (answeredIdTokens.length > 0 && requestIdTokens.length > 0) {
        return answeredIdTokens.some((token) => requestIdTokens.includes(token));
    }

    const answeredContentTokens = answeredTokens.filter((token) => token.startsWith('content:'));
    const requestContentTokens = requestTokens.filter((token) => token.startsWith('content:'));
    const answeredEventTokens = answeredTokens.filter((token) => token.startsWith('event:'));
    const requestEventTokens = requestTokens.filter((token) => token.startsWith('event:'));
    if (
        answeredContentTokens.length > 0
        && requestContentTokens.length > 0
        && answeredContentTokens.some((token) => requestContentTokens.includes(token))
    ) {
        if (answeredEventTokens.length > 0 || requestEventTokens.length > 0) {
            return answeredEventTokens.length > 0
                && requestEventTokens.length > 0
                && answeredEventTokens.some((token) => requestEventTokens.includes(token));
        }
        return true;
    }

    return answeredTokens.some((token) => requestTokens.includes(token));
};

const readFinancialAidMoreInfoRequestId = (record: Record<string, unknown>): string => {
    const decision = record.decision && typeof record.decision === 'object' && !Array.isArray(record.decision)
        ? record.decision as Record<string, unknown>
        : {};
    const isRequestMoreInfoDecision = readText(decision.decisionType) === 'request_more_info';
    return normalizeFinancialAidMoreInfoRequestKeyText(
        record.moreInfoRequestId
        ?? record.requestId
        ?? (isRequestMoreInfoDecision ? decision.moreInfoRequestId ?? decision.requestId : undefined),
    );
};

const normalizeCompletedMoreInfoResponseStatusToken = (value: unknown): string => {
    const statusToken = normalizeParentFinancialAidStatusToken(value);
    return COMPLETED_MORE_INFO_RESPONSE_STATUS_TOKENS.has(statusToken) ? statusToken : '';
};

const shouldClearCompletedMoreInfoResponseMarkerForStatus = (value: unknown): boolean =>
    COMPLETED_MORE_INFO_RESPONSE_CLEAR_STATUS_TOKENS.has(normalizeParentFinancialAidStatusToken(value));

const resolveCompletedMoreInfoResponseStatusToken = (...values: unknown[]): string =>
    values.map(normalizeCompletedMoreInfoResponseStatusToken).find(Boolean) || 'under_review';

const readFinancialAidMoreInfoRequestKey = (record: Record<string, unknown>): string => {
    const decision = record.decision && typeof record.decision === 'object' && !Array.isArray(record.decision)
        ? record.decision as Record<string, unknown>
        : {};
    const isRequestMoreInfoDecision = readText(decision.decisionType) === 'request_more_info';
    const requestId = readFinancialAidMoreInfoRequestId(record)
        || normalizeFinancialAidMoreInfoRequestKeyText(
            isRequestMoreInfoDecision ? decision.id ?? decision.decisionId : undefined,
        );
    const requestedInfo = normalizeFinancialAidMoreInfoRequestKeyText(
        record.requestedInfo
        || (isRequestMoreInfoDecision ? decision.requestedInfo : undefined),
    );
    const responseDeadline = normalizeFinancialAidMoreInfoRequestKeyDeadline(
        record.responseDeadline
        ?? record.deadline
        ?? (isRequestMoreInfoDecision ? decision.responseDeadline ?? decision.deadline : undefined),
    );
    const legacyRequestDiscriminator = requestId
        ? ''
        : normalizeFinancialAidMoreInfoRequestKeyDeadline(
            record.requestedAt
            ?? record.requestedAtMs
            ?? record.statusChangedAt
            ?? record.lastUpdatedAt
            ?? record.updatedAt
            ?? record.lastActivityAt
            ?? (isRequestMoreInfoDecision
                ? decision.decidedAt ?? decision.createdAt ?? decision.updatedAt
                : undefined),
        );
    const compositeKey = [
        requestedInfo ? `info:${requestedInfo}` : '',
        responseDeadline ? `deadline:${responseDeadline}` : '',
    ].filter(Boolean).join('|');
    const tokens = [
        ...(requestId ? buildFinancialAidMoreInfoRequestKeyToken('id', `id:${requestId}`) : []),
        ...(compositeKey ? buildFinancialAidMoreInfoRequestKeyToken('content', compositeKey) : []),
        ...(!requestId && legacyRequestDiscriminator
            ? buildFinancialAidMoreInfoRequestKeyToken('event', `event:${legacyRequestDiscriminator}`)
            : []),
    ];
    return tokens.join('|');
};

const toDocumentUploadFailureMessage = (status: number): string => (
    `Document upload failed before it could be saved (S3 status ${status}). Please try again or contact support if it keeps happening.`
);

const getBrowserStorage = (storageName: 'localStorage' | 'sessionStorage'): Storage | null => {
    if (typeof window === 'undefined') return null;
    try {
        return window[storageName];
    } catch {
        return null;
    }
};

const getFinancialAidPointerStoragePair = (): {
    localStorage: Storage | null;
    sessionStorage: Storage | null;
} => ({
    localStorage: getBrowserStorage('localStorage'),
    sessionStorage: getBrowserStorage('sessionStorage'),
});

const getFinancialAidPointerStorage = (): Storage | null => {
    return selectParentFinancialAidPointerStorage(getFinancialAidPointerStoragePair());
};

const getFinancialAidSessionPointerStorage = (): Storage | null => {
    return getBrowserStorage('sessionStorage');
};

const resolveCompletedMoreInfoResponseStorageKey = (applicationId: string): string =>
    `${COMPLETED_MORE_INFO_RESPONSE_STORAGE_PREFIX}:${readText(applicationId) || 'unknown-application'}`;

const isCompletedMoreInfoResponsePhase = (value: unknown): value is CompletedMoreInfoResponseMarker['phase'] =>
    value === 'pending_return' || value === 'returned';

const readStoredCompletedMoreInfoResponseMarker = (
    applicationId: string | null | undefined,
    nowMs: number = Date.now(),
): CompletedMoreInfoResponseMarker | null => {
    const normalizedApplicationId = readText(applicationId);
    if (!normalizedApplicationId) return null;
    const storage = getFinancialAidSessionPointerStorage();
    if (!storage) return null;
    const storageKey = resolveCompletedMoreInfoResponseStorageKey(normalizedApplicationId);
    try {
        const raw = storage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredCompletedMoreInfoResponseMarker>;
        const completedAtMs = Number(parsed.completedAtMs);
        const returnedAtMs = Number(parsed.returnedAtMs);
        if (
            parsed.version !== 1
            || !isCompletedMoreInfoResponsePhase(parsed.phase)
            || !Number.isFinite(completedAtMs)
            || completedAtMs <= 0
            || nowMs - completedAtMs > COMPLETED_MORE_INFO_RESPONSE_STORAGE_MAX_AGE_MS
        ) {
            storage.removeItem(storageKey);
            return null;
        }
        return {
            phase: parsed.phase,
            completedAtMs,
            returnedAtMs: Number.isFinite(returnedAtMs) && returnedAtMs > 0 ? returnedAtMs : undefined,
            answeredRequestKey: readText(parsed.answeredRequestKey) || undefined,
        };
    } catch {
        storage.removeItem(storageKey);
        return null;
    }
};

const writeStoredCompletedMoreInfoResponseMarker = (
    applicationId: string | null | undefined,
    marker: CompletedMoreInfoResponseMarker,
): void => {
    const normalizedApplicationId = readText(applicationId);
    const storage = getFinancialAidSessionPointerStorage();
    if (!normalizedApplicationId || !storage) return;
    try {
        const record: StoredCompletedMoreInfoResponseMarker = {
            version: 1,
            ...marker,
        };
        storage.setItem(resolveCompletedMoreInfoResponseStorageKey(normalizedApplicationId), JSON.stringify(record));
    } catch {
        // Best-effort reload continuity only.
    }
};

const clearStoredCompletedMoreInfoResponseMarker = (applicationId: string | null | undefined): void => {
    const normalizedApplicationId = readText(applicationId);
    const storage = getFinancialAidSessionPointerStorage();
    if (!normalizedApplicationId || !storage) return;
    try {
        storage.removeItem(resolveCompletedMoreInfoResponseStorageKey(normalizedApplicationId));
    } catch {
        // Best-effort cleanup only.
    }
};

const resolveFinancialAidDraftActorId = (profile: { userSub?: string; email?: string } | null | undefined): string => {
    return readText(profile?.userSub) || readText(profile?.email).toLowerCase() || 'anonymous-parent';
};

const fallbackText = (value: unknown, fallback: string): string => readText(value) || fallback;
const toCurrencyNumber = (value: string | number | undefined | null): number => {
    if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : 0;
    return cleanNumber(String(value ?? ''));
};
const toPositiveCurrencyNumber = (value: string | number | undefined | null): number =>
    Math.max(0, toCurrencyNumber(value));
const toPositiveInteger = (value: string | number | undefined | null): number => {
    const numeric = typeof value === 'number' ? value : parseInt(String(value ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};
const toNonNegativeInteger = (value: string | number | undefined | null): number => {
    const numeric = typeof value === 'number' ? value : parseInt(String(value ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};
const normalizePhone = (value: unknown): string => {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.length >= 10 ? digits.slice(-10) : '';
};
const isInvitePlaceholderText = (value: unknown): boolean => {
    const normalized = readText(value).toLowerCase().replace(/\s+/g, ' ');
    return normalized === 'invited' || normalized === 'invited family';
};
const isInvitePlaceholderPhone = (value: unknown): boolean => {
    const digits = readText(value).replace(/\D/g, '');
    return digits.length >= 10 && /^0+$/.test(digits);
};
const readNonInvitePlaceholderText = (value: unknown): string =>
    isInvitePlaceholderText(value) ? '' : readText(value);
const readNonInvitePlaceholderFamilyText = (value: unknown): string =>
    isInvitePlaceholderText(value) ? '' : readText(value);
const readNonInvitePlaceholderPhone = (value: unknown): string =>
    isInvitePlaceholderPhone(value) ? '' : readText(value);
const normalizeNonInvitePlaceholderPhone = (value: unknown): string =>
    isInvitePlaceholderPhone(value) ? '' : normalizePhone(value);
const normalizeEmail = (value: unknown, fallback = ''): string => {
    const candidate = readText(value);
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(candidate)) return candidate;
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fallback) ? fallback : '';
};
const normalizeZip = (value: unknown): string => readText(value) || '00000';
const buildParentGuardianAddress = (
    parent: ParentInfo,
    existingParent?: Record<string, unknown>,
): Record<string, string> | null => {
    const existingAddress = asRecord(existingParent?.address);
    const street = readText(parent.address) || readText(existingAddress.street);
    const city = readText(parent.city) || readText(existingAddress.city);
    const state = readText(parent.state) || readText(existingAddress.state);
    const zipSource = readText(parent.zip) || readText(existingAddress.zipCode ?? existingAddress.zip);
    if (!street || !city || !state || !zipSource) {
        return null;
    }
    return {
        street,
        city,
        state,
        zipCode: normalizeZip(zipSource),
    };
};
type ParentSectionKey = 'father' | 'mother';
const PARENT_GUARDIAN_RELATIONSHIPS = new Set(['father', 'mother', 'stepfather', 'stepmother', 'guardian', 'guardian1', 'guardian2']);
const PARENT_GUARDIAN_DISPLAY_ROLES = new Set<ParentGuardianDisplayRole>(
    PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS.map((option) => option.value),
);
const readParentGuardianRelationship = (value: unknown): string | null => {
    const relationship = readText(value);
    return PARENT_GUARDIAN_RELATIONSHIPS.has(relationship) ? relationship : null;
};
const readParentGuardianDisplayRole = (value: unknown): ParentGuardianDisplayRole | null => {
    const role = readText(value) as ParentGuardianDisplayRole;
    return PARENT_GUARDIAN_DISPLAY_ROLES.has(role) ? role : null;
};
const readParentSectionRelationshipRole = (value: unknown): ParentSectionKey | null => {
    const relationship = readText(value);
    if (relationship === 'father' || relationship === 'stepfather' || relationship === 'guardian1') return 'father';
    if (relationship === 'mother' || relationship === 'stepmother' || relationship === 'guardian2') return 'mother';
    return null;
};
const resolveParentGuardianSectionDisplayRole = (
    householdComposition: ParentGuardianHouseholdComposition,
    parentKey: ParentSectionKey,
): ParentGuardianDisplayRole =>
    householdComposition.mode === 'single_parent_guardian' && householdComposition.primaryKey === parentKey
        ? householdComposition.primaryRole
        : parentKey;
const resolveParentSocialSecurityLast4 = (
    editableValue: unknown,
    existingLast4: unknown,
    clearExisting = false,
): string | null => {
    if (clearExisting && !isValidFinancialAidSocialSecurityNumber(editableValue)) return null;
    return isValidFinancialAidSocialSecurityNumber(editableValue)
        ? readFinancialAidSocialSecurityLast4(editableValue)
        : readFinancialAidSocialSecurityLast4(existingLast4);
};
const buildParentGuardianSection = (
    parent: ParentInfo,
    relationship: ParentGuardianPersistenceRelationship,
    displayRole: ParentGuardianDisplayRole,
    otherRoleLabel: string | null,
    fallbackLastName: string,
    fallbackEmail: string,
    existingParent?: Record<string, unknown>,
    options: { allowSyntheticIdentityFallbacks?: boolean } = {},
): Record<string, unknown> => ({
    title: readText(parent.title) || null,
    firstName: options.allowSyntheticIdentityFallbacks === false
        ? readText(parent.firstName)
        : fallbackText(parent.firstName, 'Unknown'),
    lastName: options.allowSyntheticIdentityFallbacks === false
        ? readText(parent.lastName)
        : fallbackText(parent.lastName, fallbackLastName),
    relationship,
    displayRole,
    otherRoleLabel,
    email: options.allowSyntheticIdentityFallbacks === false
        ? normalizeEmail(parent.email)
        : normalizeEmail(parent.email, fallbackEmail),
    address: buildParentGuardianAddress(parent, existingParent),
    cellPhone: normalizeNonInvitePlaceholderPhone(parent.cellPhone) || normalizeNonInvitePlaceholderPhone(parent.homePhone),
    homePhone: normalizeNonInvitePlaceholderPhone(parent.homePhone),
    workPhone: normalizeNonInvitePlaceholderPhone(readText(existingParent?.workPhone)) || '',
    socialSecurityLast4: resolveParentSocialSecurityLast4(
        parent.ssn,
        existingParent?.socialSecurityLast4 ?? parent.ssnLast4OnFile,
        parent.ssnLast4ClearRequested,
    ),
    employer: readText(parent.employer) || null,
    occupation: readText(parent.occupation) || null,
    isUnemployed: Boolean(parent.isUnemployed),
    unemploymentDuration: parent.isUnemployed ? readText(existingParent?.unemploymentDuration) || null : null,
    unemploymentWeeklyAmount: parent.isUnemployed ? readText(parent.unemploymentBenefits) || null : null,
});
const hasPersistableParentGuardianDraftInput = (parent: ParentInfo): boolean =>
    [
        parent.title,
        parent.firstName,
        parent.lastName,
        parent.address,
        parent.city,
        parent.state,
        parent.zip,
        parent.cellPhone,
        parent.homePhone,
        parent.email,
        parent.occupation,
        parent.employer,
        parent.businessName,
        parent.shul,
        parent.rabbi,
        parent.ssn,
        parent.ssnLast4OnFile,
        parent.unemploymentBenefits,
    ].some((value) => readText(value))
    || Boolean(parent.isUnemployed)
    || Boolean(parent.isDivorced)
    || Boolean(parent.ssnLast4ClearRequested);
const readExistingParentGuardianSection = (
    existingSections: Record<string, unknown>,
    relationship: ParentSectionKey,
): Record<string, unknown> => {
    const parentSections = [asRecord(existingSections.parent1), asRecord(existingSections.parent2)];
    const matchedSection = parentSections.find((section) => readParentSectionRelationshipRole(section.relationship) === relationship);
    if (matchedSection) return matchedSection;

    const occupiedRoles = new Set(parentSections
        .map((section) => readParentSectionRelationshipRole(section.relationship))
        .filter((role): role is ParentSectionKey => Boolean(role)));
    const guardianSection = parentSections.find((section) => readParentGuardianRelationship(section.relationship) === 'guardian');
    if (guardianSection && occupiedRoles.size > 0 && !occupiedRoles.has(relationship)) {
        return guardianSection;
    }

    const hasRelationshipMarkers = parentSections.some((section) => readParentSectionRelationshipRole(section.relationship));
    if (hasRelationshipMarkers) return {};

    return relationship === 'father' ? parentSections[0] : parentSections[1];
};

const isLegacyParentFinancialAidStudentPlaceholder = (student: Record<string, unknown>): boolean =>
    !readText(student.childApplicationId)
    && readText(student.firstName).toLowerCase() === 'student'
    && readText(student.grade).toLowerCase() === 'grade_1';

const readExistingParentFinancialAidYstStudents = (
    existingSections: Record<string, unknown>,
): Record<string, unknown>[] =>
    asArray(asRecord(existingSections).ystStudents)
        .map(asRecord)
        .filter((student) => (
            Boolean(readText(student.firstName) || readText(student.lastName))
            && !isLegacyParentFinancialAidStudentPlaceholder(student)
        ));

const formatFinancialAidCopyAmount = (value: string | number | undefined | null): string =>
    `$${toPositiveCurrencyNumber(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const invalidateFinancialAidSignatures = (data: FinancialAidData): FinancialAidData => {
    if (!data.signatures.father && !data.signatures.mother && !data.signatures.fatherCaptured && !data.signatures.motherCaptured) return data;
    return {
        ...data,
        signatures: {
            ...data.signatures,
            father: null,
            mother: null,
            fatherCaptured: false,
            motherCaptured: false,
        },
    };
};

const readParentCopyName = (parent: ParentInfo, fallback: string): string => {
    const name = `${readText(parent.firstName)} ${readText(parent.lastName)}`.trim();
    return name || fallback;
};

const maskFinancialAidCopySsn = (value: unknown): string => {
    const last4 = readFinancialAidSocialSecurityLast4(value);
    return last4 ? `xxx-xx-${last4}` : 'Not provided';
};

const joinFinancialAidCopyParts = (...parts: Array<string | null | undefined>): string =>
    parts.map((part) => readText(part)).filter(Boolean).join(', ');

const financialAidCopyRow = (label: string, value: string | number | boolean | null | undefined) => ({
    label,
    value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : readText(value),
});

const financialAidYearLabel = (base: string, year: number | null): string =>
    year ? `${base} ${year}` : base;

const financialAidEstimateLabel = (parentLabel: string, year: number | null): string =>
    year ? `${parentLabel} ${year} estimate` : `${parentLabel} income estimate`;

const financialAidIncomeSubtitle = (actualYear: number | null, estimateYear: number | null): string =>
    actualYear && estimateYear
        ? `Enter ${actualYear} actuals and ${estimateYear} estimates.`
        : 'Enter actual income and estimates for the selected financial aid cycle.';

const financialAidLargePurchasesLabel = (actualYear: number | null, estimateYear: number | null): string =>
    actualYear && estimateYear
        ? `Please list any purchases or expenditures of $5000 or more that you or your immediate family have made in ${actualYear} or plan to make in ${estimateYear}.`
        : 'Please list any purchases or expenditures of $5000 or more that you or your immediate family have made or plan to make for this financial aid cycle.';

const financialAidSummerCampLabel = (year: number | null, fallback: string): string =>
    year ? `Summer Camps (${year})` : fallback;

const financialAidAcademicYearCopyLabel = (academicYear: unknown): string =>
    resolveFinancialAidDisplayYears(academicYear).academicYear || 'Selected financial aid cycle';

const financialAidAcademicYearInlineLabel = (academicYear: unknown): string =>
    resolveFinancialAidDisplayYears(academicYear).academicYear || 'the selected financial aid cycle';

const buildFinancialAidCopyPdfSections = (
    data: FinancialAidData,
    reference: string,
    academicYear: string,
    certification?: ParentFinancialAidCopyCertificationContext | null,
): SectionedPdfSection[] => {
    const displayYears = resolveFinancialAidDisplayYears(academicYear);
    const householdComposition = resolveParentGuardianHouseholdComposition(data);
    const isFatherActive = isParentGuardianFormKeyActive(householdComposition, 'father');
    const isMotherActive = isParentGuardianFormKeyActive(householdComposition, 'mother');
    const fatherComplete = isFatherActive && isParentFinancialAidParentInfoComplete(data, 'father');
    const motherComplete = isMotherActive && isParentFinancialAidParentInfoComplete(data, 'mother');
    const fatherLabel = resolveParentGuardianDisplayLabel(householdComposition, 'father');
    const motherLabel = resolveParentGuardianDisplayLabel(householdComposition, 'mother');
    const fatherName = readParentCopyName(data.father, 'Father');
    const motherName = readParentCopyName(data.mother, 'Mother');
    const activeParents = resolveParentGuardianActiveKeys(householdComposition).map((parentKey) => data[parentKey]);
    const activeShul = activeParents.map((parent) => readText(parent.shul)).find(Boolean) ?? '';
    const activeRabbi = activeParents.map((parent) => readText(parent.rabbi)).find(Boolean) ?? '';
    const totalIncome =
        (isFatherActive ? toPositiveCurrencyNumber(data.incomeFather.gross2024) : 0)
        + (isMotherActive ? toPositiveCurrencyNumber(data.incomeMother.gross2024) : 0);
    const totalAssets =
        toPositiveCurrencyNumber(data.assets.purchasePrice)
        + toPositiveCurrencyNumber(data.savings.balance)
        + toPositiveCurrencyNumber(data.checking.balance)
        + data.vehicles.reduce((sum, vehicle) => sum + toPositiveCurrencyNumber(vehicle.value), 0)
        + data.investments.reduce((sum, investment) => sum + toPositiveCurrencyNumber(investment.value), 0)
        + data.childrenAssets.reduce((sum, investment) => sum + toPositiveCurrencyNumber(investment.value), 0);
    const totalExpenses =
        toPositiveCurrencyNumber(data.expenses.housing)
        + toPositiveCurrencyNumber(data.expenses.utilities)
        + toPositiveCurrencyNumber(data.expenses.food)
        + toPositiveCurrencyNumber(data.expenses.tuition)
        + toPositiveCurrencyNumber(data.expenses.bankLoans)
        + toPositiveCurrencyNumber(data.expenses.studentLoans);
    const uploadedDocuments = data.documents
        .map((document) => `${document.name}: ${document.uploaded ? 'Uploaded' : 'Missing'}`);

    return [
        {
            title: 'Submission',
            rows: [
                financialAidCopyRow('Reference', reference),
                financialAidCopyRow('Academic year', financialAidAcademicYearCopyLabel(academicYear)),
                financialAidCopyRow('Submitted date', formatParentFinancialAidCopyCertificationDate(certification?.certificationDate || data.signatures.date)),
            ],
        },
        {
            title: 'Parents / Guardians',
            rows: [
                ...(isFatherActive ? [
                    financialAidCopyRow(fatherLabel, fatherComplete ? fatherName : 'Not provided'),
                    financialAidCopyRow(`${fatherLabel} contact`, joinFinancialAidCopyParts(data.father.email, data.father.cellPhone, data.father.homePhone)),
                    financialAidCopyRow(`${fatherLabel} address`, joinFinancialAidCopyParts(data.father.address, data.father.city, data.father.state, data.father.zip)),
                    financialAidCopyRow(`${fatherLabel} employment`, joinFinancialAidCopyParts(data.father.occupation, data.father.employer, data.father.businessName)),
                    financialAidCopyRow(`${fatherLabel} SSN`, maskFinancialAidCopySsn(data.father.ssn)),
                ] : []),
                ...(isMotherActive ? [
                    financialAidCopyRow(motherLabel, motherComplete ? motherName : 'Not provided'),
                    financialAidCopyRow(`${motherLabel} contact`, joinFinancialAidCopyParts(data.mother.email, data.mother.cellPhone, data.mother.homePhone)),
                    financialAidCopyRow(`${motherLabel} address`, joinFinancialAidCopyParts(data.mother.address, data.mother.city, data.mother.state, data.mother.zip)),
                    financialAidCopyRow(`${motherLabel} employment`, joinFinancialAidCopyParts(data.mother.occupation, data.mother.employer, data.mother.businessName)),
                    financialAidCopyRow(`${motherLabel} SSN`, maskFinancialAidCopySsn(data.mother.ssn)),
                ] : []),
                financialAidCopyRow('Synagogue / Rabbi', joinFinancialAidCopyParts(activeShul, activeRabbi)),
            ],
        },
        {
            title: 'Income',
            rows: [
                ...(isFatherActive ? [
                    financialAidCopyRow(financialAidYearLabel(`${fatherLabel} gross income`, displayYears.actualYear), formatFinancialAidCopyAmount(data.incomeFather.gross2024)),
                ] : []),
                ...(isMotherActive ? [
                    financialAidCopyRow(financialAidYearLabel(`${motherLabel} gross income`, displayYears.actualYear), formatFinancialAidCopyAmount(data.incomeMother.gross2024)),
                ] : []),
                financialAidCopyRow('Household income', formatFinancialAidCopyAmount(totalIncome)),
                ...(isFatherActive ? [
                    financialAidCopyRow(financialAidEstimateLabel(fatherLabel, displayYears.estimateYear), formatFinancialAidCopyAmount(data.incomeFather.estimated2025)),
                ] : []),
                ...(isMotherActive ? [
                    financialAidCopyRow(financialAidEstimateLabel(motherLabel, displayYears.estimateYear), formatFinancialAidCopyAmount(data.incomeMother.estimated2025)),
                ] : []),
                financialAidCopyRow('Benefits', joinFinancialAidCopyParts(
                    `Health insurance ${formatFinancialAidCopyAmount(data.benefits.healthInsurance)}`,
                    `Tuition ${formatFinancialAidCopyAmount(data.benefits.tuition)}`,
                    `Auto ${formatFinancialAidCopyAmount(data.benefits.auto)}`,
                    `Other ${formatFinancialAidCopyAmount(data.benefits.other)}`,
                )),
                financialAidCopyRow('Gifts received', data.benefits.gifts ? formatFinancialAidCopyAmount(data.benefits.giftsAmount) : 'No'),
            ],
        },
        {
            title: 'Assets',
            rows: [
                financialAidCopyRow('Housing', data.assets.housingType === 'own' ? 'Own' : 'Rent'),
                financialAidCopyRow('Home purchase price', formatFinancialAidCopyAmount(data.assets.purchasePrice)),
                financialAidCopyRow('Mortgage balance', formatFinancialAidCopyAmount(data.assets.mortgageBalance)),
                financialAidCopyRow('Mortgage years', data.assets.mortgageYears || 'Not provided'),
                financialAidCopyRow('Monthly mortgage / rent', data.assets.housingType === 'own'
                    ? formatFinancialAidCopyAmount(data.assets.mortgagePayment)
                    : formatFinancialAidCopyAmount(data.assets.rentPayment)),
                financialAidCopyRow('Savings balance', formatFinancialAidCopyAmount(data.savings.balance)),
                financialAidCopyRow('Checking balance', formatFinancialAidCopyAmount(data.checking.balance)),
                financialAidCopyRow('Investments', data.investments.length
                    ? data.investments.map((investment) => `${investment.description || 'Investment'} ${formatFinancialAidCopyAmount(investment.value)}`).join('; ')
                    : 'None reported'),
                financialAidCopyRow('Children assets', data.childrenAssets.length
                    ? data.childrenAssets.map((asset) => `${asset.description || 'Asset'} ${formatFinancialAidCopyAmount(asset.value)}`).join('; ')
                    : 'None reported'),
                financialAidCopyRow('Vehicles', data.vehicles.length
                    ? data.vehicles.map((vehicle) => joinFinancialAidCopyParts(
                        `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim(),
                        `value ${formatFinancialAidCopyAmount(vehicle.value)}`,
                        `debt ${formatFinancialAidCopyAmount(vehicle.debt)}`,
                    )).join('; ')
                    : 'None reported'),
                financialAidCopyRow('Assets reported', formatFinancialAidCopyAmount(totalAssets)),
            ],
        },
        {
            title: 'Expenses',
            rows: [
                financialAidCopyRow('Monthly expenses reported', formatFinancialAidCopyAmount(totalExpenses)),
                financialAidCopyRow('Housing', formatFinancialAidCopyAmount(data.expenses.housing)),
                financialAidCopyRow('Utilities', formatFinancialAidCopyAmount(data.expenses.utilities)),
                financialAidCopyRow('Food', formatFinancialAidCopyAmount(data.expenses.food)),
                financialAidCopyRow('Student loans', formatFinancialAidCopyAmount(data.expenses.studentLoans)),
                financialAidCopyRow('Bank loans', formatFinancialAidCopyAmount(data.expenses.bankLoans)),
                financialAidCopyRow('Insurance', joinFinancialAidCopyParts(
                    `Life ${formatFinancialAidCopyAmount(data.expenses.lifeInsurance)}`,
                    `Car ${formatFinancialAidCopyAmount(data.expenses.carInsurance)}`,
                    `Health ${formatFinancialAidCopyAmount(data.expenses.healthInsurance)}`,
                )),
                financialAidCopyRow('Other expenses', formatFinancialAidCopyAmount(data.expenses.other)),
            ],
        },
        {
            title: 'Tuition Request',
            rows: [
                financialAidCopyRow('Children count', String(data.contract.childrenCount)),
                financialAidCopyRow('Total tuition', formatFinancialAidCopyAmount(data.contract.totalTuition)),
                financialAidCopyRow('Total fees', formatFinancialAidCopyAmount(data.contract.totalFees)),
                financialAidCopyRow('Less Preschool tuition', formatFinancialAidCopyAmount(data.contract.eligibleTuition)),
                financialAidCopyRow('Scholarship requested', formatFinancialAidCopyAmount(data.request.requestedAmount)),
                financialAidCopyRow('Family tuition offer', formatFinancialAidCopyAmount(data.request.offeredTuition)),
                financialAidCopyRow('Family fees offer', formatFinancialAidCopyAmount(data.request.offeredFees)),
                financialAidCopyRow('Family total tuition and fees offer', formatFinancialAidCopyAmount(
                    String(cleanNumber(data.request.offeredTuition) + cleanNumber(data.request.offeredFees)),
                )),
                financialAidCopyRow('Other children tuition', data.otherChildren.length
                    ? data.otherChildren.map((child) => `${child.name || 'Child'} (${child.school || 'school not provided'}): tuition ${formatFinancialAidCopyAmount(child.fullTuition)}, scholarship ${formatFinancialAidCopyAmount(child.scholarship)}`).join('; ')
                    : 'None reported'),
            ],
        },
        {
            title: 'Lifestyle / Other Context',
            rows: [
                financialAidCopyRow('Major expenditures', joinFinancialAidCopyParts(
                    data.majorExp.homeImprovementsDetails,
                    data.majorExp.largePurchasesDetails,
                    data.majorExp.extraordinaryObligations,
                ) || 'None reported'),
                financialAidCopyRow('Simcha costs', joinFinancialAidCopyParts(data.majorExp.simchaType, formatFinancialAidCopyAmount(data.majorExp.simchaCost))),
                financialAidCopyRow('Services to YST', data.majorExp.servicesToYST ? data.majorExp.servicesDescription || 'Yes' : 'No'),
                financialAidCopyRow(displayYears.currentCampYear ? `Camp ${displayYears.currentCampYear}` : 'Current-year camp', data.camps2025.length
                    ? data.camps2025.map((camp) => `${camp.childName || 'Child'} - ${camp.campName || 'Camp'} ${formatFinancialAidCopyAmount(camp.fee)}`).join('; ')
                    : 'None reported'),
                financialAidCopyRow(displayYears.priorCampYear ? `Camp ${displayYears.priorCampYear}` : 'Prior-year camp', data.camps2024.length
                    ? data.camps2024.map((camp) => `${camp.childName || 'Child'} - ${camp.campName || 'Camp'} ${formatFinancialAidCopyAmount(camp.fee)}`).join('; ')
                    : 'None reported'),
                financialAidCopyRow('Vacation', data.vacation.traveled
                    ? joinFinancialAidCopyParts(data.vacation.destinations, formatFinancialAidCopyAmount(data.vacation.cost))
                    : 'No travel reported'),
            ],
        },
        {
            title: 'Documents',
            rows: uploadedDocuments.map((document) => financialAidCopyRow('Document', document)),
        },
        {
            title: 'Certification',
            rows: [
                ...(isFatherActive ? [financialAidCopyRow(`${fatherLabel} signature`, formatParentFinancialAidCopySignatureStatus(
                    resolveParentFinancialAidCopySignatureStatus({
                        parentComplete: fatherComplete,
                        localSignature: data.signatures.father,
                        captured: Boolean(data.signatures.fatherCaptured && certification?.certificationDate),
                    }),
                ))] : []),
                ...(isMotherActive ? [financialAidCopyRow(`${motherLabel} signature`, formatParentFinancialAidCopySignatureStatus(
                    resolveParentFinancialAidCopySignatureStatus({
                        parentComplete: motherComplete,
                        localSignature: data.signatures.mother,
                        captured: Boolean(data.signatures.motherCaptured && certification?.certificationDate),
                    }),
                ))] : []),
                financialAidCopyRow('Attestations', didParentFinancialAidCopyCompleteAttestations(data.signatures.attestations, certification) ? 'Completed' : 'Incomplete'),
                financialAidCopyRow('Electronic submission certification', formatParentFinancialAidCopyCertificationStatus(certification)),
                ...(
                    certification?.electronicSignature
                        ? [financialAidCopyRow('Certified by', certification.electronicSignature)]
                        : [] satisfies SectionedPdfRow[]
                ),
            ],
        },
    ];
};
const buildParentFinancialAidSections = (
    data: FinancialAidData,
    profile: { email?: string } | null | undefined,
    existingSections: Record<string, unknown> = {},
    certification?: ParentFinancialAidCopyCertificationContext | null,
    rawSnapshotData: FinancialAidData = data,
): Record<string, unknown> => {
    const fatherIncome = toPositiveCurrencyNumber(data.incomeFather.gross2024);
    const motherIncome = toPositiveCurrencyNumber(data.incomeMother.gross2024);
    const householdComposition = resolveParentGuardianHouseholdComposition(data);
    const activeParentKeys = resolveParentGuardianActiveKeys(householdComposition);
    const activeParentCount = activeParentKeys.length;
    const isFatherActive = isParentGuardianFormKeyActive(householdComposition, 'father');
    const isMotherActive = isParentGuardianFormKeyActive(householdComposition, 'mother');
    const activeFatherIncome = isFatherActive ? fatherIncome : 0;
    const activeMotherIncome = isMotherActive ? motherIncome : 0;
    const totalAnnualIncome = activeFatherIncome + activeMotherIncome;
    const savingsBalance = toPositiveCurrencyNumber(data.savings.balance);
    const checkingBalance = toPositiveCurrencyNumber(data.checking.balance);
    const investmentTotal =
        data.investments.reduce((sum, investment) => sum + toPositiveCurrencyNumber(investment.value), 0)
        + data.childrenAssets.reduce((sum, investment) => sum + toPositiveCurrencyNumber(investment.value), 0);
    const vehicleValue = data.vehicles.reduce((sum, vehicle) => sum + toPositiveCurrencyNumber(vehicle.value), 0);
    const vehicleDebt = data.vehicles.reduce((sum, vehicle) => sum + toPositiveCurrencyNumber(vehicle.debt), 0);
    const mortgageBalance = toPositiveCurrencyNumber(data.assets.mortgageBalance);
    const homeValue = data.assets.housingType === 'own' ? toPositiveCurrencyNumber(data.assets.purchasePrice) : 0;
    const liquidAssets = savingsBalance + checkingBalance;
    const totalAssets = liquidAssets + investmentTotal + vehicleValue + homeValue;
    const studentLoans = toPositiveCurrencyNumber(data.expenses.studentLoans);
    const bankLoans = toPositiveCurrencyNumber(data.expenses.bankLoans);
    const loanAmount = toPositiveCurrencyNumber(data.majorExp.loanAmount);
    const totalLiabilities = mortgageBalance + vehicleDebt + studentLoans + bankLoans + loanAmount;
    const housingCost = data.assets.housingType === 'own'
        ? toPositiveCurrencyNumber(data.assets.mortgagePayment)
        : toPositiveCurrencyNumber(data.assets.rentPayment);
    const utilitiesTotal =
        toPositiveCurrencyNumber(data.assets.utilities)
        + toPositiveCurrencyNumber(data.expenses.utilities);
    const foodTotal = toPositiveCurrencyNumber(data.expenses.food);
    const medicalCoverageType = readText(data.expenses.medicalCoverage);
    const medicalCoverageDetails = readText(data.expenses.medicalCoverageDetails);
    const healthcareTotal =
        toPositiveCurrencyNumber(data.expenses.healthInsurance);
    const insuranceTotal =
        toPositiveCurrencyNumber(data.expenses.carInsurance)
        + toPositiveCurrencyNumber(data.expenses.lifeInsurance);
    const otherTotal =
        toPositiveCurrencyNumber(data.expenses.other)
        + toPositiveCurrencyNumber(data.expenses.shulDues)
        + toPositiveCurrencyNumber(data.expenses.cleaningHelp);
    const totalMonthlyExpenses =
        housingCost
        + utilitiesTotal
        + foodTotal
        + healthcareTotal
        + insuranceTotal
        + studentLoans
        + bankLoans
        + otherTotal;
    const eligibleTuition = toPositiveCurrencyNumber(data.contract.eligibleTuition);
    const ystStudentCount = Math.max(0, toPositiveInteger(data.contract.childrenCount));
    const totalChildCount = Math.max(ystStudentCount, data.otherChildren.length + ystStudentCount);
    const emailFallback = normalizeEmail(profile?.email);
    const fatherEmail = normalizeEmail(data.father.email, emailFallback);
    const motherEmail = normalizeEmail(data.mother.email, emailFallback);
    const fatherComplete = isParentFinancialAidParentInfoComplete(data, 'father');
    const motherComplete = isParentFinancialAidParentInfoComplete(data, 'mother');
    const fatherHasPersistableDraftInput = fatherComplete || hasPersistableParentGuardianDraftInput(data.father);
    const motherHasPersistableDraftInput = motherComplete || hasPersistableParentGuardianDraftInput(data.mother);
    const primaryParentKey: ParentSectionKey = householdComposition.mode === 'single_parent_guardian'
        ? householdComposition.primaryKey
        : fatherHasPersistableDraftInput || !motherHasPersistableDraftInput ? 'father' : 'mother';
    const secondaryParentKey: ParentSectionKey | null = householdComposition.mode === 'single_parent_guardian'
        ? null
        : fatherHasPersistableDraftInput && motherHasPersistableDraftInput
            ? (primaryParentKey === 'father' ? 'mother' : 'father')
            : null;
    const primaryParent = data[primaryParentKey];
    const secondaryParent = secondaryParentKey ? data[secondaryParentKey] : null;
    const activeParentLookup = {
        father: {
            parent: data.father,
            income: activeFatherIncome,
            email: fatherEmail,
            label: resolveParentGuardianDisplayLabel(householdComposition, 'father'),
        },
        mother: {
            parent: data.mother,
            income: activeMotherIncome,
            email: motherEmail,
            label: resolveParentGuardianDisplayLabel(householdComposition, 'mother'),
        },
    } satisfies Record<ParentSectionKey, { parent: ParentInfo; income: number; email: string; label: string }>;
    const familyName = fallbackText(primaryParent.lastName || secondaryParent?.lastName, 'Unknown');
    const existingPrimaryParent = readExistingParentGuardianSection(existingSections, primaryParentKey);
    const existingSecondaryParent = secondaryParentKey
        ? readExistingParentGuardianSection(existingSections, secondaryParentKey)
        : {};
    const existingYstStudents = readExistingParentFinancialAidYstStudents(existingSections);
    const state = fallbackText(primaryParent.state || secondaryParent?.state, 'NY');
    const homeAddress = {
        street: fallbackText(primaryParent.address || secondaryParent?.address, 'Not provided'),
        city: fallbackText(primaryParent.city || secondaryParent?.city, 'Not provided'),
        state,
        zipCode: normalizeZip(primaryParent.zip || secondaryParent?.zip),
    };
    const activeParentIncomeEntries = activeParentKeys
        .map((parentKey) => {
            const activeParent = activeParentLookup[parentKey];
            return activeParent.income > 0 ? {
            type: 'salary_wages',
                description: `${activeParent.label} income`,
                annualAmount: activeParent.income,
            isRecurring: true,
            frequency: 'annually',
            } : null;
        })
        .filter(Boolean);
    const incomeEntries = [
        ...activeParentIncomeEntries,
        data.benefits.gifts && toPositiveCurrencyNumber(data.benefits.giftsAmount) > 0 ? {
            type: 'gift_support',
            description: 'Gift support',
            annualAmount: toPositiveCurrencyNumber(data.benefits.giftsAmount),
            isRecurring: true,
            frequency: 'annually',
        } : null,
    ].filter(Boolean);
    const otherTuitionEntries = data.otherChildren
        .filter((child) => readText(child.name) && readText(child.school))
        .map((child) => {
            const annualTuition = toPositiveCurrencyNumber(child.fullTuition);
            const scholarshipReceived = toPositiveCurrencyNumber(child.scholarship);
            return {
                schoolName: fallbackText(child.school, 'Other school'),
                studentName: fallbackText(child.name, 'Other child'),
                annualTuition,
                scholarshipReceived,
                netTuition: Math.max(0, annualTuition - scholarshipReceived),
            };
        });
    const majorEntries = [
        {
            description: 'Home improvements',
            value: data.majorExp.homeImprovements,
            reason: data.majorExp.homeImprovementsDetails,
        },
        {
            description: 'Large purchases',
            value: data.majorExp.largePurchases,
            reason: data.majorExp.largePurchasesDetails,
        },
        {
            description: 'Simcha',
            value: data.majorExp.simchaCost,
            reason: data.majorExp.simchaType,
        },
    ]
        .filter(({ value, reason }) => readText(value) || readText(reason))
        .map(({ description, value, reason }) => ({
            description,
            expectedAmount: toPositiveCurrencyNumber(value) || 0,
            reason: readText(reason) || null,
        }));
    const vehicleEntries = data.vehicles
        .filter((vehicle) => readText(vehicle.make) || readText(vehicle.model) || readText(vehicle.year))
        .map((vehicle) => ({
            make: fallbackText(vehicle.make, 'Unknown'),
            model: fallbackText(vehicle.model, 'Unknown'),
            year: Math.min(2100, Math.max(1900, toPositiveInteger(vehicle.year) || new Date().getFullYear())),
            estimatedValue: toPositiveCurrencyNumber(vehicle.value),
            loanBalance: toPositiveCurrencyNumber(vehicle.debt),
            monthlyPayment: toPositiveCurrencyNumber(vehicle.payment),
            isLeased: false,
        }));

    return {
        familyInformation: {
            familyName,
            homeAddress,
            homePhone: activeParentKeys
                .map((parentKey) => normalizeNonInvitePlaceholderPhone(data[parentKey].homePhone))
                .find(Boolean) || '',
            maritalStatus: primaryParent.isDivorced || Boolean(secondaryParent?.isDivorced)
                ? 'divorced'
                : householdComposition.mode === 'single_parent_guardian'
                    ? 'single'
                    : 'married',
            numberOfDependents: totalChildCount,
            numberOfYstStudents: ystStudentCount,
            numberOfPreschoolStudents: 0,
            householdSize: Math.max(1, totalChildCount + activeParentCount),
        },
        parent1: buildParentGuardianSection(
            data[primaryParentKey],
            resolveParentGuardianPersistenceRelationship(householdComposition, primaryParentKey),
            resolveParentGuardianSectionDisplayRole(householdComposition, primaryParentKey),
            householdComposition.primaryRole === 'other' ? readText(householdComposition.otherRoleLabel) || null : null,
            familyName,
            primaryParentKey === 'father' ? fatherEmail : motherEmail,
            existingPrimaryParent,
        ),
        parent2: secondaryParentKey
            ? buildParentGuardianSection(
                data[secondaryParentKey],
                resolveParentGuardianPersistenceRelationship(householdComposition, secondaryParentKey),
                resolveParentGuardianSectionDisplayRole(householdComposition, secondaryParentKey),
                null,
                familyName,
                motherEmail,
                existingSecondaryParent,
                { allowSyntheticIdentityFallbacks: false },
            )
            : null,
        employment: {
            employments: activeParentKeys
                .map((parentKey) => {
                    const activeParent = activeParentLookup[parentKey];
                    return readText(activeParent.parent.employer) ? {
                        parentIndex: parentKey === primaryParentKey ? 1 : 2,
                        employerName: activeParent.parent.employer,
                        position: readText(activeParent.parent.occupation) || null,
                        annualSalary: activeParent.income,
                        isPrimaryJob: true,
                    } : null;
                })
                .filter(Boolean),
            isSelfEmployed: activeParentKeys.some((parentKey) => Boolean(readText(data[parentKey].businessName))),
            selfEmploymentDescription: activeParentKeys
                .map((parentKey) => data[parentKey].businessName)
                .find((businessName) => readText(businessName)) || null,
        },
        income: {
            entries: incomeEntries,
            totalAnnualIncome,
            previousYearIncome: totalAnnualIncome,
            incomeChangedSignificantly: false,
        },
        assets: {
            entries: [
                liquidAssets > 0 ? {
                    type: 'checking_savings',
                    description: 'Checking and savings',
                    currentValue: liquidAssets,
                    isLiquid: true,
                } : null,
                investmentTotal > 0 ? {
                    type: 'investments',
                    description: 'Investments',
                    currentValue: investmentTotal,
                    isLiquid: true,
                } : null,
                homeValue > 0 ? {
                    type: 'real_estate',
                    description: 'Primary residence',
                    currentValue: homeValue,
                    isLiquid: false,
                } : null,
                vehicleValue > 0 ? {
                    type: 'vehicles',
                    description: 'Vehicles',
                    currentValue: vehicleValue,
                    isLiquid: false,
                } : null,
            ].filter(Boolean),
            totalAssets,
            cashOnHand: 0,
            checkingSavingsTotal: liquidAssets,
            investmentsTotal: investmentTotal,
            retirementTotal: 0,
        },
        liabilities: {
            entries: [
                mortgageBalance > 0 ? {
                    type: 'mortgage',
                    creditor: null,
                    currentBalance: mortgageBalance,
                    monthlyPayment: housingCost,
                    interestRate: toPositiveCurrencyNumber(data.assets.interestRate) || null,
                } : null,
                vehicleDebt > 0 ? {
                    type: 'car_loan',
                    creditor: null,
                    currentBalance: vehicleDebt,
                    monthlyPayment: data.vehicles.reduce((sum, vehicle) => sum + toPositiveCurrencyNumber(vehicle.payment), 0),
                } : null,
                studentLoans > 0 ? {
                    type: 'student_loan',
                    creditor: null,
                    currentBalance: studentLoans,
                    monthlyPayment: studentLoans,
                } : null,
                bankLoans > 0 ? {
                    type: 'personal_loan',
                    creditor: null,
                    currentBalance: bankLoans,
                    monthlyPayment: bankLoans,
                } : null,
            ].filter(Boolean),
            totalLiabilities,
            totalMonthlyDebtPayments: studentLoans + bankLoans,
        },
        expenses: {
            entries: [
                housingCost > 0 ? { category: 'housing', description: 'Housing', monthlyAmount: housingCost } : null,
                utilitiesTotal > 0 ? { category: 'housing', description: 'Utilities', monthlyAmount: utilitiesTotal } : null,
                foodTotal > 0 ? { category: 'food', description: 'Food', monthlyAmount: foodTotal } : null,
                healthcareTotal > 0 ? {
                    category: 'healthcare',
                    description: 'Health insurance',
                    monthlyAmount: healthcareTotal,
                    sourceRequirementFieldKey: 'expenses.healthInsurance',
                } : null,
                toPositiveCurrencyNumber(data.expenses.carInsurance) > 0 ? {
                    category: 'insurance',
                    description: 'Car insurance',
                    monthlyAmount: toPositiveCurrencyNumber(data.expenses.carInsurance),
                    sourceRequirementFieldKey: 'expenses.carInsurance',
                } : null,
                toPositiveCurrencyNumber(data.expenses.lifeInsurance) > 0 ? {
                    category: 'insurance',
                    description: 'Life insurance',
                    monthlyAmount: toPositiveCurrencyNumber(data.expenses.lifeInsurance),
                    sourceRequirementFieldKey: 'expenses.lifeInsurance',
                } : null,
                toPositiveCurrencyNumber(data.expenses.shulDues) > 0 ? {
                    category: 'other',
                    description: 'Shul dues',
                    monthlyAmount: toPositiveCurrencyNumber(data.expenses.shulDues),
                    sourceRequirementFieldKey: 'expenses.shulDues',
                } : null,
                toPositiveCurrencyNumber(data.expenses.cleaningHelp) > 0 ? {
                    category: 'other',
                    description: 'Cleaning help',
                    monthlyAmount: toPositiveCurrencyNumber(data.expenses.cleaningHelp),
                    sourceRequirementFieldKey: 'expenses.cleaningHelp',
                } : null,
                toPositiveCurrencyNumber(data.expenses.other) > 0 ? {
                    category: 'other',
                    description: 'Other monthly expenses',
                    monthlyAmount: toPositiveCurrencyNumber(data.expenses.other),
                    sourceRequirementFieldKey: 'expenses.other',
                } : null,
            ].filter(Boolean),
            housingCost,
            utilitiesTotal,
            transportationTotal: data.vehicles.reduce((sum, vehicle) => sum + toPositiveCurrencyNumber(vehicle.payment), 0),
            foodTotal,
            healthInsurance: toPositiveCurrencyNumber(data.expenses.healthInsurance),
            medicalCoverage: medicalCoverageType || undefined,
            medicalCoverageDetails: medicalCoverageDetails || null,
            healthcareTotal,
            insuranceTotal,
            otherTotal,
            totalMonthlyExpenses,
        },
        specialCircumstances: {
            hasUnusualExpenses: Boolean(readText(data.majorExp.extraordinaryObligations)),
            unusualExpensesDescription: readText(data.majorExp.extraordinaryObligations) || null,
            hasRecentJobLoss: activeParentKeys.some((parentKey) => Boolean(data[parentKey].isUnemployed)),
            jobLossDetails: activeParentKeys
                .map((parentKey) => data[parentKey].unemploymentBenefits)
                .find((value) => readText(value)) || null,
            hasMedicalExpenses: Boolean(medicalCoverageType || medicalCoverageDetails),
            medicalExpensesDetails: joinFinancialAidCopyParts(medicalCoverageType, medicalCoverageDetails) || null,
        },
        otherTuition: {
            entries: otherTuitionEntries,
            totalOtherTuition: otherTuitionEntries.reduce((sum, entry) => sum + entry.netTuition, 0),
        },
        majorExpenditures: {
            hasPlannedExpenditures: majorEntries.length > 0,
            entries: majorEntries,
            totalPlannedExpenditures: majorEntries.reduce((sum, entry) => sum + entry.expectedAmount, 0),
            hasSimchaPlanned: Boolean(readText(data.majorExp.simchaType) || readText(data.majorExp.simchaCost)),
            simchaDetails: readText(data.majorExp.simchaType) || null,
            servicesToYST: Boolean(data.majorExp.servicesToYST),
            servicesDescription: readText(data.majorExp.servicesDescription) || null,
        },
        businessOwnership: {
            ownsBusinesses: activeParentKeys.some((parentKey) => Boolean(readText(data[parentKey].businessName))),
            businesses: activeParentKeys
                .map((parentKey) => data[parentKey].businessName)
                .filter((businessName) => readText(businessName))
                .map((businessName) => ({
                    businessName: readText(businessName),
                    ownershipPercentage: 100,
                    businessType: 'other',
                    annualRevenue: null,
                    annualProfit: null,
                    numberOfEmployees: null,
                })),
        },
        realEstate: {
            ownsRealEstate: data.assets.housingType === 'own' && homeValue > 0,
            properties: data.assets.housingType === 'own' && homeValue > 0 ? [{
                propertyType: 'primary_residence',
                address: homeAddress,
                currentValue: homeValue,
                mortgageBalance,
                equity: homeValue - mortgageBalance,
                monthlyMortgage: housingCost,
                rentalIncome: 0,
            }] : [],
            totalRealEstateValue: homeValue,
            totalRealEstateEquity: homeValue - mortgageBalance,
        },
        vehicles: {
            vehicles: vehicleEntries,
            totalVehicleValue: vehicleValue,
            totalVehicleLoans: vehicleDebt,
        },
        additionalInfo: {
            receivedFinancialAidBefore: false,
            additionalComments: readText(data.majorExp.servicesDescription) || null,
            [PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY]: buildParentFinancialAidRawSnapshot(rawSnapshotData, certification),
        },
        ystStudents: existingYstStudents,
    };
};



type FinancialAidApplicationRecord = {
    id?: unknown;
    code?: unknown;
    academicYear?: unknown;
    status?: unknown;
    sections?: unknown;
    hydration?: unknown;
    invite?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const cloneFinancialAidData = (data: FinancialAidData): FinancialAidData =>
    JSON.parse(JSON.stringify(data)) as FinancialAidData;
const asBoolean = (value: unknown): boolean => value === true;
const readParentFinancialAidCopyCertificationContext = (
    sections: unknown,
): ParentFinancialAidCopyCertificationContext | null => {
    const certification = asRecord(asRecord(sections).certification);
    if (Object.keys(certification).length === 0) return null;
    return {
        certifiedAccurate: asBoolean(certification.certifiedAccurate),
        agreedToTerms: asBoolean(certification.agreedToTerms),
        electronicSignature: readText(certification.electronicSignature),
        certificationDate: readText(certification.certificationDate),
    };
};
const formatParentFinancialAidDeadlineLabel = (value: unknown): string => {
    const numeric = typeof value === 'number' ? value : Number(String(value ?? '').trim());
    if (!Number.isFinite(numeric) || numeric <= 0) return '';
    const timestampMs = numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
    try {
        return new Date(timestampMs).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    } catch {
        return '';
    }
};
const moneyText = (value: unknown): string => {
    const numeric = typeof value === 'number' ? value : Number(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(numeric) && numeric > 0 ? String(Math.round(numeric)) : '';
};
const hasRecordKey = (record: Record<string, unknown>, key: string): boolean =>
    Object.prototype.hasOwnProperty.call(record, key);
const moneyTextAllowingZero = (value: unknown): string => {
    const normalized = typeof value === 'number' ? value : String(value ?? '').replace(/,/g, '').trim();
    if (normalized === '') return '';
    const numeric = typeof normalized === 'number' ? normalized : Number(normalized);
    return Number.isFinite(numeric) && numeric >= 0 ? String(Math.round(numeric)) : '';
};
const looksCurrencyLikeText = (value: unknown): boolean => {
    const text = readText(value);
    if (!text) return false;
    return /^-?\d+(\.\d+)?$/.test(text.replace(/[$,\s]/g, ''));
};
const nonMoneyText = (value: unknown): string => {
    const text = readText(value);
    if (!text) return '';
    return moneyTextAllowingZero(text) ? '' : text;
};

const firstMatchingEntry = (entries: unknown[], matcher: (entry: Record<string, unknown>) => boolean): Record<string, unknown> | null => {
    for (const entry of entries) {
        const record = asRecord(entry);
        if (matcher(record)) return record;
    }
    return null;
};

const hydrateTextFieldsFromSnapshot = (
    target: Record<string, unknown>,
    source: Record<string, unknown>,
    fieldNames: string[],
) => {
    fieldNames.forEach((fieldName) => {
        if (hasRecordKey(source, fieldName)) {
            target[fieldName] = readText(source[fieldName]);
        }
    });
};

const hydrateBooleanFieldsFromSnapshot = (
    target: Record<string, unknown>,
    source: Record<string, unknown>,
    fieldNames: string[],
) => {
    fieldNames.forEach((fieldName) => {
        if (hasRecordKey(source, fieldName)) {
            target[fieldName] = asBoolean(source[fieldName]);
        }
    });
};

const readCampSnapshot = (value: unknown): Camp[] =>
    asArray(value).map(asRecord).map((camp) => ({
        id: readText(camp.id) || crypto.randomUUID(),
        childName: readText(camp.childName),
        campName: readText(camp.campName),
        fee: readText(camp.fee),
    }));

const readInvestmentSnapshot = (value: unknown): Investment[] =>
    asArray(value).map(asRecord).map((investment) => ({
        id: readText(investment.id) || crypto.randomUUID(),
        description: readText(investment.description),
        value: readText(investment.value),
    }));

const readOtherChildSnapshot = (value: unknown): OtherChild[] =>
    asArray(value).map(asRecord).map((child) => ({
        id: readText(child.id) || crypto.randomUUID(),
        name: readText(child.name),
        age: readText(child.age),
        grade: readText(child.grade),
        school: readText(child.school),
        fullTuition: readText(child.fullTuition),
        scholarship: readText(child.scholarship),
    }));

const readVehicleSnapshot = (value: unknown): Vehicle[] =>
    asArray(value).map(asRecord).map((vehicle) => ({
        id: readText(vehicle.id) || crypto.randomUUID(),
        year: readText(vehicle.year),
        make: readText(vehicle.make),
        model: readText(vehicle.model),
        value: readText(vehicle.value),
        debt: readText(vehicle.debt),
        payment: readText(vehicle.payment),
    }));

const hydrateParentFinancialAidDataFromRawSnapshot = (
    next: FinancialAidData,
    sections: Record<string, unknown>,
): void => {
    const additionalInfo = asRecord(sections.additionalInfo);
    const rawSnapshot = asRecord(additionalInfo[PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY]);
    if (Object.keys(rawSnapshot).length === 0) return;

    if (hasRecordKey(rawSnapshot, 'householdComposition')) {
        next.householdComposition = normalizeParentGuardianHouseholdComposition(rawSnapshot.householdComposition);
    }
    const fatherSnapshot = asRecord(rawSnapshot.father);
    const motherSnapshot = asRecord(rawSnapshot.mother);
    hydrateTextFieldsFromSnapshot(next.father as unknown as Record<string, unknown>, fatherSnapshot, [
        'title',
        'firstName',
        'lastName',
        'address',
        'city',
        'state',
        'zip',
        'cellPhone',
        'homePhone',
        'email',
        'occupation',
        'employer',
        'businessName',
        'shul',
        'rabbi',
    ]);
    hydrateTextFieldsFromSnapshot(next.mother as unknown as Record<string, unknown>, motherSnapshot, [
        'title',
        'firstName',
        'lastName',
        'address',
        'city',
        'state',
        'zip',
        'cellPhone',
        'homePhone',
        'email',
        'occupation',
        'employer',
        'businessName',
        'shul',
        'rabbi',
    ]);
    if (hasRecordKey(fatherSnapshot, 'unemploymentWeeklyAmount')) {
        next.father.unemploymentBenefits = readText(fatherSnapshot.unemploymentWeeklyAmount);
    }
    if (hasRecordKey(fatherSnapshot, 'isUnemployed')) {
        next.father.isUnemployed = asBoolean(fatherSnapshot.isUnemployed);
    }
    if (hasRecordKey(fatherSnapshot, 'isDivorced')) {
        next.father.isDivorced = asBoolean(fatherSnapshot.isDivorced);
    }
    if (hasRecordKey(motherSnapshot, 'unemploymentWeeklyAmount')) {
        next.mother.unemploymentBenefits = readText(motherSnapshot.unemploymentWeeklyAmount);
    }
    if (hasRecordKey(motherSnapshot, 'isUnemployed')) {
        next.mother.isUnemployed = asBoolean(motherSnapshot.isUnemployed);
    }
    if (hasRecordKey(motherSnapshot, 'isDivorced')) {
        next.mother.isDivorced = asBoolean(motherSnapshot.isDivorced);
    }
    hydrateTextFieldsFromSnapshot(next.incomeFather as unknown as Record<string, unknown>, asRecord(rawSnapshot.incomeFather), [
        'gross2024',
        'parsonage',
        'fedTax',
        'stateTax',
        'ficaTax',
        'refunds',
        'estimated2025',
    ]);
    hydrateTextFieldsFromSnapshot(next.incomeMother as unknown as Record<string, unknown>, asRecord(rawSnapshot.incomeMother), [
        'gross2024',
        'parsonage',
        'fedTax',
        'stateTax',
        'ficaTax',
        'refunds',
        'estimated2025',
    ]);

    const benefits = asRecord(rawSnapshot.benefits);
    hydrateTextFieldsFromSnapshot(next.benefits as unknown as Record<string, unknown>, benefits, [
        'healthInsurance',
        'tuition',
        'auto',
        'other',
        'giftsAmount',
    ]);
    hydrateBooleanFieldsFromSnapshot(next.benefits as unknown as Record<string, unknown>, benefits, ['gifts']);

    const assets = asRecord(rawSnapshot.assets);
    hydrateTextFieldsFromSnapshot(next.assets as unknown as Record<string, unknown>, assets, [
        'purchasePrice',
        'purchaseDate',
        'mortgageBalance',
        'mortgagePayment',
        'mortgageYears',
        'propertyTaxes',
        'interestRate',
        'rentPayment',
        'utilities',
        'additionalProperties',
    ]);
    if (assets.housingType === 'own' || assets.housingType === 'rent') {
        next.assets.housingType = assets.housingType;
    }

    if (hasRecordKey(rawSnapshot, 'vehicles')) {
        next.vehicles = readVehicleSnapshot(rawSnapshot.vehicles);
    }

    if (hasRecordKey(rawSnapshot, 'investments')) {
        next.investments = readInvestmentSnapshot(rawSnapshot.investments);
    }

    hydrateTextFieldsFromSnapshot(next.savings as unknown as Record<string, unknown>, asRecord(rawSnapshot.savings), [
        'bank',
        'accountNumber',
        'balance',
    ]);
    hydrateTextFieldsFromSnapshot(next.checking as unknown as Record<string, unknown>, asRecord(rawSnapshot.checking), [
        'bank',
        'accountNumber',
        'balance',
    ]);

    if (hasRecordKey(rawSnapshot, 'childrenAssets')) {
        next.childrenAssets = readInvestmentSnapshot(rawSnapshot.childrenAssets);
    }

    const expenses = asRecord(rawSnapshot.expenses);
    hydrateTextFieldsFromSnapshot(next.expenses as unknown as Record<string, unknown>, expenses, [
        'housing',
        'utilities',
        'studentLoans',
        'food',
        'tuition',
        'bankLoans',
        'lifeInsurance',
        'shulDues',
        'carInsurance',
        'healthInsurance',
        'cleaningHelp',
        'other',
        'medicalCoverage',
        'medicalCoverageDetails',
    ]);
    hydrateBooleanFieldsFromSnapshot(next.expenses as unknown as Record<string, unknown>, expenses, ['taxesIncluded']);

    const majorExp = asRecord(rawSnapshot.majorExp);
    hydrateTextFieldsFromSnapshot(next.majorExp as unknown as Record<string, unknown>, majorExp, [
        'homeImprovements',
        'homeImprovementsDetails',
        'largePurchases',
        'largePurchasesDetails',
        'extraordinaryObligations',
        'simchaType',
        'simchaCost',
        'servicesDescription',
        'loanAmount',
    ]);
    hydrateBooleanFieldsFromSnapshot(next.majorExp as unknown as Record<string, unknown>, majorExp, ['servicesToYST']);

    if (hasRecordKey(rawSnapshot, 'otherChildren')) {
        next.otherChildren = readOtherChildSnapshot(rawSnapshot.otherChildren);
    }
    if (hasRecordKey(rawSnapshot, 'camps2025')) {
        next.camps2025 = ensureFirstCampRow(readCampSnapshot(rawSnapshot.camps2025));
    }
    if (hasRecordKey(rawSnapshot, 'camps2024')) {
        next.camps2024 = ensureFirstCampRow(readCampSnapshot(rawSnapshot.camps2024));
    }

    const vacation = asRecord(rawSnapshot.vacation);
    hydrateTextFieldsFromSnapshot(next.vacation as unknown as Record<string, unknown>, vacation, ['destinations', 'cost']);
    hydrateBooleanFieldsFromSnapshot(next.vacation as unknown as Record<string, unknown>, vacation, [
        'traveled',
        'parentsTraveled',
        'childrenTraveled',
    ]);

    const contract = asRecord(rawSnapshot.contract);
    if (hasRecordKey(contract, 'childrenCount')) {
        next.contract.childrenCount = toNonNegativeInteger(contract.childrenCount as string | number | null | undefined);
    }
    hydrateTextFieldsFromSnapshot(next.contract as unknown as Record<string, unknown>, contract, [
        'totalTuition',
        'totalFees',
        'preschoolTuition',
        'eligibleTuition',
    ]);
    hydrateTextFieldsFromSnapshot(next.request as unknown as Record<string, unknown>, asRecord(rawSnapshot.request), [
        'requestedAmount',
        'offeredTuition',
        'offeredFees',
    ]);

    const signatures = asRecord(rawSnapshot.signatures);
    const nextSignatures = next.signatures as unknown as Record<string, unknown>;
    nextSignatures.fatherCaptured = Boolean(signatures.fatherCaptured || readText(signatures.father));
    nextSignatures.motherCaptured = Boolean(signatures.motherCaptured || readText(signatures.mother));
    hydrateTextFieldsFromSnapshot(nextSignatures, signatures, ['date']);
    hydrateBooleanFieldsFromSnapshot(
        next.signatures.attestations as unknown as Record<string, unknown>,
        asRecord(signatures.attestations),
        ['certifiedAccurate', 'authorizedVerification', 'incomeChangeAgreement'],
    );
};

const hasHydratableParentFinancialAidSections = (application: FinancialAidApplicationRecord): boolean => {
    const sections = asRecord(application.sections);
    return [
        'familyInformation',
        'parent1',
        'parent2',
        'income',
        'assets',
        'liabilities',
        'expenses',
        'otherTuition',
        'majorExpenditures',
        'businessOwnership',
        'realEstate',
        'vehicles',
        'additionalInfo',
        'ystStudents',
    ].some((key) => {
        const value = sections[key];
        if (Array.isArray(value)) return value.length > 0;
        return Object.keys(asRecord(value)).length > 0;
    });
};

const resolveConfiguredParentFinancialAidDocumentsForApplication = (
    application: FinancialAidApplicationRecord | null | undefined,
    configuredDocuments: ParentFinancialAidDocumentStatus[],
): ParentFinancialAidDocumentStatus[] => {
    const sections = asRecord(application?.sections);
    const checklist = asRecord(sections.documentChecklist);
    const snapshotDocuments = buildParentFinancialAidDocumentsFromRequiredTypes(
        checklist.requiredDocumentTypes,
        configuredDocuments,
    );
    return snapshotDocuments ?? configuredDocuments;
};

const hasHydratableParentInput = (parent: Record<string, unknown>): boolean => [
    parent.title,
    parent.firstName,
    parent.lastName,
    parent.email,
    parent.occupation,
    parent.employer,
    parent.unemploymentDuration,
].some((value) => readNonInvitePlaceholderText(value))
    || Boolean(readNonInvitePlaceholderPhone(parent.cellPhone))
    || Boolean(readNonInvitePlaceholderPhone(parent.workPhone))
    || asBoolean(parent.isUnemployed)
    || asBoolean(parent.isDivorced);

const buildParentFinancialAidDataFromApplication = (
    application: FinancialAidApplicationRecord,
    profile: { email?: string } | null | undefined,
    configuredDocuments: ParentFinancialAidDocumentStatus[] = [],
): FinancialAidData => {
    const next = createEmptyFinancialAidData();
    const sections = asRecord(application.sections);
    const familyInformation = asRecord(sections.familyInformation);
    const homeAddress = asRecord(familyInformation.homeAddress);
    const fatherParent = readExistingParentGuardianSection(sections, 'father');
    const motherParent = readExistingParentGuardianSection(sections, 'mother');
    const familyName = readNonInvitePlaceholderFamilyText(familyInformation.familyName);
    const hasFatherSection = Object.keys(fatherParent).length > 0;
    const hasMotherSection = Object.keys(motherParent).length > 0;
    const hasFatherInput = hasHydratableParentInput(fatherParent);
    const hasMotherInput = hasHydratableParentInput(motherParent);
    const primaryExistingParentKey: ParentGuardianFormKey = hasFatherInput || !hasMotherInput ? 'father' : 'mother';
    const primaryExistingParentRecord = primaryExistingParentKey === 'father' ? fatherParent : motherParent;
    const secondaryExistingParentKey: ParentGuardianFormKey | null = hasFatherInput && hasMotherInput
        ? (primaryExistingParentKey === 'father' ? 'mother' : 'father')
        : null;
    const secondaryExistingParentRecord = secondaryExistingParentKey === 'father'
        ? fatherParent
        : secondaryExistingParentKey === 'mother'
            ? motherParent
            : null;
    if (hasFatherInput !== hasMotherInput && hasFatherSection !== hasMotherSection) {
        const primaryRelationship = readParentGuardianRelationship(primaryExistingParentRecord.relationship);
        const persistedPrimaryRole = readParentGuardianDisplayRole(primaryExistingParentRecord.displayRole);
        const primaryRole: ParentGuardianDisplayRole =
            primaryRelationship === 'mother'
                ? 'mother'
                : primaryRelationship === 'father'
                    ? 'father'
                    : persistedPrimaryRole ?? 'legal_guardian';
        next.householdComposition = normalizeParentGuardianHouseholdComposition({
            mode: 'single_parent_guardian',
            primaryKey: primaryExistingParentKey,
            primaryRole,
            otherRoleLabel: readText(primaryExistingParentRecord.otherRoleLabel),
        });
    }
    const income = asRecord(sections.income);
    const assets = asRecord(sections.assets);
    const liabilities = asRecord(sections.liabilities);
    const expenses = asRecord(sections.expenses);
    const specialCircumstances = asRecord(sections.specialCircumstances);
    const vehicles = asRecord(sections.vehicles);
    const otherTuition = asRecord(sections.otherTuition);
    const majorExpenditures = asRecord(sections.majorExpenditures);
    const ystStudents = asArray(sections.ystStudents).map(asRecord);
    const incomeEntries = asArray(income.entries).map(asRecord);

    const fatherIncome = firstMatchingEntry(incomeEntries, (entry) => readText(entry.description).toLowerCase().includes('father'));
    const motherIncome = firstMatchingEntry(incomeEntries, (entry) => readText(entry.description).toLowerCase().includes('mother'));
    const totalIncome = moneyText(income.totalAnnualIncome);

    const hydrateParentFormKeyFromSection = (
        parentKey: ParentSectionKey,
        parent: Record<string, unknown>,
        options: { fallbackLastName: string; fallbackEmail?: string; fallbackHomePhone?: string },
    ) => {
        const existingParent = next[parentKey];
        const parentAddress = asRecord(parent.address);
        next[parentKey] = {
            ...existingParent,
            title: readText(parent.title),
            firstName: readNonInvitePlaceholderText(parent.firstName),
            lastName: readNonInvitePlaceholderText(parent.lastName) || options.fallbackLastName,
            address: readText(parentAddress.street) || readText(homeAddress.street),
            city: readText(parentAddress.city) || readText(homeAddress.city),
            state: readText(parentAddress.state) || readText(homeAddress.state),
            zip: readText(parentAddress.zipCode ?? parentAddress.zip) || readText(homeAddress.zipCode),
            cellPhone: readNonInvitePlaceholderPhone(parent.cellPhone),
            homePhone: readNonInvitePlaceholderPhone(parent.homePhone) || options.fallbackHomePhone || readNonInvitePlaceholderPhone(parent.workPhone),
            email: normalizeEmail(parent.email, options.fallbackEmail),
            occupation: readText(parent.occupation),
            employer: readText(parent.employer),
            isUnemployed: asBoolean(parent.isUnemployed),
            unemploymentBenefits: readText(parent.unemploymentWeeklyAmount)
                || (looksCurrencyLikeText(parent.unemploymentDuration) ? readText(parent.unemploymentDuration) : ''),
            isDivorced: readText(familyInformation.maritalStatus) === 'divorced',
            ssnLast4OnFile: readFinancialAidSocialSecurityLast4(parent.socialSecurityLast4) || existingParent.ssnLast4OnFile,
        };
    };

    const familyHomePhone = readNonInvitePlaceholderPhone(familyInformation.homePhone);
    hydrateParentFormKeyFromSection(primaryExistingParentKey, primaryExistingParentRecord, {
        fallbackLastName: familyName,
        fallbackEmail: normalizeEmail(profile?.email),
        fallbackHomePhone: familyHomePhone,
    });
    if (secondaryExistingParentKey && secondaryExistingParentRecord) {
        hydrateParentFormKeyFromSection(secondaryExistingParentKey, secondaryExistingParentRecord, {
            fallbackLastName: '',
            fallbackHomePhone: next[primaryExistingParentKey].homePhone || familyHomePhone,
        });
    }

    next.incomeFather.gross2024 = moneyText(fatherIncome?.annualAmount) || (hasFatherInput && !motherIncome ? totalIncome : '');
    next.incomeMother.gross2024 = moneyText(motherIncome?.annualAmount) || (hasMotherInput && !fatherIncome ? totalIncome : '');
    next.savings.balance = moneyText(assets.checkingSavingsTotal);
    next.checking.balance = moneyText(assets.cashOnHand);
    next.investments = moneyText(assets.investmentsTotal)
        ? [{ id: crypto.randomUUID(), description: 'Investments', value: moneyText(assets.investmentsTotal) }]
        : next.investments;

    const liabilityEntries = asArray(liabilities.entries).map(asRecord);
    const mortgage = firstMatchingEntry(liabilityEntries, (entry) => readText(entry.type) === 'mortgage');
    next.assets.purchasePrice = moneyText(asRecord(firstMatchingEntry(asArray(assets.entries), (entry) => readText(entry.type) === 'real_estate') ?? {}).currentValue);
    next.assets.mortgageBalance = moneyText(mortgage?.currentBalance);
    next.assets.mortgagePayment = moneyText(expenses.housingCost || mortgage?.monthlyPayment);
    next.assets.utilities = moneyText(expenses.utilitiesTotal);
    next.expenses.housing = moneyText(expenses.housingCost);
    next.expenses.utilities = moneyText(expenses.utilitiesTotal);
    next.expenses.food = moneyText(expenses.foodTotal);
    const rawMedicalCoverage = hasRecordKey(expenses, 'medicalCoverage') ? expenses.medicalCoverage : undefined;
    next.expenses.healthInsurance = hasRecordKey(expenses, 'healthInsurance')
        ? moneyTextAllowingZero(expenses.healthInsurance)
        : moneyText(expenses.healthcareTotal);
    next.expenses.medicalCoverage = hasRecordKey(expenses, 'medicalCoverage') ? readText(rawMedicalCoverage) : '';
    next.expenses.medicalCoverageDetails = nonMoneyText(rawMedicalCoverage)
        || readText(specialCircumstances.medicalExpensesDetails);
    next.expenses.lifeInsurance = moneyText(expenses.insuranceTotal);
    next.expenses.other = moneyText(expenses.otherTotal);

    const vehicleEntries = asArray(vehicles.vehicles).map(asRecord);
    if (vehicleEntries.length > 0) {
        next.vehicles = vehicleEntries.map((vehicle) => ({
            id: crypto.randomUUID(),
            year: readText(vehicle.year),
            make: readText(vehicle.make),
            model: readText(vehicle.model),
            value: moneyText(vehicle.estimatedValue),
            debt: moneyText(vehicle.loanBalance),
            payment: moneyText(vehicle.monthlyPayment),
        }));
    }

    const otherTuitionEntries = asArray(otherTuition.entries).map(asRecord);
    next.otherChildren = otherTuitionEntries.map((entry) => ({
        id: crypto.randomUUID(),
        name: readText(entry.studentName),
        age: '',
        grade: '',
        school: readText(entry.schoolName),
        fullTuition: moneyText(entry.annualTuition),
        scholarship: moneyText(entry.scholarshipReceived),
    }));

    const majorEntries = asArray(majorExpenditures.entries).map(asRecord);
    const homeImprovementEntry = asRecord(firstMatchingEntry(majorEntries, (entry) => readText(entry.description).toLowerCase().includes('home')) ?? {});
    const largePurchaseEntry = asRecord(firstMatchingEntry(majorEntries, (entry) => readText(entry.description).toLowerCase().includes('large')) ?? {});
    const simchaEntry = asRecord(firstMatchingEntry(majorEntries, (entry) => readText(entry.description).toLowerCase().includes('simcha')) ?? {});
    next.majorExp.homeImprovements = moneyText(homeImprovementEntry.expectedAmount);
    next.majorExp.homeImprovementsDetails = readText(homeImprovementEntry.reason);
    next.majorExp.largePurchases = moneyText(largePurchaseEntry.expectedAmount);
    next.majorExp.largePurchasesDetails = readText(largePurchaseEntry.reason);
    next.majorExp.simchaCost = moneyText(simchaEntry.expectedAmount);
    next.majorExp.simchaType = readText(simchaEntry.reason);
    next.majorExp.extraordinaryObligations = readText(specialCircumstances.unusualExpensesDescription);
    next.majorExp.servicesToYST = asBoolean(majorExpenditures.servicesToYST);
    next.majorExp.servicesDescription = readText(majorExpenditures.servicesDescription);

    if (ystStudents.length > 0) {
        const totalTuition = ystStudents.reduce((sum, student) => sum + cleanNumber(moneyText(student.tuitionAmount)), 0);
        const totalFees = ystStudents.reduce((sum, student) => sum + cleanNumber(moneyText(student.feesAmount)), 0);
        next.contract.childrenCount = ystStudents.length;
        next.contract.totalTuition = moneyText(totalTuition);
        next.contract.totalFees = moneyText(totalFees);
        next.contract.eligibleTuition = moneyText(totalTuition);
    } else {
        next.contract.childrenCount = Number(familyInformation.numberOfYstStudents) || 0;
    }

    hydrateParentFinancialAidDataFromRawSnapshot(next, sections);
    next.documents = restoreParentFinancialAidDocumentsFromApplication(
        application,
        resolveConfiguredParentFinancialAidDocumentsForApplication(application, configuredDocuments),
    );

    return next;
};

const restoreParentFinancialAidDocumentsFromApplication = (
    application: FinancialAidApplicationRecord,
    configuredDocuments: ParentFinancialAidDocumentStatus[],
): ParentFinancialAidDocumentStatus[] => {
    if (configuredDocuments.length === 0) {
        return [];
    }

    const sections = asRecord(application.sections);
    const checklist = asRecord(sections.documentChecklist);
    const checklistItems = asArray(checklist.items).map(asRecord);
    const uploadedByType = new Map<string, Record<string, unknown>>();
    checklistItems.forEach((item) => {
        const documentType = normalizeParentFinancialAidDocumentId(item.documentType);
        if (!documentType) return;

        const existing = uploadedByType.get(documentType);
        const itemUploaded = asBoolean(item.isUploaded);
        const existingUploaded = existing ? asBoolean(existing.isUploaded) : false;
        const itemFileName = readText(item.fileName);
        const existingFileName = existing ? readText(existing.fileName) : '';
        if (!existing || (!existingUploaded && itemUploaded) || (!existingFileName && itemFileName)) {
            uploadedByType.set(documentType, item);
        }
    });

    return configuredDocuments.map((document) => {
        const item = uploadedByType.get(normalizeParentFinancialAidDocumentId(document.id));
        if (!item) {
            return { ...document };
        }
        const uploaded = asBoolean(item.isUploaded);
        return {
            ...document,
            uploaded,
            fileName: uploaded ? readText(item.fileName) || document.fileName : undefined,
        };
    });
};

const preserveConfirmedParentFinancialAidDocumentUploads = (
    currentDocuments: ParentFinancialAidDocumentStatus[],
    hydratedDocuments: ParentFinancialAidDocumentStatus[],
): ParentFinancialAidDocumentStatus[] => {
    if (currentDocuments.length === 0 || hydratedDocuments.length === 0) {
        return hydratedDocuments;
    }

    const currentById = new Map(
        currentDocuments.map((document) => [normalizeParentFinancialAidDocumentId(document.id), document]),
    );

    return hydratedDocuments.map((document) => {
        const current = currentById.get(normalizeParentFinancialAidDocumentId(document.id));
        if (!current?.uploaded || document.uploaded) {
            return document;
        }
        return {
            ...document,
            uploaded: true,
            fileName: current.fileName || document.fileName,
        };
    });
};

const resolveInviteEntryParams = (search: string): { inviteToken: string; academicYear: string } => {
    const params = new URLSearchParams(search || '');
    return {
        inviteToken: readText(params.get('invite')),
        academicYear: readText(params.get('academicYear')),
    };
};

const CANONICAL_PARENT_FINANCIAL_AID_PATH = '/parent/portal/financialaid';

const buildClaimedInviteParentFinancialAidRoute = (
    pathname: string,
    search: string,
    academicYear: string,
    applicationId: string,
): { pathname: string; search: string } => {
    const params = new URLSearchParams(search || '');
    params.delete('invite');
    const resolvedAcademicYear = readText(academicYear) || readText(params.get('academicYear'));
    if (resolvedAcademicYear) {
        params.set('academicYear', resolvedAcademicYear);
    }
    const resolvedApplicationId = readText(applicationId);
    if (resolvedApplicationId) {
        params.set('applicationId', resolvedApplicationId);
    }
    const query = params.toString();
    const nextPathname = pathname.startsWith('/parent/portal--new/')
        || pathname === CANONICAL_PARENT_FINANCIAL_AID_PATH
        || pathname.startsWith(`${CANONICAL_PARENT_FINANCIAL_AID_PATH}/`)
        ? CANONICAL_PARENT_FINANCIAL_AID_PATH
        : pathname;
    return {
        pathname: nextPathname,
        search: query ? `?${query}` : '',
    };
};

const resolveHydrationBannerMessage = (application: FinancialAidApplicationRecord): string | null => {
    const hydration = asRecord(application.hydration);
    return readText(hydration.bannerMessage) || null;
};

const resolveHydrationBannerKey = (application: FinancialAidApplicationRecord): string | null => {
    const applicationId = readText(application.id);
    const hydration = asRecord(application.hydration);
    const hydratedAt = readText(hydration.hydratedAt);
    const sourceApplicationId = readText(hydration.sourceApplicationId);
    const sourceAcademicYear = readText(hydration.sourceAcademicYear);
    const message = resolveHydrationBannerMessage(application);
    if (!message) return null;
    return [
        applicationId || 'unknown-application',
        hydratedAt || sourceApplicationId || sourceAcademicYear || message,
    ].join(':');
};

const isClaimedOrFamilyLinkedParentApplication = (
    application: ParentFinancialAidApplicationLike | null | undefined,
): boolean => {
    const record = asRecord(application);
    const invite = asRecord(record.invite);
    const familyLink = asRecord(record.familyLink);
    return Boolean(readText(invite.claimedBy) || readText(familyLink.applicantId));
};

const isClaimedInviteParentApplication = (
    application: ParentFinancialAidApplicationLike | null | undefined,
): boolean => {
    const record = asRecord(application);
    const invite = asRecord(record.invite);
    if (Object.keys(invite).length === 0) return false;
    const familyLink = asRecord(record.familyLink);
    return Boolean(readText(invite.claimedBy) || readText(familyLink.applicantId));
};

const isGenericOpenCycleParentApplication = (
    application: ParentFinancialAidApplicationLike | null | undefined,
): boolean => {
    if (isClaimedOrFamilyLinkedParentApplication(application)) return false;
    const record = asRecord(application);
    if (readText(record.entrySource) === 'generic_open_cycle') return true;
    const sections = asRecord(record.sections);
    const additionalInfo = asRecord(sections.additionalInfo);
    return readText(additionalInfo.inviteEntrySource) === 'generic_open_cycle';
};

const doesOpenCycleMatchAcademicYear = (
    openCycleRecord: Record<string, unknown>,
    academicYear: string,
): boolean => {
    const openCycleAcademicYear = readText(openCycleRecord.academicYear) || readText(openCycleRecord.schoolYear);
    return doFinancialAidAcademicYearsMatch(openCycleAcademicYear, academicYear);
};

// --- Components ---

const ProgressBar = ({ currentStep, onStepClick }: { currentStep: number, onStepClick: (step: number) => void }) => {
    const steps = PARENT_FINANCIAL_AID_STEP_LABELS;
    const progress = Math.round(((currentStep + 1) / steps.length) * 100);

    return (
        <div className="mb-8">
            <div className="flex justify-between items-end mb-2">
                <span className="text-[10px] font-bold text-irongrey/50 tracking-widest">APPLICATION PROGRESS</span>
                <span className="text-[10px] font-bold text-irongrey/50 tracking-widest">{progress}%</span>
            </div>
            <div className="h-1.5 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
                <div
                    className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
            <div className="hidden md:flex justify-between relative">
                {steps.map((step, idx) => (
                    <button
                        key={step}
                        onClick={() => idx <= currentStep ? onStepClick(idx) : null}
                        disabled={idx > currentStep}
                        className={`text-[10px] font-bold tracking-wide text-left transition-colors ${
                            idx === currentStep ? 'text-primary' :
                            idx < currentStep ? 'text-primary/70 cursor-pointer hover:text-primary' :
                            'text-irongrey/30 cursor-not-allowed'
                        }`}
                    >
                        {idx + 1}. {step}
                    </button>
                ))}
            </div>
             <div className="md:hidden text-center text-xs font-bold text-primary">
                Step {currentStep + 1}: {steps[currentStep]}
            </div>
        </div>
    );
};

const SectionHeader: React.FC<{ icon?: React.ElementType, title: string, subtitle?: string }> = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-start gap-4 mb-6">
        {Icon && <div className="p-2 bg-primary/10 rounded-lg text-primary"><Icon size={20} /></div>}
        <div>
            <h3 className="text-lg font-bold text-irongrey font-heading">{title}</h3>
            {subtitle && <p className="text-xs text-irongrey/50 mt-1">{subtitle}</p>}
        </div>
    </div>
);

const CurrencyInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ value, onChange, className, ...props }) => {
    const format = (v: string | number | readonly string[] | undefined) => {
        if (!v) return '';
        const str = v.toString().replace(/,/g, '');
        if (isNaN(Number(str))) return str;
        // Split decimal to protect trailing dot
        const parts = str.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/,/g, '');
        // Allow basic number patterns: "123", "123.", "123.45"
        if (raw && !/^\d*\.?\d*$/.test(raw)) return;

        // Synthetic event to propagate cleaned value to parent
        const syntheticEvent = {
            ...e,
            target: { ...e.target, value: raw }
        };
        if (onChange) onChange(syntheticEvent);
    };

    return (
        <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-irongrey/40 font-semibold">$</span>
            <Input
                {...props}
                className={`pl-7 ${className}`}
                type="text"
                value={format(value)}
                onChange={handleChange}
            />
        </div>
    );
};

// --- Signature Pad (Reused) ---
const SignaturePad: React.FC<{
    label: string,
    value: string | null,
    onChange: (val: string | null) => void
}> = ({ label, value, onChange }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.strokeStyle = '#404f4d';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        if (value) {
            const img = new Image();
            img.src = value;
            img.onload = () => ctx.drawImage(img, 0, 0);
        }
    }, []);

    const getPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        let clientX, clientY;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = (e as React.MouseEvent).clientX;
            clientY = (e as React.MouseEvent).clientY;
        }
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const start = (e: any) => { setIsDrawing(true); const ctx = canvasRef.current?.getContext('2d'); ctx?.beginPath(); ctx?.moveTo(getPos(e).x, getPos(e).y); };
    const draw = (e: any) => { if (!isDrawing) return; const ctx = canvasRef.current?.getContext('2d'); const p = getPos(e); ctx?.lineTo(p.x, p.y); ctx?.stroke(); };
    const stop = () => { if (!isDrawing) return; setIsDrawing(false); onChange(canvasRef.current?.toDataURL() || null); };
    const clear = () => { const ctx = canvasRef.current?.getContext('2d'); ctx?.clearRect(0,0,400,160); onChange(null); };

    return (
        <div className="w-full">
            <InputLabel>{label}</InputLabel>
            <div className="border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 touch-none overflow-hidden relative group hover:border-primary/30 transition-colors">
                <canvas ref={canvasRef} width={400} height={160} className="w-full h-40 cursor-crosshair"
                    onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
                    onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
                {!value && !isDrawing && <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-irongrey/20 font-bold text-sm">Sign here</div>}
                <button onClick={clear} className="absolute top-2 right-2 p-1.5 rounded-lg bg-white shadow-sm border border-gray-100 text-irongrey/40 hover:text-strawberry hover:bg-strawberry/5 transition-colors"><Trash2 size={16}/></button>
            </div>
        </div>
    );
};

const FinancialAidTechnicalSupportDetails = ({ report }: { report: FinancialAidActionErrorReport }) => {
    if (!report.technicalReport) return null;

    return (
        <details
            className="mt-3 rounded-xl border border-strawberry/20 bg-white/75 px-3 py-2 text-irongrey"
            data-testid="parent-financial-aid-error-technical-details"
        >
            <summary className="cursor-pointer text-xs font-extrabold uppercase tracking-[0.12em] text-strawberry">
                Technical details for support
            </summary>
            <p className="mt-2 text-xs font-semibold leading-5 text-irongrey/70">
                Share this support report with Intellio support if the issue continues.
            </p>
            <pre
                className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-white px-3 py-2 text-[11px] leading-5 text-irongrey"
                data-testid="parent-financial-aid-error-technical-report"
            >
                {report.technicalReport}
            </pre>
        </details>
    );
};

type FinancialAidReportPopupWindow = Window | null;

const openPendingFinancialAidReportWindow = (): FinancialAidReportPopupWindow => {
    if (typeof window === 'undefined' || typeof window.open !== 'function') return null;
    try {
        const pendingShellHtml = [
            '<!doctype html>',
            '<html>',
            '<head><title>Preparing PDF</title></head>',
            '<body style="margin:0;background:#fff;color:#243b3a;font-family:system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;">',
            '<main style="padding:32px;line-height:1.5;">',
            '<h1 style="font-size:24px;margin:0 0 12px;">Preparing your PDF...</h1>',
            '<p style="margin:0;max-width:720px;">The YST Financial Aid Report will open here when it is ready.</p>',
            '</main>',
            '</body>',
            '</html>',
        ].join('');
        const popup = window.open('', '_blank');
        if (!popup || popup.closed) return null;
        popup.document.open();
        popup.document.write(pendingShellHtml);
        popup.document.close();
        return popup;
    } catch {
        return null;
    }
};

const closePendingFinancialAidReportWindow = (popup: FinancialAidReportPopupWindow): void => {
    try {
        if (popup && !popup.closed) popup.close();
    } catch {
        // Ignore browser-managed popup lifecycle errors.
    }
};

const NON_PARENT_INVITE_SESSION_MESSAGE =
    'Please sign in with a parent account to use this financial aid invitation. If you are signed in as school staff, sign out first and reopen the invite from the parent email.';

type InviteEntryErrorReport = FinancialAidActionErrorReport & {
    code?: string | null;
};

const createInviteConfirmationUnavailableReport = (technicalMessage: string | null = null): InviteEntryErrorReport => ({
    ...createFinancialAidActionErrorReport(
        FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_MESSAGE,
        technicalMessage,
        FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE,
    ),
});

const isInviteConfirmationUnavailableReport = (report: FinancialAidActionErrorReport | null): boolean =>
    isFinancialAidInviteConfirmationUnavailableCode((report as InviteEntryErrorReport | null)?.code);

const readInviteClaimErrorReport = (error: unknown): FinancialAidActionErrorReport => {
    const fallback = 'Unable to claim this financial aid invitation right now.';
    const report = readFinancialAidActionErrorReport(error, fallback);
    if (/^Forbidden:.*requires persona in \[[^\]]*\bparent\b[^\]]*\]/i.test(report.userMessage)) {
        return createFinancialAidActionErrorReport(NON_PARENT_INVITE_SESSION_MESSAGE, report.technicalMessage);
    }
    const parentSafeInviteMessages = [
        /^Please sign in to claim this financial aid invitation\.$/i,
        /^Please sign in using the same email address that received this financial aid invitation\.$/i,
        /^Please sign in with a parent account to use this financial aid invitation\./i,
        /^Financial aid invitation access requires an authenticated parent account\.$/i,
        /^We could not confirm the email address on your signed-in account\. Please sign in again, or ask the school to resend the invitation\.$/i,
        /^This financial aid invitation could not be found\. Ask the school to resend it\.$/i,
        /^This financial aid invitation is missing setup details\. Ask the school to resend it\.$/i,
        /^This financial aid invitation has already been claimed by another parent account\.$/i,
        /^This financial aid invitation is for .+, which is not currently open\. Please contact the school office if you need help\.$/i,
    ];
    if (isFinancialAidInviteConfirmationUnavailableCode(report.code)) {
        return createInviteConfirmationUnavailableReport(report.technicalMessage);
    }
    const isKnownParentSafeInviteMessage = parentSafeInviteMessages.some((pattern) => pattern.test(report.userMessage));
    return createFinancialAidActionErrorReport(
        isKnownParentSafeInviteMessage ? report.userMessage : fallback,
        report.technicalMessage,
    );
};

const readParentYstReportErrorReport = (
    error: unknown,
    fallback: string,
): FinancialAidActionErrorReport => {
    const report = readFinancialAidActionErrorReport(error, fallback);
    const staleReportText = [
        report.userMessage,
        report.technicalMessage,
    ].filter(Boolean).join('\n');
    if (!/stale after a staff correction/i.test(staleReportText)) {
        return report;
    }
    return createFinancialAidActionErrorReport(
        'This YST Financial Aid Report is stale after a staff correction. Generate the report again before sharing or reviewing it.',
        report.technicalMessage,
        report.code,
    );
};

const hasParentSessionGroup = (groups: unknown): boolean => (
    Array.isArray(groups)
        ? groups.some((group) => String(group ?? '').trim().toLowerCase() === 'parent')
        : false
);

const shouldBlockInviteClaimForNonParentSession = ({
    effectivePersona,
    groups,
}: {
    effectivePersona: unknown;
    groups: unknown;
}): boolean => {
    if (hasParentSessionGroup(groups)) return false;
    const persona = String(effectivePersona ?? '').trim().toLowerCase();
    if (persona === 'parent') return false;
    if (persona && persona !== 'guest' && persona !== 'parent') return true;
    return Array.isArray(groups) && groups.length > 0;
};

const createNonParentInviteSessionErrorReport = (): FinancialAidActionErrorReport =>
    createFinancialAidActionErrorReport(NON_PARENT_INVITE_SESSION_MESSAGE);

// --- Main Form ---

const StudentForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<FinancialAidData>(() => createEmptyFinancialAidData());
    const { profile, effectivePersona } = useShellProfile();
    const inviteEntryParams = useMemo(() => resolveInviteEntryParams(location.search), [location.search]);
    const settingsModuleConfigQuery = useGetSettingsModuleConfigQuery({ moduleKey: SETTINGS_MODULE_KEY });
    const settingsPayload = (settingsModuleConfigQuery.data as { payload?: unknown } | undefined)?.payload;
    const settingsState = useMemo(
        () => parseSettingsState(settingsPayload),
        [settingsPayload],
    );
    const configuredRequiredDocuments = useMemo(() => {
        if (settingsModuleConfigQuery.isError) {
            return getDefaultParentFinancialAidDocuments();
        }
        return buildConfiguredParentFinancialAidDocuments(settingsState.financialAid.requiredDocuments);
    }, [settingsModuleConfigQuery.isError, settingsState.financialAid.requiredDocuments]);
    const financialAidRequirementPolicy = settingsState.financialAid.requirementPolicy;
    const openCycle = useFinancialAidOpenCycle();
    const openCycleRecord = asRecord(openCycle.data);
    const effectiveFinancialAidAcademicYear = resolveEffectiveFinancialAidAcademicYear({
        inviteAcademicYear: inviteEntryParams.academicYear,
        openCycle: openCycleRecord,
        settingsState,
    });
    const financialAidDisplayAcademicYear = resolveFinancialAidDisplayAcademicYear({
        inviteAcademicYear: inviteEntryParams.academicYear,
        openCycle: openCycleRecord,
        settingsState,
    });
    const financialAidDisplayYears = useMemo(
        () => resolveFinancialAidDisplayYears(financialAidDisplayAcademicYear),
        [financialAidDisplayAcademicYear],
    );
    const configuredFinancialAidAcademicYear = financialAidDisplayYears.academicYear;
    const isFinancialAidAcademicYearConfigured = financialAidDisplayYears.hasCycleAuthority && Boolean(configuredFinancialAidAcademicYear);
    const financialAidCycleInlineLabel = financialAidAcademicYearInlineLabel(financialAidDisplayAcademicYear);
    const configuredFinancialAidCycleStatus = useMemo(
        () => inviteEntryParams.academicYear
            ? resolveFinancialAidCycleStatusFromSettings(settingsState, effectiveFinancialAidAcademicYear)
            : null,
        [effectiveFinancialAidAcademicYear, inviteEntryParams.academicYear, settingsState],
    );
    const parentApplicantId = readText(profile?.userSub);
    const parentFinancialAidApplications = useFinancialAidByApplicant(parentApplicantId, {
        academicYear: effectiveFinancialAidAcademicYear,
        enabled: Boolean(parentApplicantId),
    });
    const isSettingsModuleConfigPending = Boolean(
        (settingsModuleConfigQuery as { isLoading?: unknown; isFetching?: unknown }).isLoading
        || (settingsModuleConfigQuery as { isLoading?: unknown; isFetching?: unknown }).isFetching,
    );
    const isFinancialAidOpenCyclePending =
        (openCycle.isLoading && !openCycle.error)
        || isSettingsModuleConfigPending;
    const isFinancialAidDocumentSettingsPending = Boolean(
        isSettingsModuleConfigPending
        && !settingsModuleConfigQuery.data
        && !settingsModuleConfigQuery.error,
    );
    const isParentFinancialAidStatusLookupPending = Boolean(parentApplicantId) && Boolean(
        parentFinancialAidApplications.isLoading,
    );
    const isParentFinancialAidLandingLookupPending =
        isFinancialAidOpenCyclePending || isParentFinancialAidStatusLookupPending;
    const parentFinancialAidApplicationItems =
        ((parentFinancialAidApplications.data as { items?: ParentFinancialAidApplicationLike[] } | undefined)?.items ?? []);
    const parentFinancialAidApplicationSummaries = useMemo(
        () => summarizeParentFinancialAidApplications(parentFinancialAidApplicationItems, {
            applicantId: parentApplicantId,
            academicYear: effectiveFinancialAidAcademicYear,
            includeFutureYears: true,
        }),
        [effectiveFinancialAidAcademicYear, parentApplicantId, parentFinancialAidApplicationItems],
    );
    const statusViewParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const isStatusViewModeRequested = statusViewParams.get('view') === 'status';
    const claimedInviteNavigationState = useMemo(
        () => asRecord(asRecord(location.state).parentFinancialAidClaimedInvite),
        [location.state],
    );
    const routeApplicationId = readText(statusViewParams.get('applicationId'));
    const routeApplicationFromParentList = useMemo(
        () => routeApplicationId
            ? parentFinancialAidApplicationItems.find((application) => readText(application.id) === routeApplicationId) ?? null
            : null,
        [parentFinancialAidApplicationItems, routeApplicationId],
    );
    const routeApplicationSummaryFromParentList = useMemo(
        () => summarizeParentFinancialAidApplication(routeApplicationFromParentList),
        [routeApplicationFromParentList],
    );
    const isRouteApplicationIdFromClaimState = Boolean(
        routeApplicationId && readText(claimedInviteNavigationState.applicationId) === routeApplicationId,
    );
    const shouldFetchRouteApplication = Boolean(
        routeApplicationId
        && (
            isStatusViewModeRequested
            || isRouteApplicationIdFromClaimState
            || routeApplicationFromParentList
        ),
    );
    const statusViewApplication = useFinancialAidApplication(
        shouldFetchRouteApplication ? routeApplicationId : undefined,
    );
    const statusViewApplicationRecord = statusViewApplication.data as FinancialAidApplicationRecord | undefined;
    const isStatusViewApplicationDetailsPending = Boolean(shouldFetchRouteApplication)
        && readText(statusViewApplicationRecord?.id) !== routeApplicationId
        && Boolean(
            (statusViewApplication as { isLoading?: unknown; isFetching?: unknown }).isLoading
            || (statusViewApplication as { isLoading?: unknown; isFetching?: unknown }).isFetching,
        );
    const statusViewApplicationSummary = useMemo(
        () => {
            if (!routeApplicationId) return null;
            const listedSummary = parentFinancialAidApplicationSummaries.find((summary) => summary.id === routeApplicationId);
            if (listedSummary) return listedSummary;
            if (routeApplicationSummaryFromParentList) return routeApplicationSummaryFromParentList;
            if (!shouldFetchRouteApplication) return null;
            if (readText(statusViewApplicationRecord?.id) !== routeApplicationId) return null;
            return summarizeParentFinancialAidApplication(statusViewApplicationRecord);
        },
        [
            parentFinancialAidApplicationSummaries,
            routeApplicationId,
            routeApplicationSummaryFromParentList,
            shouldFetchRouteApplication,
            statusViewApplicationRecord,
        ],
    );
    const currentParentFinancialAidApplication = useMemo(
        () => selectCurrentParentFinancialAidApplication(
            parentFinancialAidApplicationItems,
            {
                applicantId: parentApplicantId,
                academicYear: effectiveFinancialAidAcademicYear,
                includeFutureYears: true,
            },
        ),
        [
            effectiveFinancialAidAcademicYear,
            parentApplicantId,
            parentFinancialAidApplicationItems,
        ],
    );
    const currentApplicationSummary = useMemo(
        () => resolveCurrentParentFinancialAidApplicationSummary(
            parentFinancialAidApplicationItems,
            {
                applicantId: parentApplicantId,
                academicYear: effectiveFinancialAidAcademicYear,
                includeFutureYears: true,
            },
        ),
        [
            effectiveFinancialAidAcademicYear,
            parentApplicantId,
            parentFinancialAidApplicationItems,
        ],
    );
    const isOpenCycleLookupError = Boolean(openCycle.error);
    const isGenericOpenCycleAuthorityUnavailable = Boolean(
        !inviteEntryParams.academicYear
        && openCycle.data
        && openCycleRecord.isOpen !== true,
    );
    const isOpenCycleDataUnavailableForEffectiveYear = Boolean(
        isGenericOpenCycleAuthorityUnavailable
        || (configuredFinancialAidCycleStatus
            ? configuredFinancialAidCycleStatus.isOpen !== true
            : (
                openCycle.data
                && (
                    openCycleRecord.isOpen !== true
                    || !doesOpenCycleMatchAcademicYear(openCycleRecord, effectiveFinancialAidAcademicYear)
                )
            )),
    );
    const isOpenCycleUnavailableForEffectiveYear = Boolean(
        isOpenCycleLookupError || isOpenCycleDataUnavailableForEffectiveYear,
    );
    const draftActorId = resolveFinancialAidDraftActorId(profile);
    const pointerStorageKey = resolveParentFinancialAidPointerStorageKey(
        draftActorId,
        effectiveFinancialAidAcademicYear,
    );
    const legacyPointerStorageKey = resolveLegacyParentFinancialAidPointerStorageKey(draftActorId);
    const createApplication = useCreateFinancialAidApplication();
    const updateApplication = useUpdateFinancialAidApplication();
    const {
        mutateAsync: recordInviteOpenAsync,
        isPending: isRecordInviteOpenPending,
    } = useRecordFinancialAidInviteOpen();
    const {
        mutateAsync: claimInviteAsync,
        isPending: isClaimInvitePending,
    } = useClaimFinancialAidInvite();
    const uploadDocument = useUploadFinancialAidDocument();
    const confirmDocumentUpload = useConfirmFinancialAidDocumentUpload();
    const respondToInfoRequest = useRespondToInfoRequest();
    const submitApplication = useSubmitFinancialAidApplication();
    const generateApplicationReport = useGenerateFinancialAidApplicationReport();
    const getReportDownloadUrl = useGetFinancialAidApplicationReportDownloadUrl();
    const initialDataRef = useRef<FinancialAidData>(createEmptyFinancialAidData());
    const activePointerStorageKeyRef = useRef<string | null>(null);
    const claimedInviteTokenRef = useRef<string | null>(null);
    const claimedInviteCanonicalizedApplicationRef = useRef<{
        applicationId: string;
        statusToken: string;
        summary: ParentFinancialAidApplicationSummary | null;
        hydrationBannerMessage: string | null;
        hydrationBannerKey: string | null;
    } | null>(null);
    const claimedInviteNavigationStateKeyRef = useRef<string | null>(null);
    const inviteClaimRunIdRef = useRef(0);
    const isMountedRef = useRef(true);
    const pendingResumeStartedAtRef = useRef<number | null>(null);
    const hydratedApplicationIdRef = useRef<string | null>(null);
    const hydratedApplicationDocumentsRef = useRef<string | null>(null);
    const restoredExistingDraftDuringCreateRef = useRef(false);
    const submitInFlightRef = useRef(false);
    const submissionCompletedRef = useRef(false);
    const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);
    const [draftHydrated, setDraftHydrated] = useState(false);
    const [isFormStarted, setIsFormStarted] = useState(false);
    const [draftApplicationId, setDraftApplicationId] = useState<string | null>(null);
    const [pendingResumeApplicationId, setPendingResumeApplicationId] = useState<string | null>(null);
    const financialAidApplication = useFinancialAidApplication(draftApplicationId ?? undefined);
    const documentStatus = useFinancialAidDocumentStatus(draftApplicationId ?? undefined);
    const activeConfiguredRequiredDocuments = useMemo(() => {
        const application = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        return resolveConfiguredParentFinancialAidDocumentsForApplication(application, configuredRequiredDocuments);
    }, [configuredRequiredDocuments, financialAidApplication.data]);
    const activeConfiguredRequiredDocumentHydrationKey = useMemo(
        () => activeConfiguredRequiredDocuments.map((document) => `${document.id}:${document.name}`).join('|'),
        [activeConfiguredRequiredDocuments],
    );
    const [uploadError, setUploadError] = useState<FinancialAidActionErrorReport | null>(null);
    const [submissionError, setSubmissionError] = useState<FinancialAidActionErrorReport | null>(null);
    const [saveDraftMessage, setSaveDraftMessage] = useState<string | null>(null);
    const [saveDraftError, setSaveDraftError] = useState<FinancialAidActionErrorReport | null>(null);
    const [downloadError, setDownloadError] = useState<FinancialAidActionErrorReport | null>(null);
    const [moreInfoResponseMessage, setMoreInfoResponseMessage] = useState('');
    const [completedMoreInfoResponseByApplicationId, setCompletedMoreInfoResponseByApplicationId] = useState<Record<string, CompletedMoreInfoResponseMarker>>(() => {
        const storedMarker = readStoredCompletedMoreInfoResponseMarker(routeApplicationId);
        return routeApplicationId && storedMarker ? { [routeApplicationId]: storedMarker } : {};
    });
    const [inviteEntryMessage, setInviteEntryMessage] = useState<string | null>(null);
    const [inviteEntryError, setInviteEntryError] = useState<FinancialAidActionErrorReport | null>(null);
    const [inviteCycleRetryAttempt, setInviteCycleRetryAttempt] = useState(0);
    const [inviteClaimedApplicationSummary, setInviteClaimedApplicationSummary] = useState<ParentFinancialAidApplicationSummary | null>(null);
    const [hydrationBannerMessage, setHydrationBannerMessage] = useState<string | null>(null);
    const [hydrationBannerKey, setHydrationBannerKey] = useState<string | null>(null);

    const readExistingDraftFinancialAidSections = (): Record<string, unknown> => {
        const loadedApplication = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        if (draftApplicationId && readText(loadedApplication?.id) === draftApplicationId) {
            return asRecord(loadedApplication?.sections);
        }
        if (draftApplicationId && readText(statusViewApplicationRecord?.id) === draftApplicationId) {
            return asRecord(statusViewApplicationRecord?.sections);
        }
        return {};
    };
    const applyPersistedSsnClearRequests = () => {
        setFormData((current) => {
            let changed = false;
            const next = cloneFinancialAidData(current);
            (['father', 'mother'] as ParentSectionKey[]).forEach((parentKey) => {
                const parent = next[parentKey];
                if (parent.ssnLast4ClearRequested
                    && !isValidFinancialAidSocialSecurityNumber(parent.ssn)
                    && parent.ssnLast4OnFile) {
                    parent.ssnLast4OnFile = '';
                    changed = true;
                }
            });
            return changed ? next : current;
        });
    };
    const dismissedHydrationBannerKeysRef = useRef<Set<string>>(new Set());
    const [submittedReference, setSubmittedReference] = useState<string | null>(null);
    const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
    const [submittedCertification, setSubmittedCertification] = useState<ParentFinancialAidCopyCertificationContext | null>(null);
    const [completionStatus, setCompletionStatus] = useState<'submitted' | 'decision'>('submitted');
    const [isSsnVisible, setIsSsnVisible] = useState<{ father: boolean; mother: boolean }>({ father: false, mother: false });
    const [validationRequested, setValidationRequested] = useState(false);
    const financialAidCertificationStatements = settingsState.financialAid.certificationStatements;
    const financialAidValidationOptions = useMemo(() => ({
        academicYear: financialAidDisplayAcademicYear,
        requirementPolicy: financialAidRequirementPolicy,
        householdComposition: resolveParentGuardianHouseholdComposition(formData),
        certificationStatements: financialAidCertificationStatements,
    }), [financialAidCertificationStatements, financialAidDisplayAcademicYear, financialAidRequirementPolicy, formData]);
    const validationIssues = useMemo(
        () => getParentFinancialAidValidationIssues(formData, financialAidValidationOptions),
        [financialAidValidationOptions, formData],
    );
    const currentStepValidationIssues = useMemo(
        () => getParentFinancialAidStepValidationIssues(formData, currentStep, financialAidValidationOptions),
        [currentStep, financialAidValidationOptions, formData],
    );
    const visibleValidationIssues = validationRequested
        ? (currentStep < 8 ? currentStepValidationIssues : validationIssues)
        : [];
    const isConfiguredRequired = useCallback(
        (fieldKey: string) => shouldParentFinancialAidShowConfiguredRequiredLabel(
            formData,
            financialAidRequirementPolicy,
            fieldKey,
        ),
        [financialAidRequirementPolicy, formData],
    );
    const isConfiguredVisible = useCallback(
        (fieldKey: string) => shouldParentFinancialAidShowConfiguredField(
            financialAidRequirementPolicy,
            fieldKey,
        ),
        [financialAidRequirementPolicy],
    );
    const routeApplicationSummaryForActiveYear =
        routeApplicationId
        && doFinancialAidAcademicYearsMatch(
            statusViewApplicationSummary?.academicYear,
            effectiveFinancialAidAcademicYear,
        )
            ? statusViewApplicationSummary
            : null;
    const activeApplicationSummary =
        inviteClaimedApplicationSummary
        ?? (
            isStatusViewModeRequested
                ? (statusViewApplicationSummary ?? currentApplicationSummary)
                : (routeApplicationSummaryForActiveYear ?? currentApplicationSummary)
        );
    const activeApplicationAcademicYear = activeApplicationSummary?.academicYear ?? '';
    const activeApplicationMatchesEffectiveYear =
        doFinancialAidAcademicYearsMatch(activeApplicationAcademicYear, effectiveFinancialAidAcademicYear);
    const activeApplicationIsFutureOfEffectiveYear = isParentFinancialAidAcademicYearAfter(
        activeApplicationAcademicYear,
        effectiveFinancialAidAcademicYear,
    );
    const activeApplicationMatchesLandingYear =
        activeApplicationMatchesEffectiveYear || activeApplicationIsFutureOfEffectiveYear;
    // INT-568: a resumable draft from an earlier cycle must keep its own
    // continue path even after the resolved current cycle moves forward.
    // Without this, the saved draft is stranded on the landing page and the
    // only visible action ("Start Application") silently creates a new
    // application for the wrong school year. The draft stays actionable only
    // while its own cycle is not known-closed in settings.
    const isResumablePriorYearParentFinancialAidSummary = useCallback(
        (summary: ParentFinancialAidApplicationSummary | null | undefined): boolean => {
            if (!summary?.canResume) return false;
            if (!isParentFinancialAidAcademicYearAfter(effectiveFinancialAidAcademicYear, summary.academicYear)) {
                return false;
            }
            const ownCycleStatus = resolveFinancialAidCycleStatusFromSettings(settingsState, summary.academicYear);
            return !ownCycleStatus || (ownCycleStatus.isOpen === true && ownCycleStatus.isClosed !== true);
        },
        [effectiveFinancialAidAcademicYear, settingsState],
    );
    const activeApplicationIsResumablePriorYearDraft =
        isResumablePriorYearParentFinancialAidSummary(activeApplicationSummary);
    const activeApplicationIsGenericOpenCycle = Boolean(
        !inviteClaimedApplicationSummary
        && activeApplicationMatchesEffectiveYear
        && isGenericOpenCycleParentApplication(currentParentFinancialAidApplication),
    );
    const activeApplicationIsClaimedInvite = Boolean(
        Boolean(inviteClaimedApplicationSummary)
        || (
            activeApplicationMatchesEffectiveYear
            && isClaimedInviteParentApplication(currentParentFinancialAidApplication)
        ),
    );
    const activeApplicationRequiresOpenCycle = Boolean(
        activeApplicationSummary?.canResume
        && (activeApplicationIsGenericOpenCycle || activeApplicationIsClaimedInvite),
    );
    const isRequestedInviteCycleClosed = Boolean(
        inviteEntryParams.inviteToken
        && inviteEntryParams.academicYear
        && configuredFinancialAidCycleStatus
        && configuredFinancialAidCycleStatus.isOpen !== true,
    );
    const isRequestedInviteCycleStatusError = Boolean(
        inviteEntryParams.inviteToken
        && inviteEntryParams.academicYear
        && settingsModuleConfigQuery.isError
        && (
            !configuredFinancialAidCycleStatus
            || configuredFinancialAidCycleStatus.isOpen === true
        )
        && !isSettingsModuleConfigPending,
    );
    const isRequestedInviteCycleStatusPending = Boolean(
        inviteEntryParams.inviteToken
        && inviteEntryParams.academicYear
        && !configuredFinancialAidCycleStatus
        && isSettingsModuleConfigPending,
    );
    const shouldDeferRequestedInviteCycleDecisionToBackend = Boolean(
        isRequestedInviteCycleClosed
        && inviteEntryParams.inviteToken,
    );
    const shouldClosedRequestedInviteBlockEntry = Boolean(
        isRequestedInviteCycleClosed
        && !activeApplicationSummary?.canResume
        && !shouldDeferRequestedInviteCycleDecisionToBackend,
    );
    const activeApplicationCycleStatus = useMemo(
        () => activeApplicationAcademicYear
            ? resolveFinancialAidCycleStatusFromSettings(settingsState, activeApplicationAcademicYear)
            : null,
        [activeApplicationAcademicYear, settingsState],
    );
    const isActiveApplicationCycleClosed = Boolean(
        activeApplicationRequiresOpenCycle
        && (
            activeApplicationCycleStatus
                ? activeApplicationCycleStatus.isOpen !== true
                : (
                    activeApplicationMatchesEffectiveYear
                    && isOpenCycleDataUnavailableForEffectiveYear
                )
        ),
    );
    const hasApplicationScopedCycleAuthority = Boolean(
        activeApplicationSummary?.canResume
        || shouldDeferRequestedInviteCycleDecisionToBackend,
    );
    const isGenericInviteCycleClosed = Boolean(
        !hasApplicationScopedCycleAuthority
        && !isRequestedInviteCycleStatusError
        && (
            (
                (!activeApplicationSummary || !activeApplicationMatchesLandingYear)
                && isOpenCycleUnavailableForEffectiveYear
                && !inviteEntryParams.inviteToken
            )
            || (
                activeApplicationRequiresOpenCycle
                && activeApplicationMatchesEffectiveYear
                && isOpenCycleDataUnavailableForEffectiveYear
            )
            || shouldClosedRequestedInviteBlockEntry
            || isActiveApplicationCycleClosed
        ),
    );
    const isFinancialAidEntryCycleClosed = isGenericInviteCycleClosed;
    const financialAidCycleClosedInlineLabel = financialAidAcademicYearInlineLabel(
        shouldClosedRequestedInviteBlockEntry && inviteEntryParams.academicYear
            ? inviteEntryParams.academicYear
            : (
                isFinancialAidEntryCycleClosed && activeApplicationAcademicYear
                    ? activeApplicationAcademicYear
                    : financialAidDisplayAcademicYear
            ),
    );
    const isParentStatusViewRequested =
        isStatusViewModeRequested && Boolean(routeApplicationId);
    const parentStatusView = useFinancialAidParentStatus(
        isParentStatusViewRequested ? routeApplicationId : undefined,
    );
    const loadedDraftApplicationRecord = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
    const parentMoreInfoStatusRecord = asRecord(parentStatusView.data);
    const observedParentStatusApplicationId = readText(parentMoreInfoStatusRecord.applicationId);
    const observedParentStatusToken = normalizeParentFinancialAidStatusToken(parentMoreInfoStatusRecord.status);
    const observedParentStatusAtMs = readFinancialAidStatusObservationTimestamp(parentMoreInfoStatusRecord);
    const observedParentStatusMoreInfoRequestKey = readFinancialAidMoreInfoRequestKey(parentMoreInfoStatusRecord);
    const observedStatusViewApplicationId = readText(statusViewApplicationRecord?.id);
    const observedStatusViewApplicationStatusToken = normalizeParentFinancialAidStatusToken(statusViewApplicationRecord?.status);
    const observedStatusViewApplicationRecord = asRecord(statusViewApplicationRecord);
    const observedStatusViewApplicationAtMs = readFinancialAidStatusObservationTimestamp(observedStatusViewApplicationRecord);
    const observedStatusViewApplicationMoreInfoRequestKey = readFinancialAidMoreInfoRequestKey(observedStatusViewApplicationRecord);
    const observedLoadedDraftApplicationRecord = asRecord(loadedDraftApplicationRecord);
    const observedLoadedDraftApplicationId = readText(observedLoadedDraftApplicationRecord.id);
    const observedLoadedDraftStatusToken = normalizeParentFinancialAidStatusToken(observedLoadedDraftApplicationRecord.status);
    const observedLoadedDraftApplicationAtMs = readFinancialAidStatusObservationTimestamp(observedLoadedDraftApplicationRecord);
    const observedLoadedDraftApplicationMoreInfoRequestKey = readFinancialAidMoreInfoRequestKey(observedLoadedDraftApplicationRecord);
    const observedParentListApplicationRecord = asRecord(routeApplicationFromParentList);
    const observedParentListApplicationId = readText(observedParentListApplicationRecord.id);
    const observedParentListStatusToken = normalizeParentFinancialAidStatusToken(observedParentListApplicationRecord.status);
    const observedParentListApplicationAtMs = readFinancialAidStatusObservationTimestamp(observedParentListApplicationRecord);
    const observedParentListApplicationMoreInfoRequestKey = readFinancialAidMoreInfoRequestKey(observedParentListApplicationRecord);
    const isMoreInfoResponseLocallyCompleted = (applicationId: string | null | undefined): boolean =>
        Boolean(applicationId && completedMoreInfoResponseByApplicationId[applicationId]);
    const readCompletedMoreInfoResponseMarker = (
        applicationId: string | null | undefined,
    ): CompletedMoreInfoResponseMarker | null => (
        applicationId ? completedMoreInfoResponseByApplicationId[applicationId] ?? null : null
    );
    const isSameCompletedMoreInfoRequest = (
        marker: CompletedMoreInfoResponseMarker | null,
        requestKey: string,
    ): boolean => {
        if (!marker) return false;
        if (!marker.answeredRequestKey) return marker.phase === 'pending_return' || !requestKey;
        if (!requestKey) return false;
        return doFinancialAidMoreInfoRequestKeysMatch(marker.answeredRequestKey, requestKey);
    };
    const hasDifferentCompletedMoreInfoRequest = (
        marker: CompletedMoreInfoResponseMarker | null,
        requestKeys: string[],
    ): boolean => Boolean(marker && requestKeys.some((requestKey) => (
        Boolean(requestKey) && !isSameCompletedMoreInfoRequest(marker, requestKey)
    )));
    const isDifferentCompletedMoreInfoRequest = (
        marker: CompletedMoreInfoResponseMarker | null,
        requestKey: string,
    ): boolean => Boolean(
        marker
        && requestKey
        && !isSameCompletedMoreInfoRequest(marker, requestKey),
    );
    const isCompletedMoreInfoResponseCurrentForObservedRequests = (
        marker: CompletedMoreInfoResponseMarker | null,
        requestKeys: string[],
    ): boolean => Boolean(marker && !hasDifferentCompletedMoreInfoRequest(marker, requestKeys));
    const doesCompletedMoreInfoResponseMatchRequest = (
        marker: CompletedMoreInfoResponseMarker | null,
        requestKey: string,
    ): boolean => (
        isSameCompletedMoreInfoRequest(marker, requestKey)
        && isCompletedMoreInfoResponseCurrentForObservedRequests(marker, [requestKey])
    );
    const readSummaryMoreInfoRequestKey = (
        summary: ParentFinancialAidApplicationSummary | null | undefined,
    ): string => (
        normalizeParentFinancialAidStatusToken(summary?.statusToken) === 'more_info_needed'
            ? readFinancialAidMoreInfoRequestKey({
                moreInfoRequestId: summary?.moreInfoRequestId,
                requestedInfo: summary?.requestedInfo,
                responseDeadline: summary?.responseDeadline,
                requestedAt: summary?.moreInfoRequestDiscriminator,
            })
            : ''
    );
    useEffect(() => {
        if (!routeApplicationId) return;
        const storedMarker = readStoredCompletedMoreInfoResponseMarker(routeApplicationId);
        if (!storedMarker) return;
        setCompletedMoreInfoResponseByApplicationId((current) => (
            current[routeApplicationId] ? current : { ...current, [routeApplicationId]: storedMarker }
        ));
    }, [routeApplicationId]);
    useEffect(() => {
        const timers = Object.entries(completedMoreInfoResponseByApplicationId)
            .map(([applicationId, marker]) => {
                const markerAnchorAtMs = marker.phase === 'returned'
                    ? marker.returnedAtMs
                    : marker.completedAtMs;
                if (!markerAnchorAtMs) return null;
                const staleAtMs = markerAnchorAtMs + COMPLETED_MORE_INFO_RESPONSE_STALE_RETURN_GRACE_MS;
                const delayMs = Math.max(0, staleAtMs - Date.now() + 1);
                return window.setTimeout(() => {
                    setCompletedMoreInfoResponseByApplicationId((current) => {
                        const currentMarker = current[applicationId];
                        const currentAnchorAtMs = currentMarker?.phase === 'returned'
                            ? currentMarker.returnedAtMs
                            : currentMarker?.completedAtMs;
                        if (
                            !currentMarker
                            || currentMarker.phase !== marker.phase
                            || !currentAnchorAtMs
                            || currentAnchorAtMs !== markerAnchorAtMs
                            || Date.now() - currentAnchorAtMs <= COMPLETED_MORE_INFO_RESPONSE_STALE_RETURN_GRACE_MS
                        ) {
                            return current;
                        }
                        const updated = { ...current };
                        delete updated[applicationId];
                        clearStoredCompletedMoreInfoResponseMarker(applicationId);
                        return updated;
                    });
                }, delayMs);
            })
            .filter((timer): timer is number => timer !== null);
        return () => {
            timers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [completedMoreInfoResponseByApplicationId]);
    useEffect(() => {
        const observations = new Map<string, FinancialAidStatusObservation[]>();
        const addObservation = (
            applicationId: string,
            statusToken: string,
            observedAtMs: number,
            moreInfoRequestKey: string,
        ) => {
            if (!applicationId || !statusToken) return;
            const existing = observations.get(applicationId) ?? [];
            observations.set(applicationId, [...existing, {
                applicationId,
                statusToken,
                observedAtMs,
                moreInfoRequestKey: statusToken === 'more_info_needed' ? moreInfoRequestKey : '',
            }]);
        };
        addObservation(observedParentStatusApplicationId, observedParentStatusToken, observedParentStatusAtMs, observedParentStatusMoreInfoRequestKey);
        addObservation(observedStatusViewApplicationId, observedStatusViewApplicationStatusToken, observedStatusViewApplicationAtMs, observedStatusViewApplicationMoreInfoRequestKey);
        addObservation(observedLoadedDraftApplicationId, observedLoadedDraftStatusToken, observedLoadedDraftApplicationAtMs, observedLoadedDraftApplicationMoreInfoRequestKey);
        addObservation(observedParentListApplicationId, observedParentListStatusToken, observedParentListApplicationAtMs, observedParentListApplicationMoreInfoRequestKey);
        if (observations.size === 0) return;

        setCompletedMoreInfoResponseByApplicationId((current) => {
            let next: Record<string, CompletedMoreInfoResponseMarker> | null = null;
            observations.forEach((applicationObservations, applicationId) => {
                const source = next ?? current;
                const marker = source[applicationId];
                if (!marker) return;

                const returnedObservations = applicationObservations.filter(
                    (observation) => observation.statusToken !== 'more_info_needed',
                );
                const moreInfoObservations = applicationObservations.filter(
                    (observation) => observation.statusToken === 'more_info_needed',
                );
                const latestReturnedObservationAtMs = returnedObservations.reduce(
                    (latest, observation) => Math.max(latest, observation.observedAtMs || 0),
                    0,
                );
                const hasResolvedStatusObservation = returnedObservations.some((observation) =>
                    shouldClearCompletedMoreInfoResponseMarkerForStatus(observation.statusToken),
                );
                const hasFreshMoreInfoObservationAfterResponse = moreInfoObservations.some((observation) => (
                    (observation.observedAtMs || 0) > marker.completedAtMs
                    || (
                        (observation.observedAtMs || 0) === marker.completedAtMs
                        && hasDifferentCompletedMoreInfoRequest(marker, [observation.moreInfoRequestKey])
                    )
                ));
                const hasNewKeyedMoreInfoRequestAfterKeylessResponse = Boolean(
                    marker.phase === 'returned'
                    && !marker.answeredRequestKey
                    && moreInfoObservations.some((observation) => Boolean(observation.moreInfoRequestKey))
                );
                const hasExpiredReturnedMoreInfoObservation = Boolean(
                    marker.phase === 'returned'
                    && moreInfoObservations.length > 0
                    && (marker.returnedAtMs ?? 0) > 0
                    && Date.now() - (marker.returnedAtMs ?? 0) > COMPLETED_MORE_INFO_RESPONSE_STALE_RETURN_GRACE_MS
                    && moreInfoObservations.some((observation) => (
                        (observation.observedAtMs || 0) > latestReturnedObservationAtMs
                    ))
                );
                if (hasResolvedStatusObservation) {
                    const updated = { ...source };
                    delete updated[applicationId];
                    clearStoredCompletedMoreInfoResponseMarker(applicationId);
                    next = updated;
                    return;
                }

                if (marker.phase === 'pending_return' && returnedObservations.length > 0) {
                    const returnedObservationAtMs = latestReturnedObservationAtMs || marker.completedAtMs;
                    if (hasFreshMoreInfoObservationAfterResponse) {
                        const updated = { ...source };
                        delete updated[applicationId];
                        clearStoredCompletedMoreInfoResponseMarker(applicationId);
                        next = updated;
                        return;
                    }
                    const returnedAtMs = Math.max(returnedObservationAtMs, Date.now());
                    const updatedMarker = {
                        ...marker,
                        phase: 'returned' as const,
                        returnedAtMs,
                    };
                    writeStoredCompletedMoreInfoResponseMarker(applicationId, updatedMarker);
                    next = {
                        ...source,
                        [applicationId]: updatedMarker,
                    };
                    return;
                }
                if (marker.phase !== 'returned') return;

                const returnedAtMs = Math.max(marker.returnedAtMs ?? 0, latestReturnedObservationAtMs);
                if ((marker.returnedAtMs ?? 0) < returnedAtMs) {
                    const updatedMarker = {
                        ...marker,
                        returnedAtMs,
                    };
                    writeStoredCompletedMoreInfoResponseMarker(applicationId, updatedMarker);
                    next = {
                        ...source,
                        [applicationId]: updatedMarker,
                    };
                }
                if (
                    hasFreshMoreInfoObservationAfterResponse
                    || hasNewKeyedMoreInfoRequestAfterKeylessResponse
                    || hasExpiredReturnedMoreInfoObservation
                ) {
                    const updated = { ...(next ?? source) };
                    delete updated[applicationId];
                    clearStoredCompletedMoreInfoResponseMarker(applicationId);
                    next = updated;
                }
            });
            return next ?? current;
        });
    }, [
        observedParentStatusApplicationId,
        observedParentStatusAtMs,
        observedParentStatusMoreInfoRequestKey,
        observedParentStatusToken,
        observedParentListApplicationAtMs,
        observedParentListApplicationId,
        observedParentListApplicationMoreInfoRequestKey,
        observedParentListStatusToken,
        observedStatusViewApplicationId,
        observedStatusViewApplicationAtMs,
        observedStatusViewApplicationMoreInfoRequestKey,
        observedStatusViewApplicationStatusToken,
        observedLoadedDraftApplicationAtMs,
        observedLoadedDraftApplicationId,
        observedLoadedDraftApplicationMoreInfoRequestKey,
        observedLoadedDraftStatusToken,
    ]);
    const parentMoreInfoDecisionRecord = asRecord(parentMoreInfoStatusRecord.decision);
    const parentMoreInfoDecisionRequest =
        readText(parentMoreInfoDecisionRecord.decisionType) === 'request_more_info'
            ? parentMoreInfoDecisionRecord
            : {};
    const parentMoreInfoStatusMatchesDraft =
        isParentStatusViewRequested
        && draftApplicationId
        && readText(parentMoreInfoStatusRecord.applicationId) === draftApplicationId;
    const statusViewMoreInfoApplicationRecord =
        draftApplicationId && readText(statusViewApplicationRecord?.id) === draftApplicationId
            ? asRecord(statusViewApplicationRecord)
            : {};
    const loadedDraftMoreInfoApplicationRecord =
        draftApplicationId && readText(loadedDraftApplicationRecord?.id) === draftApplicationId
            ? asRecord(loadedDraftApplicationRecord)
            : {};
    const activeApplicationSummaryMoreInfoStatusToken =
        draftApplicationId && activeApplicationSummary?.id === draftApplicationId
            ? activeApplicationSummary.statusToken
            : '';
    const activeDraftMoreInfoMarker = readCompletedMoreInfoResponseMarker(draftApplicationId);
    const activeDraftParentStatusMoreInfoRequestKey =
        parentMoreInfoStatusMatchesDraft && observedParentStatusToken === 'more_info_needed'
            ? observedParentStatusMoreInfoRequestKey
            : '';
    const activeDraftStatusViewMoreInfoRequestKey =
        normalizeParentFinancialAidStatusToken(statusViewMoreInfoApplicationRecord.status) === 'more_info_needed'
            ? readFinancialAidMoreInfoRequestKey(statusViewMoreInfoApplicationRecord)
            : '';
    const activeDraftLoadedDetailMoreInfoRequestKey =
        normalizeParentFinancialAidStatusToken(loadedDraftMoreInfoApplicationRecord.status) === 'more_info_needed'
            ? readFinancialAidMoreInfoRequestKey(loadedDraftMoreInfoApplicationRecord)
            : '';
    const activeApplicationSummaryMoreInfoRequestKey =
        draftApplicationId && activeApplicationSummary?.id === draftApplicationId
            ? readSummaryMoreInfoRequestKey(activeApplicationSummary)
            : '';
    const activeApplicationSummaryRequestedInfo =
        draftApplicationId && activeApplicationSummary?.id === draftApplicationId
            ? readText(activeApplicationSummary.requestedInfo)
            : '';
    const statusViewMoreInfoStatusCandidate = selectNewestFinancialAidStatusCandidate([
        {
            source: 'parent-status',
            statusToken: parentMoreInfoStatusMatchesDraft ? readText(parentMoreInfoStatusRecord.status) : '',
            observedAtMs: parentMoreInfoStatusMatchesDraft ? observedParentStatusAtMs : 0,
            allowTimestamplessOverride: !activeDraftMoreInfoMarker || isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftParentStatusMoreInfoRequestKey,
            ),
            preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftParentStatusMoreInfoRequestKey,
            ),
            hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(activeDraftParentStatusMoreInfoRequestKey),
        },
        {
            source: 'status-view-application',
            statusToken: readText(statusViewMoreInfoApplicationRecord.status),
            observedAtMs: readText(statusViewMoreInfoApplicationRecord.status) ? observedStatusViewApplicationAtMs : 0,
            allowTimestamplessOverride: !activeDraftMoreInfoMarker || isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftStatusViewMoreInfoRequestKey,
            ),
            preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftStatusViewMoreInfoRequestKey,
            ),
            hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(activeDraftStatusViewMoreInfoRequestKey),
        },
        {
            source: 'loaded-application-detail',
            statusToken: readText(loadedDraftMoreInfoApplicationRecord.status),
            observedAtMs: readFinancialAidStatusObservationTimestamp(loadedDraftMoreInfoApplicationRecord),
            allowTimestamplessOverride: !activeDraftMoreInfoMarker || isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftLoadedDetailMoreInfoRequestKey,
            ),
            preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeDraftLoadedDetailMoreInfoRequestKey,
            ),
            hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(activeDraftLoadedDetailMoreInfoRequestKey),
        },
        {
            source: 'application-summary',
            statusToken: activeApplicationSummaryMoreInfoStatusToken,
            observedAtMs: observedParentListApplicationId === draftApplicationId ? observedParentListApplicationAtMs : 0,
            allowTimestamplessOverride: !activeDraftMoreInfoMarker || isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeApplicationSummaryMoreInfoRequestKey,
            ),
            preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                activeDraftMoreInfoMarker,
                activeApplicationSummaryMoreInfoRequestKey,
            ),
            hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(activeApplicationSummaryMoreInfoRequestKey),
        },
    ]);
    const statusViewMoreInfoStatusToken = statusViewMoreInfoStatusCandidate?.statusToken || '';
    const statusViewMoreInfoStatusSource = statusViewMoreInfoStatusCandidate?.source ?? 'application-summary';
    const isLoadedDetailMoreInfoStatusSource = statusViewMoreInfoStatusSource === 'loaded-application-detail';
    const isDraftMoreInfoResponseLocallyCompleted = isMoreInfoResponseLocallyCompleted(draftApplicationId);
    const activeMoreInfoResponseRequestedInfo = statusViewMoreInfoStatusSource === 'parent-status'
        ? (
            readText(parentMoreInfoStatusRecord.requestedInfo)
            || readText(parentMoreInfoDecisionRequest.requestedInfo)
            || readText(statusViewMoreInfoApplicationRecord.requestedInfo)
            || readText(loadedDraftMoreInfoApplicationRecord.requestedInfo)
            || activeApplicationSummaryRequestedInfo
        )
        : statusViewMoreInfoStatusSource === 'application-summary'
            ? (
                activeApplicationSummaryRequestedInfo
                || readText(statusViewMoreInfoApplicationRecord.requestedInfo)
                || readText(loadedDraftMoreInfoApplicationRecord.requestedInfo)
                || readText(parentMoreInfoStatusRecord.requestedInfo)
                || readText(parentMoreInfoDecisionRequest.requestedInfo)
            )
        : isLoadedDetailMoreInfoStatusSource
            ? (
                readText(loadedDraftMoreInfoApplicationRecord.requestedInfo)
                || readText(statusViewMoreInfoApplicationRecord.requestedInfo)
                || readText(parentMoreInfoStatusRecord.requestedInfo)
                || readText(parentMoreInfoDecisionRequest.requestedInfo)
                || activeApplicationSummaryRequestedInfo
            )
        : (
            readText(statusViewMoreInfoApplicationRecord.requestedInfo)
            || readText(loadedDraftMoreInfoApplicationRecord.requestedInfo)
            || readText(parentMoreInfoStatusRecord.requestedInfo)
            || readText(parentMoreInfoDecisionRequest.requestedInfo)
            || activeApplicationSummaryRequestedInfo
        );
    const activeMoreInfoResponseRequestKey = statusViewMoreInfoStatusSource === 'parent-status'
        ? (
            readFinancialAidMoreInfoRequestKey(parentMoreInfoStatusRecord)
            || readFinancialAidMoreInfoRequestKey(statusViewMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestKey(loadedDraftMoreInfoApplicationRecord)
            || activeApplicationSummaryMoreInfoRequestKey
        )
        : statusViewMoreInfoStatusSource === 'application-summary'
            ? (
                activeApplicationSummaryMoreInfoRequestKey
                || readFinancialAidMoreInfoRequestKey(statusViewMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestKey(loadedDraftMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestKey(parentMoreInfoStatusRecord)
            )
        : isLoadedDetailMoreInfoStatusSource
            ? (
                readFinancialAidMoreInfoRequestKey(loadedDraftMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestKey(statusViewMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestKey(parentMoreInfoStatusRecord)
                || activeApplicationSummaryMoreInfoRequestKey
            )
        : (
            readFinancialAidMoreInfoRequestKey(statusViewMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestKey(loadedDraftMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestKey(parentMoreInfoStatusRecord)
            || activeApplicationSummaryMoreInfoRequestKey
        );
    const activeMoreInfoResponseRequestId = statusViewMoreInfoStatusSource === 'parent-status'
        ? (
            readFinancialAidMoreInfoRequestId(parentMoreInfoStatusRecord)
            || readFinancialAidMoreInfoRequestId(statusViewMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestId(loadedDraftMoreInfoApplicationRecord)
            || readText(activeApplicationSummary?.moreInfoRequestId)
        )
        : statusViewMoreInfoStatusSource === 'application-summary'
            ? (
                readText(activeApplicationSummary?.moreInfoRequestId)
                || readFinancialAidMoreInfoRequestId(statusViewMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestId(loadedDraftMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestId(parentMoreInfoStatusRecord)
            )
        : isLoadedDetailMoreInfoStatusSource
            ? (
                readFinancialAidMoreInfoRequestId(loadedDraftMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestId(statusViewMoreInfoApplicationRecord)
                || readFinancialAidMoreInfoRequestId(parentMoreInfoStatusRecord)
                || readText(activeApplicationSummary?.moreInfoRequestId)
            )
        : (
            readFinancialAidMoreInfoRequestId(statusViewMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestId(loadedDraftMoreInfoApplicationRecord)
            || readFinancialAidMoreInfoRequestId(parentMoreInfoStatusRecord)
            || readText(activeApplicationSummary?.moreInfoRequestId)
        );
    const activeDraftMoreInfoResponseMatchesRequest = isSameCompletedMoreInfoRequest(
        activeDraftMoreInfoMarker,
        activeMoreInfoResponseRequestKey,
    ) && isCompletedMoreInfoResponseCurrentForObservedRequests(
        activeDraftMoreInfoMarker,
        [activeMoreInfoResponseRequestKey],
    );
    const hasDifferentActiveDraftMoreInfoRequest = hasDifferentCompletedMoreInfoRequest(
        activeDraftMoreInfoMarker,
        [
            activeDraftParentStatusMoreInfoRequestKey,
            activeDraftStatusViewMoreInfoRequestKey,
            activeDraftLoadedDetailMoreInfoRequestKey,
            activeApplicationSummaryMoreInfoRequestKey,
        ],
    );
    const moreInfoResponseApplicationStatusToken = normalizeParentFinancialAidStatusToken(
        (
            hasDifferentActiveDraftMoreInfoRequest
                ? 'more_info_needed'
                : ''
        )
        || (
            isDraftMoreInfoResponseLocallyCompleted && activeDraftMoreInfoResponseMatchesRequest
                ? resolveCompletedMoreInfoResponseStatusToken(
                    statusViewMoreInfoStatusToken,
                    activeApplicationSummaryMoreInfoStatusToken,
                    readText(statusViewMoreInfoApplicationRecord.status),
                    readText(loadedDraftMoreInfoApplicationRecord.status),
                )
                : ''
        )
        || statusViewMoreInfoStatusToken
        || activeApplicationSummaryMoreInfoStatusToken
        || readText(statusViewMoreInfoApplicationRecord.status)
        || readText(loadedDraftMoreInfoApplicationRecord.status),
    );
    const isParentMoreInfoResponseMode = Boolean(
        draftApplicationId && moreInfoResponseApplicationStatusToken === 'more_info_needed',
    );
    const isStatusViewRouteResponseStep = Boolean(
        draftApplicationId
        && isStatusViewModeRequested
        && routeApplicationId === draftApplicationId
        && currentStep >= DOCUMENTS_STEP_INDEX
    );
    const shouldSuppressParentFinancialAidPointerForMoreInfoResponse = Boolean(
        draftApplicationId
        && isStatusViewModeRequested
        && routeApplicationId === draftApplicationId
        && (
            isStatusViewRouteResponseStep
            ||
            isParentMoreInfoResponseMode
            || (
                observedParentStatusApplicationId === draftApplicationId
                && observedParentStatusToken === 'more_info_needed'
            )
            || (
                observedStatusViewApplicationId === draftApplicationId
                && observedStatusViewApplicationStatusToken === 'more_info_needed'
            )
            || (
                observedParentListApplicationId === draftApplicationId
                && observedParentListStatusToken === 'more_info_needed'
            )
        )
    );
    const hasInviteEntryToken = Boolean(inviteEntryParams.inviteToken);
    const isInviteCycleRetryableError = Boolean(
        isRequestedInviteCycleStatusError
        || isInviteConfirmationUnavailableReport(inviteEntryError),
    );
    const isInviteClaimPending = Boolean(
        hasInviteEntryToken
        && !inviteEntryError
        && !inviteClaimedApplicationSummary
        && !draftApplicationId,
    );
    const isInviteCycleRetryPending = Boolean(
        isSettingsModuleConfigPending
        || isClaimInvitePending
        || isRecordInviteOpenPending,
    );
    const isInviteEntryBlocked = Boolean(hasInviteEntryToken && inviteEntryError && !activeApplicationSummary);
    const handleRetryInviteCycleStatus = useCallback(() => {
        if (isInviteCycleRetryPending) return;
        setInviteEntryError(null);
        setInviteEntryMessage('Checking your financial aid invitation...');
        setInviteCycleRetryAttempt((attempt) => attempt + 1);
        void settingsModuleConfigQuery.refetch();
    }, [isInviteCycleRetryPending, settingsModuleConfigQuery]);
    const validationIssuesByKey = useMemo(() => {
        const issueMap = new Map<string, ParentFinancialAidValidationIssue>();
        visibleValidationIssues.forEach((issue) => {
            if (!issueMap.has(issue.key)) issueMap.set(issue.key, issue);
        });
        return issueMap;
    }, [visibleValidationIssues]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const applicationId = readText(claimedInviteNavigationState.applicationId);
        if (!applicationId) return;

        const statusToken = readText(claimedInviteNavigationState.statusToken);
        const bannerKey = readText(claimedInviteNavigationState.hydrationBannerKey);
        const stateKey = `${applicationId}:${statusToken}:${bannerKey}`;
        if (claimedInviteNavigationStateKeyRef.current === stateKey) return;
        claimedInviteNavigationStateKeyRef.current = stateKey;

        const shouldShowExistingApplicationLanding = isParentFinancialAidExistingApplicationStateStatus(statusToken);
        const summaryRecord = asRecord(claimedInviteNavigationState.summary);
        const summaryId = readText(summaryRecord.id);
        if (summaryId === applicationId && shouldShowExistingApplicationLanding) {
            setInviteClaimedApplicationSummary({
                id: summaryId,
                code: readText(summaryRecord.code) || summaryId,
                academicYear: readText(summaryRecord.academicYear),
                statusToken: readText(summaryRecord.statusToken),
                statusLabel: readText(summaryRecord.statusLabel),
                submittedDateLabel: readText(summaryRecord.submittedDateLabel),
                updatedDateLabel: readText(summaryRecord.updatedDateLabel),
                nextStep: readText(summaryRecord.nextStep),
                canResume: summaryRecord.canResume === true,
            });
            setIsFormStarted(false);
        } else if (statusToken && !shouldShowExistingApplicationLanding) {
            setInviteClaimedApplicationSummary(null);
            setIsFormStarted(true);
        }

        setInviteEntryError(null);
        setDraftApplicationId(applicationId);

        const banner = readText(claimedInviteNavigationState.hydrationBannerMessage);
        if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
            setHydrationBannerMessage(banner);
            setHydrationBannerKey(bannerKey);
        }
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
            },
            { replace: true, state: null },
        );
    }, [claimedInviteNavigationState, location.hash, location.pathname, location.search, navigate]);

    useEffect(() => {
        const application = statusViewApplicationRecord;
        if (!routeApplicationId || !application || readText(application.id) !== routeApplicationId) return;
        if (
            !isStatusViewModeRequested
            && !doFinancialAidAcademicYearsMatch(
                statusViewApplicationSummary?.academicYear,
                effectiveFinancialAidAcademicYear,
            )
        ) return;

        const banner = resolveHydrationBannerMessage(application);
        const bannerKey = resolveHydrationBannerKey(application);
        if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
            setHydrationBannerMessage(banner);
            setHydrationBannerKey(bannerKey);
        }
    }, [
        effectiveFinancialAidAcademicYear,
        isStatusViewModeRequested,
        routeApplicationId,
        statusViewApplicationRecord,
        statusViewApplicationSummary?.academicYear,
    ]);

    useEffect(() => {
        if (!routeApplicationId || isStatusViewModeRequested) return;
        const routeDraftApplication = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        const routeDraftApplicationSummary =
            readText(routeDraftApplication?.id) === routeApplicationId
                ? summarizeParentFinancialAidApplication(routeDraftApplication)
                : null;
        const claimedRouteApplicationSummary =
            inviteClaimedApplicationSummary?.id === routeApplicationId
                ? inviteClaimedApplicationSummary
                : null;
        const canonicalizedClaimRouteApplicationSummary =
            claimedInviteCanonicalizedApplicationRef.current?.applicationId === routeApplicationId
                ? claimedInviteCanonicalizedApplicationRef.current.summary
                : null;
        const navigationClaimSummaryRecord = asRecord(claimedInviteNavigationState.summary);
        const navigationClaimRouteAcademicYear =
            readText(navigationClaimSummaryRecord.id) === routeApplicationId
                ? readText(navigationClaimSummaryRecord.academicYear)
                : undefined;
        const routeSummaryAcademicYear = readText(statusViewApplicationSummary?.academicYear)
            || readText(claimedRouteApplicationSummary?.academicYear)
            || readText(canonicalizedClaimRouteApplicationSummary?.academicYear)
            || readText(navigationClaimRouteAcademicYear)
            || readText(routeDraftApplicationSummary?.academicYear);
        const canCompareRouteApplicationYear = Boolean(
            effectiveFinancialAidAcademicYear && !isFinancialAidOpenCyclePending,
        );
        const isRouteApplicationForDifferentYear = Boolean(
            canCompareRouteApplicationYear
            && routeSummaryAcademicYear
            && !doFinancialAidAcademicYearsMatch(routeSummaryAcademicYear, effectiveFinancialAidAcademicYear),
        );
        if (!isRouteApplicationForDifferentYear) return;

        const params = new URLSearchParams(location.search);
        params.delete('applicationId');
        const search = params.toString();
        navigate(
            {
                pathname: location.pathname,
                search: search ? `?${search}` : '',
                hash: location.hash,
            },
            { replace: true, state: null },
        );
    }, [
        effectiveFinancialAidAcademicYear,
        claimedInviteNavigationState.summary,
        draftApplicationId,
        financialAidApplication.data,
        inviteClaimedApplicationSummary,
        isFinancialAidOpenCyclePending,
        isStatusViewModeRequested,
        location.hash,
        location.pathname,
        location.search,
        navigate,
        routeApplicationId,
        statusViewApplicationSummary?.academicYear,
    ]);

    useEffect(() => {
        const application = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        if (
            isStatusViewModeRequested
            || !routeApplicationId
            || !application
            || readText(application?.id) !== routeApplicationId
            || isFormStarted
        ) return;

        const summary = summarizeParentFinancialAidApplication(application);
        if (
            !summary
            || !doFinancialAidAcademicYearsMatch(summary.academicYear, effectiveFinancialAidAcademicYear)
        ) return;

        setInviteClaimedApplicationSummary(summary);
        const banner = resolveHydrationBannerMessage(application);
        const bannerKey = resolveHydrationBannerKey(application);
        if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
            setHydrationBannerMessage(banner);
            setHydrationBannerKey(bannerKey);
        }
    }, [
        effectiveFinancialAidAcademicYear,
        financialAidApplication.data,
        isFormStarted,
        isStatusViewModeRequested,
        routeApplicationId,
    ]);

    useEffect(() => {
        const inviteToken = inviteEntryParams.inviteToken;
        const pointer = readParentFinancialAidPointerWithFallback(
            getFinancialAidPointerStoragePair(),
            pointerStorageKey,
            [legacyPointerStorageKey],
        );
        if (inviteToken && claimedInviteTokenRef.current === inviteToken) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftHydrated(true);
            return;
        }
        const canonicalizedClaim = claimedInviteCanonicalizedApplicationRef.current;
        if (!inviteToken && canonicalizedClaim) {
            claimedInviteCanonicalizedApplicationRef.current = null;
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftApplicationId(canonicalizedClaim.applicationId);
            if (
                canonicalizedClaim.summary
                && isParentFinancialAidExistingApplicationStateStatus(canonicalizedClaim.statusToken)
            ) {
                setInviteClaimedApplicationSummary(canonicalizedClaim.summary);
                setIsFormStarted(false);
            } else if (
                canonicalizedClaim.statusToken
                && !isParentFinancialAidExistingApplicationStateStatus(canonicalizedClaim.statusToken)
            ) {
                setInviteClaimedApplicationSummary(null);
                setIsFormStarted(true);
            }
            if (
                canonicalizedClaim.hydrationBannerMessage
                && canonicalizedClaim.hydrationBannerKey
                && !dismissedHydrationBannerKeysRef.current.has(canonicalizedClaim.hydrationBannerKey)
            ) {
                setHydrationBannerMessage(canonicalizedClaim.hydrationBannerMessage);
                setHydrationBannerKey(canonicalizedClaim.hydrationBannerKey);
            }
            setDraftHydrated(true);
            return;
        }
        const routePointerApplicationId = readText(pointer?.applicationId);
        const activeDraftApplicationId = readText(draftApplicationId);
        if (
            !inviteToken
            && !routeApplicationId
            && activeDraftApplicationId
            && pendingResumeApplicationId === activeDraftApplicationId
        ) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftHydrated(true);
            return;
        }
        if (
            !inviteToken
            && !routeApplicationId
            && routePointerApplicationId
            && !activeDraftApplicationId
            && !currentApplicationSummary
            && !isParentFinancialAidLandingLookupPending
            && isFinancialAidAcademicYearConfigured
        ) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftApplicationId(routePointerApplicationId);
            setCurrentStep(clampParentFinancialAidStep(pointer?.currentStep ?? 0));
            setPendingResumeApplicationId(routePointerApplicationId);
            pendingResumeStartedAtRef.current = Date.now();
            setSaveDraftMessage('Loading saved financial aid draft...');
            setDraftHydrated(true);
            return;
        }
        if (!inviteToken && routeApplicationId && isFormStarted) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftHydrated(true);
            if (activeDraftApplicationId !== routeApplicationId) {
                const params = new URLSearchParams(location.search);
                if (activeDraftApplicationId) {
                    params.set('applicationId', activeDraftApplicationId);
                } else {
                    params.delete('applicationId');
                }
                const search = params.toString();
                navigate(
                    {
                        pathname: location.pathname,
                        search: search ? `?${search}` : '',
                        hash: location.hash,
                    },
                    { replace: true, state: null },
                );
            }
            return;
        }
        if (
            !inviteToken
            && routeApplicationId
            && !isStatusViewModeRequested
            && routePointerApplicationId === routeApplicationId
        ) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftApplicationId(routeApplicationId);
            setDraftHydrated(true);
            return;
        }
        if (
            !inviteToken
            && routeApplicationId
            && !isStatusViewModeRequested
            && draftApplicationId === routeApplicationId
        ) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftHydrated(true);
            return;
        }
        if (!inviteToken && !routeApplicationId && isFormStarted) {
            activePointerStorageKeyRef.current = pointerStorageKey;
            setDraftHydrated(true);
            return;
        }
        const emptyData = createEmptyFinancialAidData();

        setDraftHydrated(false);
        activePointerStorageKeyRef.current = pointerStorageKey;
        initialDataRef.current = cloneFinancialAidData(emptyData);
        setFormData(emptyData);
        setHydrationBannerMessage(null);
        setHydrationBannerKey(null);
        setInviteEntryError(null);
        setInviteEntryMessage(null);
        setInviteClaimedApplicationSummary(null);
        setSaveDraftMessage(null);
        setSaveDraftError(null);
        setSubmittedReference(null);
        setSubmittedApplicationId(null);
        setSubmittedCertification(null);
        claimedInviteTokenRef.current = null;
        claimedInviteCanonicalizedApplicationRef.current = null;
        claimedInviteNavigationStateKeyRef.current = null;
        inviteClaimRunIdRef.current += 1;
        hydratedApplicationIdRef.current = null;
        hydratedApplicationDocumentsRef.current = null;
        pendingResumeStartedAtRef.current = null;
        setPendingResumeApplicationId(null);
        setDraftApplicationId(null);
        setIsFormStarted(false);
        setCurrentStep(inviteToken ? 0 : clampParentFinancialAidStep(pointer?.currentStep ?? 0));
        setDraftHydrated(true);
    }, [
        draftApplicationId,
        inviteEntryParams.inviteToken,
        isFormStarted,
        currentApplicationSummary,
        isFinancialAidAcademicYearConfigured,
        isParentFinancialAidLandingLookupPending,
        isStatusViewModeRequested,
        legacyPointerStorageKey,
        location.hash,
        location.pathname,
        location.search,
        navigate,
        pendingResumeApplicationId,
        pointerStorageKey,
        routeApplicationId,
    ]);

    useEffect(() => {
        if (!draftHydrated || activePointerStorageKeyRef.current !== pointerStorageKey) return;
        if (!isFormStarted) return;
        if (shouldSuppressParentFinancialAidPointerForMoreInfoResponse) return;

        const storage = getFinancialAidPointerStorage();
        if (currentStep >= 9) {
            clearParentFinancialAidPointerStorageFrom(storage);
            return;
        }
        writeParentFinancialAidPointer(storage, pointerStorageKey, {
            applicationId: draftApplicationId,
            currentStep,
        });
    }, [currentStep, draftApplicationId, draftHydrated, inviteEntryParams.inviteToken, isFormStarted, pointerStorageKey, shouldSuppressParentFinancialAidPointerForMoreInfoResponse]);


    useEffect(() => {
        const token = inviteEntryParams.inviteToken;
        if (!token || !draftHydrated) return;

        const actorId = readText(profile?.userSub);
        if (!actorId) {
            setInviteEntryError(createFinancialAidActionErrorReport('Please sign in to claim this financial aid invitation.'));
            return;
        }
        if (shouldBlockInviteClaimForNonParentSession({
            effectivePersona,
            groups: profile?.groups,
        })) {
            setInviteEntryError(createNonParentInviteSessionErrorReport());
            setInviteEntryMessage(null);
            return;
        }
        if (isRequestedInviteCycleStatusError) {
            setInviteEntryError(createInviteConfirmationUnavailableReport());
            setInviteEntryMessage(null);
            return;
        }
        if (
            isRequestedInviteCycleClosed
            && !shouldDeferRequestedInviteCycleDecisionToBackend
        ) {
            const requestedInviteCycleLabel = financialAidAcademicYearInlineLabel(inviteEntryParams.academicYear);
            setInviteEntryError(createFinancialAidActionErrorReport(
                `This financial aid invitation is for ${requestedInviteCycleLabel}, which is not currently open. You can continue any current application below, or contact the school office if you need help.`,
            ));
            setInviteEntryMessage(null);
            if (activeApplicationSummary?.canResume) {
                navigate(
                    {
                        pathname: location.pathname,
                        search: '',
                    },
                    { replace: true },
                );
            }
            return;
        }
        if (isRequestedInviteCycleStatusPending) {
            setInviteEntryError(null);
            setInviteEntryMessage('Checking your financial aid invitation...');
            return;
        }
        if (claimedInviteTokenRef.current === token) return;

        claimedInviteTokenRef.current = token;
        const runId = inviteClaimRunIdRef.current + 1;
        inviteClaimRunIdRef.current = runId;
        const isCurrentInviteRun = () =>
            isMountedRef.current
            && claimedInviteTokenRef.current === token
            && inviteClaimRunIdRef.current === runId;
        setInviteEntryError(null);
        setInviteEntryMessage('Linking your financial aid invitation...');

        void (async () => {
            try {
                const claimed = await claimInviteAsync({ token }) as FinancialAidApplicationRecord;
                await recordInviteOpenAsync({ token }).catch((openError) => {
                    console.warn('[financial-aid] Unable to record invite-open activity after claim.', openError);
                });
                if (!isCurrentInviteRun()) return;

                const applicationId = readText(claimed.id);
                if (!applicationId) {
                    throw new Error('Financial aid invitation was claimed without an application id.');
                }

                setDraftApplicationId(applicationId);
                writeParentFinancialAidPointer(getFinancialAidPointerStorage(), pointerStorageKey, {
                    applicationId,
                    currentStep,
                });
                const claimedAcademicYear = readText(claimed.academicYear);
                const requestedAcademicYear = readText(inviteEntryParams.academicYear);
                const banner = resolveHydrationBannerMessage(claimed);
                const bannerKey = resolveHydrationBannerKey(claimed);
                const status = readText(claimed.status).toLowerCase().replace(/[\s-]+/g, '_');
                const claimedSummary = summarizeParentFinancialAidApplication(claimed);
                const canonicalRoute = buildClaimedInviteParentFinancialAidRoute(
                    location.pathname,
                    location.search,
                    claimedAcademicYear || requestedAcademicYear,
                    applicationId,
                );
                if (location.pathname !== canonicalRoute.pathname || location.search !== canonicalRoute.search) {
                    claimedInviteCanonicalizedApplicationRef.current = {
                        applicationId,
                        statusToken: status,
                        summary: claimedSummary,
                        hydrationBannerMessage: banner,
                        hydrationBannerKey: bannerKey,
                    };
                    navigate(canonicalRoute, {
                        replace: true,
                        state: {
                            parentFinancialAidClaimedInvite: {
                                applicationId,
                                statusToken: status,
                                summary: claimedSummary,
                                hydrationBannerMessage: banner,
                                hydrationBannerKey: bannerKey,
                            },
                        },
                    });
                }

                if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
                    setHydrationBannerMessage(banner);
                    setHydrationBannerKey(bannerKey);
                }

                if (claimedSummary && isParentFinancialAidExistingApplicationStateStatus(status)) {
                    setInviteClaimedApplicationSummary(claimedSummary);
                    setIsFormStarted(false);
                    setCurrentStep(0);
                    setInviteEntryMessage(
                        isParentFinancialAidApplicationResumable(status)
                            ? 'Invitation linked. Review your application status below to continue.'
                            : 'This financial aid application is already linked to your parent account.',
                    );
                    return;
                }

                setIsFormStarted(true);
                setInviteEntryMessage('Invitation linked. You can continue the application here.');

                if (['submitted', 'under_review', 'more_info_needed', 'approved', 'denied', 'accepted', 'declined', 'award_accepted', 'award_declined'].includes(status)) {
                    submissionCompletedRef.current = true;
                    setSubmittedApplicationId(applicationId);
                    setSubmittedReference(readText(claimed.code) || applicationId);
                    setSubmittedCertification(readParentFinancialAidCopyCertificationContext(claimed.sections));
                    setCompletionStatus(['approved', 'denied', 'accepted', 'declined', 'award_accepted', 'award_declined'].includes(status)
                        ? 'decision'
                        : 'submitted');
                    setCurrentStep(9);
                }
            } catch (error) {
                if (!isCurrentInviteRun()) return;
                claimedInviteTokenRef.current = null;
                claimedInviteCanonicalizedApplicationRef.current = null;
                setDraftApplicationId(null);
                setInviteClaimedApplicationSummary(null);
                setIsFormStarted(false);
                setCurrentStep(0);
                setInviteEntryError(readInviteClaimErrorReport(error));
                setInviteEntryMessage(null);
            }
        })();
    }, [
        activeApplicationSummary,
        claimInviteAsync,
        currentStep,
        draftHydrated,
        inviteCycleRetryAttempt,
        inviteEntryParams.academicYear,
        inviteEntryParams.inviteToken,
        isRequestedInviteCycleClosed,
        shouldDeferRequestedInviteCycleDecisionToBackend,
        isRequestedInviteCycleStatusError,
        isRequestedInviteCycleStatusPending,
        location.pathname,
        location.search,
        navigate,
        pointerStorageKey,
        effectivePersona,
        profile?.groups,
        profile?.userSub,
        recordInviteOpenAsync,
    ]);

    useEffect(() => {
        const application = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        const applicationId = readText(application?.id);
        const hydrationKey = resolveParentFinancialAidApplicationHydrationKey(application);
        if (!application || !applicationId || hydratedApplicationIdRef.current === hydrationKey) return;

        const hasInviteMetadata = Boolean(readText(asRecord(application?.invite).token));
        const hasHydrationMetadata = Boolean(readText(asRecord(application?.hydration).hydratedAt) || resolveHydrationBannerMessage(application));
        const hasSavedParentSections = hasHydratableParentFinancialAidSections(application);
        if (!hasInviteMetadata && !hasHydrationMetadata && !(isFormStarted && hasSavedParentSections)) return;

        const oldInitialData = initialDataRef.current;
        const previousData = formData;
        const hydratedData = buildParentFinancialAidDataFromApplication(application, profile, activeConfiguredRequiredDocuments);
        hydratedData.documents = preserveConfirmedParentFinancialAidDocumentUploads(previousData.documents, hydratedData.documents);
        hydratedData.signatures.attestations = resolveHydratedParentFinancialAidAttestations(
            hydratedData.signatures.attestations,
            previousData.signatures.attestations,
            readParentFinancialAidStoredAttestations(getFinancialAidPointerStorage(), applicationId),
        );
        const banner = resolveHydrationBannerMessage(application);
        const bannerKey = resolveHydrationBannerKey(application);
        const shouldPreserveUnsavedLocalData =
            currentStep > 0 && hasHydrationBlockingUnsavedParentFinancialAidData(previousData, oldInitialData);
        const shouldPreserveMoreCompleteLocalData =
            isFormStarted && shouldPreserveMoreCompleteLocalFinancialAidData(previousData, oldInitialData, hydratedData);

        hydratedApplicationIdRef.current = hydrationKey;
        if (shouldPreserveUnsavedLocalData || shouldPreserveMoreCompleteLocalData) {
            return;
        }
        if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
            setHydrationBannerMessage(banner);
            setHydrationBannerKey(bannerKey);
        }

        // Store a clone: field updates mutate nested form objects in place,
        // so a shared reference would silently rewrite the unsaved-changes
        // baseline and break the unload warning (INT-568).
        initialDataRef.current = cloneFinancialAidData(hydratedData);
        setFormData(hydratedData);
    }, [activeConfiguredRequiredDocumentHydrationKey, activeConfiguredRequiredDocuments, currentStep, financialAidApplication.data, formData, isFormStarted, profile]);

    useEffect(() => {
        const application = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        const applicationId = readText(application?.id);
        const applicationHydrationKey = resolveParentFinancialAidApplicationHydrationKey(application);
        const hydrationKey = applicationId
            ? `${applicationHydrationKey}:documents:${activeConfiguredRequiredDocumentHydrationKey}`
            : '';
        if (!application || !applicationId || hydratedApplicationDocumentsRef.current === hydrationKey) return;

        const hasInviteMetadata = Boolean(readText(asRecord(application?.invite).token));
        const hasHydrationMetadata = Boolean(readText(asRecord(application?.hydration).hydratedAt) || resolveHydrationBannerMessage(application));
        const hasSavedParentSections = hasHydratableParentFinancialAidSections(application);
        if (!hasInviteMetadata && !hasHydrationMetadata && !(isFormStarted && hasSavedParentSections)) return;

        const restoredDocuments = restoreParentFinancialAidDocumentsFromApplication(application, activeConfiguredRequiredDocuments);
        hydratedApplicationDocumentsRef.current = hydrationKey;
        setFormData((previousData) => {
            const documents = preserveConfirmedParentFinancialAidDocumentUploads(previousData.documents, restoredDocuments);
            if (areParentFinancialAidDocumentsEqual(previousData.documents, documents)) return previousData;
            initialDataRef.current = {
                ...initialDataRef.current,
                documents: preserveConfirmedParentFinancialAidDocumentUploads(initialDataRef.current.documents, documents),
            };
            return { ...previousData, documents };
        });
    }, [activeConfiguredRequiredDocumentHydrationKey, activeConfiguredRequiredDocuments, financialAidApplication.data, isFormStarted]);

    const dismissHydrationBanner = () => {
        if (hydrationBannerKey) {
            dismissedHydrationBannerKeysRef.current.add(hydrationBannerKey);
        }
        setHydrationBannerMessage(null);
        setHydrationBannerKey(null);
    };

    const scrollToTop = () => document.getElementById('root')?.parentElement?.scrollTo({ top: 0, behavior: 'smooth' });
    const requestCurrentStepValidation = () => {
        setValidationRequested(true);
        scrollToTop();
    };
    const nextStep = () => {
        if (currentStep >= 9) return;
        if (!isFinancialAidAcademicYearConfigured) {
            setValidationRequested(false);
            setSubmissionError(createFinancialAidActionErrorReport('Financial aid is not configured for a school year yet. Please contact the school office.'));
            scrollToTop();
            return;
        }
        if (currentStep === 7 && isFinancialAidDocumentSettingsPending) {
            setUploadError(createFinancialAidActionErrorReport('We are still loading the school document requirements. Please wait a moment and try again.'));
            scrollToTop();
            return;
        }
        if (currentStepValidationIssues.length > 0) {
            requestCurrentStepValidation();
            return;
        }
        setValidationRequested(false);
        setCurrentStep(p => p + 1);
        scrollToTop();
    };
    const goToProgressStep = (step: number) => {
        if (step <= currentStep) {
            setValidationRequested(false);
            setCurrentStep(step);
            scrollToTop();
            return;
        }
        nextStep();
    };
    const prevStep = () => { if (currentStep > 0) { setValidationRequested(false); setCurrentStep(p => p - 1); scrollToTop(); } };
    const isSubmitting =
        createApplication.isPending
        || updateApplication.isPending
        || isRecordInviteOpenPending
        || isClaimInvitePending
        || uploadDocument.isPending
        || confirmDocumentUpload.isPending
        || respondToInfoRequest.isPending
        || submitApplication.isPending
        || Boolean(uploadingDocId);
    const shouldWarnBrowserBack = currentStep < 9
        && !submittedReference
        && draftHydrated
        && isFormStarted
        && !isSubmitting
        && hasUnsavedFormData(formData, initialDataRef.current);
    const browserBackGuard = useUnsavedBrowserNavigationGuard({ enabled: shouldWarnBrowserBack });

    const getInputClass = (val: any, initial: any) =>
        val !== initial ? "bg-amber-50 border-amber-200" : (initial ? "bg-blue-50/50 border-blue-100" : "");

    const getValidationIssue = (key: string) => validationIssuesByKey.get(key);
    const getValidationErrorId = (key: string) => `parent-financial-aid-field-error-${key.replace(/[^a-z0-9]+/gi, '-')}`;
    const getValidatedInputClass = (key: string, className = '') => {
        const issue = getValidationIssue(key);
        return `${className} ${issue ? '!border-strawberry !bg-strawberry/5 focus:!border-strawberry focus:!ring-strawberry' : ''}`.trim();
    };
    const getValidationProps = (key: string) => (
        getValidationIssue(key)
            ? { 'aria-invalid': true, 'aria-describedby': getValidationErrorId(key) }
            : {}
    );
    const renderFieldError = (key: string) => {
        const issue = getValidationIssue(key);
        if (!issue) return null;
        return (
            <p id={getValidationErrorId(key)} className="mt-1.5 text-xs font-semibold text-strawberry" data-testid={`parent-financial-aid-field-error-${key}`}>
                {issue.message}
            </p>
        );
    };
    const householdGrossIncomeDescriptionId = 'parent-financial-aid-household-gross-income-requirement';
    const getIncomeGrossValidationProps = (incomeKey: 'incomeFather' | 'incomeMother') => {
        const grossKey = `${incomeKey}.gross2024`;
        const validationProps = getValidationProps(grossKey);
        const describedBy = [
            householdGrossIncomeDescriptionId,
            getValidationIssue('income.householdGross2024') ? getValidationErrorId('income.householdGross2024') : null,
            validationProps['aria-describedby'],
        ].filter(Boolean).join(' ');
        return {
            ...validationProps,
            'aria-describedby': describedBy,
        };
    };
    const getArrayFieldValidationKey = (arrayKey: string, index: number, fieldKey: string) => `${arrayKey}.${index}.${fieldKey}`;
    const resolveIncomeGrossDisplayValidationKey = (incomeKey: 'incomeFather' | 'incomeMother') => {
        const grossKey = `${incomeKey}.gross2024`;
        if (getValidationIssue(grossKey)) return grossKey;
        if (getValidationIssue('income.householdGross2024')) {
            return 'income.householdGross2024';
        }
        return grossKey;
    };

    // --- Data Helpers ---
    const update = (path: string[], value: any) => {
        setSubmissionError(null);
        setSaveDraftMessage(null);
        setSaveDraftError(null);
        setFormData(prev => {
            const next = { ...prev };
            let curr: any = next;
            for (let i = 0; i < path.length - 1; i++) curr = curr[path[i]];
            curr[path[path.length - 1]] = value;
            if (path[0] === 'signatures' && (path[1] === 'father' || path[1] === 'mother')) {
                curr[`${path[1]}Captured`] = Boolean(value);
            }
            return path[0] === 'signatures' ? next : invalidateFinancialAidSignatures(next);
        });
    };

    const updateHouseholdComposition = (partial: Partial<ParentGuardianHouseholdComposition>) => {
        const current = resolveParentGuardianHouseholdComposition(formData);
        const next = normalizeParentGuardianHouseholdComposition({ ...current, ...partial });
        setSubmissionError(null);
        setSaveDraftMessage(null);
        setSaveDraftError(null);
        setFormData((previous) => invalidateFinancialAidSignatures({
            ...previous,
            householdComposition: next,
        }));
    };

    const addRow = (section: 'vehicles' | 'investments' | 'childrenAssets' | 'otherChildren' | 'camps2025' | 'camps2024') => {
        const id = crypto.randomUUID();
        const templates: any = {
            vehicles: { id, year: '', make: '', model: '', value: '', debt: '', payment: '' },
            investments: { id, description: '', value: '' },
            childrenAssets: { id, description: '', value: '' },
            otherChildren: { id, name: '', age: '', grade: '', school: '', fullTuition: '', scholarship: '' },
            camps2025: { id, childName: '', campName: '', fee: '' },
            camps2024: { id, childName: '', campName: '', fee: '' }
        };
        setFormData(p => invalidateFinancialAidSignatures({ ...p, [section]: [...p[section], templates[section]] }));
    };

    const removeRow = (section: 'vehicles' | 'investments' | 'childrenAssets' | 'otherChildren' | 'camps2025' | 'camps2024', id: string) => {
        setFormData(p => {
            const rows = (p[section] as any[]).filter(x => x.id !== id);
            // Camp sections always keep a visible starter row; deleting the
            // last row clears it instead.
            const nextRows = (section === 'camps2025' || section === 'camps2024')
                ? ensureFirstCampRow(rows as Camp[])
                : rows;
            return invalidateFinancialAidSignatures({ ...p, [section]: nextRows });
        });
    };

    const updateRow = (section: string, id: string, field: string, val: string) => {
        setFormData(p => invalidateFinancialAidSignatures({
            ...p,
            [section]: (p as any)[section].map((item: any) => item.id === id ? { ...item, [field]: val } : item)
        }));
    };

    useEffect(() => {
        setFormData(prev => {
            const documents = reconcileParentFinancialAidDocuments(prev.documents, activeConfiguredRequiredDocuments);
            if (areParentFinancialAidDocumentsEqual(prev.documents, documents)) return prev;
            initialDataRef.current = {
                ...initialDataRef.current,
                documents: reconcileParentFinancialAidDocuments(initialDataRef.current.documents, activeConfiguredRequiredDocuments),
            };
            return { ...prev, documents };
        });
    }, [activeConfiguredRequiredDocuments]);

    // Drafts saved before the camp starter-row behavior may hold empty camp
    // arrays; restore the starter rows without touching signatures. Keep
    // initialDataRef in step with the reconciled rows (mirroring the documents
    // reconcile above) so a clean hydration does not read as unsaved changes
    // and arm the browser unload warning (INT-568).
    useEffect(() => {
        setFormData(prev => {
            if (prev.camps2025.length > 0 && prev.camps2024.length > 0) return prev;
            initialDataRef.current = {
                ...initialDataRef.current,
                camps2025: ensureFirstCampRow(initialDataRef.current.camps2025),
                camps2024: ensureFirstCampRow(initialDataRef.current.camps2024),
            };
            return {
                ...prev,
                camps2025: ensureFirstCampRow(prev.camps2025),
                camps2024: ensureFirstCampRow(prev.camps2024),
            };
        });
    }, [formData.camps2025.length, formData.camps2024.length]);

    useEffect(() => {
        const status = documentStatus.data as unknown as FinancialAidDocumentStatusResult | undefined;
        if (!Array.isArray(status?.items)) return;
        const statusByDocumentType = new Map(
            status.items
                .map((item) => [normalizeParentFinancialAidDocumentId(item.documentType), item] as const)
                .filter(([documentType]) => Boolean(documentType)),
        );
        const applyDocumentStatus = (
            documents: ParentFinancialAidDocumentStatus[],
        ): ParentFinancialAidDocumentStatus[] => documents.map((doc) => {
            const item = statusByDocumentType.get(normalizeParentFinancialAidDocumentId(doc.id));
            if (!item) return doc;
            return {
                ...doc,
                uploaded: Boolean(item.isUploaded),
                fileName: item.isUploaded ? readText(item.fileName) || doc.fileName : undefined,
            };
        });
        // Backend document status is server truth, not unsaved parent input.
        // Skip no-op updates and keep initialDataRef in step (mirroring the
        // documents reconcile above) so a clean draft re-entry does not read
        // as unsaved changes and arm the browser unload warning (INT-568).
        setFormData(prev => {
            const documents = applyDocumentStatus(prev.documents);
            if (areParentFinancialAidDocumentsEqual(prev.documents, documents)) return prev;
            initialDataRef.current = {
                ...initialDataRef.current,
                documents: applyDocumentStatus(initialDataRef.current.documents),
            };
            return { ...prev, documents };
        });
    }, [documentStatus.data]);

    const ensureBackendDraftApplication = async ({
        persistSections = true,
        suppressHiddenFields = true,
        certification = null,
    }: {
        persistSections?: boolean;
        suppressHiddenFields?: boolean;
        certification?: ParentFinancialAidCopyCertificationContext | null;
    } = {}): Promise<string> => {
        restoredExistingDraftDuringCreateRef.current = false;
        const applicantId = parentApplicantId;
        if (!applicantId) {
            throw new Error('Unable to determine your parent identity. Please sign in again.');
        }
        if (!configuredFinancialAidAcademicYear) {
            throw new Error('Financial aid is not configured for a school year yet. Please contact the school office.');
        }
        if (isGenericInviteCycleClosed) {
            throw new Error('The financial aid cycle is currently closed for this academic year.');
        }
        const persistableFormData = suppressHiddenFields
            ? clearParentFinancialAidHiddenRequirementValues(formData, financialAidRequirementPolicy)
            : formData;
        // Hidden fields are suppressed from structured/output sections and the raw snapshot
        // so fields currently configured as hidden are not retained by the backend.
        const rawSnapshotData = persistableFormData;
        if (draftApplicationId) {
            if (persistSections) {
                await updateApplication.mutateAsync({
                    id: draftApplicationId,
                    sections: buildParentFinancialAidSections(
                        persistableFormData,
                        profile,
                        readExistingDraftFinancialAidSections(),
                        certification,
                        rawSnapshotData,
                    ),
                });
                applyPersistedSsnClearRequests();
            }
            return draftApplicationId;
        }

        const created = await createApplication.mutateAsync({
            applicantId,
            academicYear: configuredFinancialAidAcademicYear,
            sections: persistSections
                ? buildParentFinancialAidSections(persistableFormData, profile, {}, certification, rawSnapshotData)
                : {},
        });
        const createdRecord = asRecord(created);
        const applicationId = readText(createdRecord.id);
        if (!applicationId) {
            throw new Error('Financial aid application was created without an id.');
        }
        if (!isParentFinancialAidApplicationResumable(createdRecord.status)) {
            throw new Error('A financial aid application for this school year already exists. Please review its status before starting another one.');
        }
        setDraftApplicationId(applicationId);
        setIsFormStarted(true);
        writeParentFinancialAidPointer(getFinancialAidPointerStorage(), pointerStorageKey, {
            applicationId,
            currentStep,
        });
        const createdApplication = createdRecord as FinancialAidApplicationRecord;
        if (
            persistSections
            && canHydrateParentFinancialAidFormFromApplication(createdApplication)
            && !isParentFinancialAidFormAlreadyHydratedFromApplication(createdApplication)
        ) {
            const hydratedExistingDraft = hydrateParentFinancialAidFormFromApplication(createdApplication);
            if (hydratedExistingDraft) {
                restoredExistingDraftDuringCreateRef.current = true;
                return applicationId;
            }
        }
        if (persistSections) {
            await updateApplication.mutateAsync({
                id: applicationId,
                sections: buildParentFinancialAidSections(
                    persistableFormData,
                    profile,
                    readExistingDraftFinancialAidSections(),
                    certification,
                    rawSnapshotData,
                ),
            });
            applyPersistedSsnClearRequests();
        }
        return applicationId;
    };

    const canHydrateParentFinancialAidFormFromApplication = (
        application: FinancialAidApplicationRecord | null | undefined,
    ): application is FinancialAidApplicationRecord => {
        if (!application || !readText(application.id)) return false;
        return (
            hasHydratableParentFinancialAidSections(application)
            || Boolean(readText(asRecord(application?.invite).token))
            || Boolean(readText(asRecord(application?.hydration).hydratedAt) || resolveHydrationBannerMessage(application))
        );
    };

    const isParentFinancialAidFormAlreadyHydratedFromApplication = (
        application: FinancialAidApplicationRecord | null | undefined,
    ): boolean => {
        const hydrationKey = resolveParentFinancialAidApplicationHydrationKey(application);
        return Boolean(hydrationKey && hydratedApplicationIdRef.current === hydrationKey);
    };

    const hydrateParentFinancialAidFormFromApplication = (
        application: FinancialAidApplicationRecord | null | undefined,
    ): boolean => {
        const applicationId = readText(application?.id);
        const hydrationKey = resolveParentFinancialAidApplicationHydrationKey(application);
        if (!application || !applicationId || !hydrationKey || hydratedApplicationIdRef.current === hydrationKey) {
            return false;
        }

        if (!canHydrateParentFinancialAidFormFromApplication(application)) {
            return false;
        }

        const previousData = formData;
        const oldInitialData = initialDataRef.current;
        const hydratedData = buildParentFinancialAidDataFromApplication(application, profile, activeConfiguredRequiredDocuments);
        hydratedData.documents = preserveConfirmedParentFinancialAidDocumentUploads(previousData.documents, hydratedData.documents);
        hydratedData.signatures.attestations = resolveHydratedParentFinancialAidAttestations(
            hydratedData.signatures.attestations,
            previousData.signatures.attestations,
            readParentFinancialAidStoredAttestations(getFinancialAidPointerStorage(), applicationId),
        );
        const shouldPreserveUnsavedLocalData =
            currentStep > 0 && hasHydrationBlockingUnsavedParentFinancialAidData(previousData, oldInitialData);
        const shouldPreserveMoreCompleteLocalData =
            isFormStarted && shouldPreserveMoreCompleteLocalFinancialAidData(previousData, oldInitialData, hydratedData);

        if (shouldPreserveUnsavedLocalData || shouldPreserveMoreCompleteLocalData) {
            return false;
        }

        const banner = resolveHydrationBannerMessage(application);
        const bannerKey = resolveHydrationBannerKey(application);
        if (banner && bannerKey && !dismissedHydrationBannerKeysRef.current.has(bannerKey)) {
            setHydrationBannerMessage(banner);
            setHydrationBannerKey(bannerKey);
        }

        hydratedApplicationIdRef.current = hydrationKey;
        // Store a clone: field updates mutate nested form objects in place,
        // so a shared reference would silently rewrite the unsaved-changes
        // baseline and break the unload warning (INT-568).
        initialDataRef.current = cloneFinancialAidData(hydratedData);
        hydratedApplicationDocumentsRef.current = `${hydrationKey}:documents:${activeConfiguredRequiredDocumentHydrationKey}`;
        setFormData(hydratedData);
        return true;
    };

    useEffect(() => {
        if (
            !isStatusViewModeRequested
            || !isFormStarted
            || !draftApplicationId
            || draftApplicationId !== routeApplicationId
        ) return;
        if (readText(statusViewApplicationRecord?.id) !== draftApplicationId) return;
        hydrateParentFinancialAidFormFromApplication(statusViewApplicationRecord);
    }, [
        activeConfiguredRequiredDocumentHydrationKey,
        activeConfiguredRequiredDocuments,
        currentStep,
        draftApplicationId,
        formData,
        isFormStarted,
        isStatusViewModeRequested,
        profile,
        routeApplicationId,
        statusViewApplicationRecord,
    ]);

    useEffect(() => {
        if (!pendingResumeApplicationId || draftApplicationId !== pendingResumeApplicationId) return;
        const application = financialAidApplication.data as FinancialAidApplicationRecord | undefined;
        const failPendingResume = (message: string) => {
            pendingResumeStartedAtRef.current = null;
            setPendingResumeApplicationId(null);
            setSaveDraftMessage(null);
            setSaveDraftError(createFinancialAidActionErrorReport(message));
        };
        if (readText(application?.id) !== pendingResumeApplicationId) {
            if (
                Boolean((financialAidApplication as { error?: unknown }).error)
                && !Boolean((financialAidApplication as { isLoading?: unknown; isFetching?: unknown }).isLoading)
                && !Boolean((financialAidApplication as { isLoading?: unknown; isFetching?: unknown }).isFetching)
            ) {
                failPendingResume('Unable to load this saved financial aid draft right now. Please try again.');
            }
            return;
        }
        const hydrated = isParentFinancialAidFormAlreadyHydratedFromApplication(application)
            || hydrateParentFinancialAidFormFromApplication(application);
        if (!hydrated && canHydrateParentFinancialAidFormFromApplication(application)) {
            failPendingResume('Unable to restore the saved financial aid draft right now. Please try again.');
            return;
        }
        pendingResumeStartedAtRef.current = null;
        setPendingResumeApplicationId(null);
        setSaveDraftMessage(null);
        setIsFormStarted(true);
    }, [
        activeConfiguredRequiredDocumentHydrationKey,
        activeConfiguredRequiredDocuments,
        currentStep,
        draftApplicationId,
        financialAidApplication,
        financialAidApplication.data,
        formData,
        pendingResumeApplicationId,
        profile,
    ]);

    useEffect(() => {
        if (!pendingResumeApplicationId) return undefined;
        const timeout = window.setTimeout(() => {
            setPendingResumeApplicationId((current) => {
                if (current !== pendingResumeApplicationId) return current;
                pendingResumeStartedAtRef.current = null;
                setSaveDraftMessage(null);
                setSaveDraftError(createFinancialAidActionErrorReport('The saved financial aid draft is taking too long to load. Please try again.'));
                return null;
            });
        }, 20000);
        return () => window.clearTimeout(timeout);
    }, [pendingResumeApplicationId]);

    const beginParentFinancialAidForm = (
        summary?: ParentFinancialAidApplicationSummary | null,
        options: { preferredStep?: number; allowMoreInfoResponse?: boolean } = {},
    ) => {
        setSubmissionError(null);
        setSaveDraftError(null);
        setSaveDraftMessage(null);
        setInviteClaimedApplicationSummary(null);

        if (summary) {
            const summaryMatchesResponseYear =
                doFinancialAidAcademicYearsMatch(summary.academicYear, effectiveFinancialAidAcademicYear)
                || isParentFinancialAidAcademicYearAfter(summary.academicYear, effectiveFinancialAidAcademicYear);
            const isSelectedStatusViewApplication =
                isStatusViewModeRequested
                && routeApplicationId === summary.id;
            const selectedParentStatusRecord = asRecord(parentStatusView.data);
            const selectedStatusViewParentStatus =
                readText(selectedParentStatusRecord.applicationId) === summary.id
                    ? readText(selectedParentStatusRecord.status)
                    : '';
            const selectedStatusViewApplicationStatus =
                readText(statusViewApplicationRecord?.id) === summary.id
                    ? readText(statusViewApplicationRecord?.status)
                    : '';
            const selectedStatusViewApplicationRecord =
                readText(statusViewApplicationRecord?.id) === summary.id
                    ? asRecord(statusViewApplicationRecord)
                    : {};
            const selectedLoadedApplicationStatus =
                readText(loadedDraftApplicationRecord?.id) === summary.id
                    ? readText(loadedDraftApplicationRecord?.status)
                    : '';
            const selectedLoadedApplicationRecord =
                readText(loadedDraftApplicationRecord?.id) === summary.id
                    ? asRecord(loadedDraftApplicationRecord)
                    : {};
            const selectedMoreInfoResponseLocallyCompleted =
                isMoreInfoResponseLocallyCompleted(summary.id);
            const selectedMoreInfoResponseMarker = readCompletedMoreInfoResponseMarker(summary.id);
            const selectedParentStatusMoreInfoRequestKey =
                normalizeParentFinancialAidStatusToken(selectedStatusViewParentStatus) === 'more_info_needed'
                    ? readFinancialAidMoreInfoRequestKey(selectedParentStatusRecord)
                    : '';
            const selectedStatusViewApplicationMoreInfoRequestKey =
                normalizeParentFinancialAidStatusToken(selectedStatusViewApplicationStatus) === 'more_info_needed'
                    ? readFinancialAidMoreInfoRequestKey(selectedStatusViewApplicationRecord)
                    : '';
            const selectedLoadedApplicationMoreInfoRequestKey =
                normalizeParentFinancialAidStatusToken(selectedLoadedApplicationStatus) === 'more_info_needed'
                    ? readFinancialAidMoreInfoRequestKey(selectedLoadedApplicationRecord)
                    : '';
            const selectedSummaryMoreInfoRequestKey = readSummaryMoreInfoRequestKey(summary);
            const selectedStatusCandidate = selectNewestFinancialAidStatusCandidate([
                {
                    source: 'parent-status',
                    statusToken: selectedStatusViewParentStatus,
                    observedAtMs: selectedStatusViewParentStatus
                        ? readFinancialAidStatusObservationTimestamp(selectedParentStatusRecord)
                        : 0,
                    allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedParentStatusMoreInfoRequestKey,
                    ),
                    preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedParentStatusMoreInfoRequestKey,
                    ),
                    hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedParentStatusMoreInfoRequestKey),
                },
                {
                    source: 'status-view-application',
                    statusToken: selectedStatusViewApplicationStatus,
                    observedAtMs: selectedStatusViewApplicationStatus
                        ? readFinancialAidStatusObservationTimestamp(selectedStatusViewApplicationRecord)
                        : 0,
                    allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedStatusViewApplicationMoreInfoRequestKey,
                    ),
                    preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedStatusViewApplicationMoreInfoRequestKey,
                    ),
                    hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedStatusViewApplicationMoreInfoRequestKey),
                },
                {
                    source: 'loaded-application-detail',
                    statusToken: selectedLoadedApplicationStatus,
                    observedAtMs: selectedLoadedApplicationStatus
                        ? readFinancialAidStatusObservationTimestamp(selectedLoadedApplicationRecord)
                        : 0,
                    allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedLoadedApplicationMoreInfoRequestKey,
                    ),
                    preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedLoadedApplicationMoreInfoRequestKey,
                    ),
                    hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedLoadedApplicationMoreInfoRequestKey),
                },
                {
                    source: 'application-summary',
                    statusToken: summary.statusToken,
                    observedAtMs: observedParentListApplicationId === summary.id
                        ? observedParentListApplicationAtMs
                        : 0,
                    allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedSummaryMoreInfoRequestKey,
                    ),
                    preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                        selectedMoreInfoResponseMarker,
                        selectedSummaryMoreInfoRequestKey,
                    ),
                    hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedSummaryMoreInfoRequestKey),
                },
            ]);
            const selectedCandidateMoreInfoRequestKey = selectedStatusCandidate?.source === 'parent-status'
                ? selectedParentStatusMoreInfoRequestKey
                : selectedStatusCandidate?.source === 'status-view-application'
                    ? selectedStatusViewApplicationMoreInfoRequestKey
                    : selectedStatusCandidate?.source === 'loaded-application-detail'
                        ? selectedLoadedApplicationMoreInfoRequestKey
                        : selectedSummaryMoreInfoRequestKey;
            const selectedMoreInfoResponseMatchesSummaryRequest = doesCompletedMoreInfoResponseMatchRequest(
                selectedMoreInfoResponseMarker,
                selectedCandidateMoreInfoRequestKey,
            );
            const selectedStatusViewStatusToken = isSelectedStatusViewApplication
                ? (
                    selectedMoreInfoResponseLocallyCompleted && selectedMoreInfoResponseMatchesSummaryRequest
                        ? resolveCompletedMoreInfoResponseStatusToken(
                            selectedStatusCandidate?.statusToken,
                            selectedStatusViewParentStatus,
                            selectedStatusViewApplicationStatus,
                            selectedLoadedApplicationStatus,
                            summary.statusToken,
                        )
                        : (selectedStatusCandidate?.statusToken || normalizeParentFinancialAidStatusToken(summary.statusToken))
                )
                : '';
            const canOpenMoreInfoResponse =
                options.allowMoreInfoResponse === true
                && !(selectedMoreInfoResponseLocallyCompleted && selectedMoreInfoResponseMatchesSummaryRequest)
                && (
                    isSelectedStatusViewApplication
                        ? selectedStatusViewStatusToken === 'more_info_needed'
                        : summary.statusToken === 'more_info_needed'
                )
                && (summaryMatchesResponseYear || isSelectedStatusViewApplication);
            if (
                isParentFinancialAidLandingLookupPending
                || (!summary.canResume && !canOpenMoreInfoResponse)
                || (isGenericInviteCycleClosed && !canOpenMoreInfoResponse)
                || (
                    !canOpenMoreInfoResponse &&
                    !doFinancialAidAcademicYearsMatch(summary.academicYear, effectiveFinancialAidAcademicYear)
                    && !isParentFinancialAidAcademicYearAfter(summary.academicYear, effectiveFinancialAidAcademicYear)
                    && !isResumablePriorYearParentFinancialAidSummary(summary)
                )
            ) return;
            const pointer = readParentFinancialAidPointerWithFallback(
                getFinancialAidPointerStoragePair(),
                pointerStorageKey,
                [legacyPointerStorageKey],
            );
            // Drafts saved while an earlier cycle was the effective year keep
            // their step pointer under that cycle's storage key. Read it
            // non-destructively so resuming a prior-year draft restores the
            // saved form location (INT-568).
            const summaryPointerStorageKey = resolveParentFinancialAidPointerStorageKey(
                draftActorId,
                summary.academicYear,
            );
            const summaryScopedPointer = summaryPointerStorageKey === pointerStorageKey
                ? null
                : readParentFinancialAidPointer(getFinancialAidPointerStorage(), summaryPointerStorageKey);
            const matchedPointer = pointer?.applicationId === summary.id
                ? pointer
                : (summaryScopedPointer?.applicationId === summary.id ? summaryScopedPointer : null);
            const pointerStep = matchedPointer
                ? clampParentFinancialAidStep(matchedPointer.currentStep)
                : 0;
            const preferredStep = typeof options.preferredStep === 'number'
                ? clampParentFinancialAidStep(options.preferredStep)
                : null;
            const nextStep = preferredStep ?? pointerStep;
            setDraftApplicationId(summary.id);
            setCurrentStep(nextStep);
            if (!canOpenMoreInfoResponse) {
                writeParentFinancialAidPointer(getFinancialAidPointerStorage(), pointerStorageKey, {
                    applicationId: summary.id,
                    currentStep: nextStep,
                });
            }
            const currentApplicationRecord = currentParentFinancialAidApplication as FinancialAidApplicationRecord | undefined;
            const resumeHydrationSource =
                readText(statusViewApplicationRecord?.id) === summary.id
                    && canHydrateParentFinancialAidFormFromApplication(statusViewApplicationRecord)
                    ? statusViewApplicationRecord
                    : (
                        currentApplicationRecord
                        &&
                        readText(currentApplicationRecord?.id) === summary.id
                        && canHydrateParentFinancialAidFormFromApplication(currentApplicationRecord)
                    )
                        ? currentApplicationRecord
                        : null;
            if (resumeHydrationSource) {
                const hydrated = isParentFinancialAidFormAlreadyHydratedFromApplication(resumeHydrationSource)
                    || hydrateParentFinancialAidFormFromApplication(resumeHydrationSource);
                if (!hydrated) {
                    setPendingResumeApplicationId(summary.id);
                    pendingResumeStartedAtRef.current = Date.now();
                    setSaveDraftMessage('Loading saved financial aid draft...');
                    setIsFormStarted(false);
                    scrollToTop();
                    return;
                }
                pendingResumeStartedAtRef.current = null;
                setPendingResumeApplicationId(null);
            } else {
                setPendingResumeApplicationId(summary.id);
                pendingResumeStartedAtRef.current = Date.now();
                setSaveDraftMessage('Loading saved financial aid draft...');
                setIsFormStarted(false);
                scrollToTop();
                return;
            }
        } else {
            if (isParentFinancialAidLandingLookupPending) return;
            if (isGenericInviteCycleClosed) {
                setSaveDraftError(createFinancialAidActionErrorReport('The financial aid cycle is currently closed for this academic year.'));
                return;
            }
            if (!isFinancialAidAcademicYearConfigured) {
                setSaveDraftError(createFinancialAidActionErrorReport('Financial aid is not configured for a school year yet. Please contact the school office.'));
                return;
            }
            setDraftApplicationId(null);
            hydratedApplicationIdRef.current = null;
            setCurrentStep(0);
            writeParentFinancialAidPointer(getFinancialAidPointerStorage(), pointerStorageKey, {
                applicationId: null,
                currentStep: 0,
            });
        }
        setPendingResumeApplicationId(null);
        setIsFormStarted(true);
        scrollToTop();
    };

    const beginParentFinancialAidMoreInfoResponse = (summary: ParentFinancialAidApplicationSummary) => {
        beginParentFinancialAidForm(summary, {
            preferredStep: DOCUMENTS_STEP_INDEX,
            allowMoreInfoResponse: true,
        });
    };

    const handleViewApplicationStatus = (summary: ParentFinancialAidApplicationSummary) => {
        const params = new URLSearchParams();
        params.set('applicationId', summary.id);
        params.set('view', 'status');
        navigate(`/parent/portal/financialaid?${params.toString()}`);
        scrollToTop();
    };

    const handleReturnToPortalHome = () => {
        navigate('/parent/portal/dashboard');
    };

    const refetchParentFinancialAidApplicationState = async () => {
        const refetchStatusViewApplication =
            (statusViewApplication as { refetch?: () => Promise<unknown> }).refetch;
        const refetchParentStatusView =
            (parentStatusView as { refetch?: () => Promise<unknown> }).refetch;
        await Promise.all([
            parentFinancialAidApplications.refetch().catch(() => undefined),
            draftApplicationId ? financialAidApplication.refetch().catch(() => undefined) : Promise.resolve(),
            refetchStatusViewApplication ? refetchStatusViewApplication().catch(() => undefined) : Promise.resolve(),
            refetchParentStatusView ? refetchParentStatusView().catch(() => undefined) : Promise.resolve(),
        ]);
    };

    const resolveParentFinancialAidDocumentUploadApplicationId = async (): Promise<string> => {
        if (isParentMoreInfoResponseMode && draftApplicationId) {
            return draftApplicationId;
        }
        return ensureBackendDraftApplication({ persistSections: true });
    };

    const handleSaveAndContinueLater = async () => {
        setSubmissionError(null);
        setSaveDraftError(null);
        setSaveDraftMessage(isParentMoreInfoResponseMode ? 'Returning to your portal...' : 'Saving draft...');

        try {
            if (isParentMoreInfoResponseMode) {
                await refetchParentFinancialAidApplicationState();
                setSaveDraftMessage(null);
                navigate('/parent/portal/dashboard');
                return;
            }
            if (isGenericInviteCycleClosed) {
                throw new Error('The financial aid cycle is currently closed for this academic year.');
            }
            const applicationId = await ensureBackendDraftApplication({ persistSections: true });
            if (restoredExistingDraftDuringCreateRef.current) {
                restoredExistingDraftDuringCreateRef.current = false;
            }
            initialDataRef.current = cloneFinancialAidData(formData);
            writeParentFinancialAidPointer(getFinancialAidPointerStorage(), pointerStorageKey, {
                applicationId,
                currentStep,
            });
            setSaveDraftMessage('Draft saved to your account. You can return to Financial Aid and continue later.');
            await Promise.all([
                parentFinancialAidApplications.refetch().catch(() => undefined),
                draftApplicationId ? financialAidApplication.refetch().catch(() => undefined) : Promise.resolve(),
            ]);
            navigate('/parent/portal/dashboard');
        } catch (error) {
            setSaveDraftMessage(null);
            setSaveDraftError(readFinancialAidActionErrorReport(error, 'Unable to save this financial aid draft right now.'));
        }
    };

    const handleDocumentFileChange = async (documentType: string, file: File | null) => {
        if (!file) return;
        const fileType = inferFinancialAidUploadFileType(file);
        setUploadError(null);
        setSubmissionError(null);

        const validationError = getFinancialAidUploadFileValidationError(file);
        if (validationError) {
            setUploadError(createFinancialAidActionErrorReport(validationError));
            return;
        }

        setUploadingDocId(documentType);
        try {
            if (isGenericInviteCycleClosed && !isParentMoreInfoResponseMode) {
                throw new Error('The financial aid cycle is currently closed for this academic year.');
            }
            const applicationId = await resolveParentFinancialAidDocumentUploadApplicationId();
            const uploadInfo = await uploadDocument.mutateAsync({
                applicationId,
                documentType,
                fileName: file.name,
                fileType,
                fileSizeBytes: file.size,
            }) as FinancialAidUploadInfo;
            const uploadUrl = readText(uploadInfo.uploadUrl);
            const documentId = readText(uploadInfo.documentId);
            const s3Key = readText(uploadInfo.s3Key);
            if (!uploadUrl || !documentId || !s3Key) {
                throw new Error('Upload request did not return the required S3 persistence fields.');
            }

            const putResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: buildBrowserUploadHeaders({
                    uploadHeaders: uploadInfo.uploadHeaders,
                    uploadUrl,
                    fileType,
                }),
            });
            if (!putResponse.ok) {
                throw new Error(toDocumentUploadFailureMessage(putResponse.status));
            }

            await confirmDocumentUpload.mutateAsync({
                applicationId,
                documentType,
                documentId,
                s3Key,
                fileName: file.name,
                fileType,
                fileSizeBytes: file.size,
            });
            if (draftApplicationId) {
                await documentStatus.refetch().catch(() => undefined);
            }
            setFormData((prev) => {
                const next = {
                    ...prev,
                    documents: prev.documents.map((doc) => (
                        doc.id === documentType
                            ? { ...doc, uploaded: true, fileName: file.name }
                            : doc
                    )),
                };
                return isParentMoreInfoResponseMode ? next : invalidateFinancialAidSignatures(next);
            });
        } catch (error) {
            setUploadError(readFinancialAidActionErrorReport(error, 'Unable to upload this document right now.'));
            setFormData(prev => ({
                ...prev,
                documents: prev.documents.map((doc) => (
                    doc.id === documentType
                        ? { ...doc, uploaded: false, fileName: undefined }
                        : doc
                )),
            }));
        } finally {
            setUploadingDocId(null);
        }
    };

    const handleSubmitMoreInfoResponse = async () => {
        setSubmissionError(null);
        setSaveDraftError(null);
        if (!draftApplicationId || !isParentMoreInfoResponseMode) {
            setSubmissionError(createFinancialAidActionErrorReport('Open the requested More Info application before sending a response.'));
            return;
        }

        const response = moreInfoResponseMessage.trim()
            || 'I uploaded the requested information through the parent portal.';
        const responseApplicationId = draftApplicationId;

        try {
            setSaveDraftMessage('Sending response...');
            await respondToInfoRequest.mutateAsync({
                applicationId: responseApplicationId,
                response,
                moreInfoRequestId: activeMoreInfoResponseRequestId || undefined,
            });
            const completedMarker = {
                phase: 'pending_return' as const,
                completedAtMs: Date.now(),
                answeredRequestKey: activeMoreInfoResponseRequestKey || undefined,
            };
            writeStoredCompletedMoreInfoResponseMarker(responseApplicationId, completedMarker);
            setCompletedMoreInfoResponseByApplicationId((current) => ({
                ...current,
                [responseApplicationId]: completedMarker,
            }));
            setMoreInfoResponseMessage('');
            setDraftApplicationId(null);
            setPendingResumeApplicationId(null);
            pendingResumeStartedAtRef.current = null;
            hydratedApplicationIdRef.current = null;
            hydratedApplicationDocumentsRef.current = null;
            setIsFormStarted(false);
            setCurrentStep(0);
            setSaveDraftMessage('Your response was sent to the financial aid office.');
            await refetchParentFinancialAidApplicationState().catch((error) => {
                const message = error instanceof Error ? error.message : String(error);
                console.warn(`[financial-aid-parent] response submitted but status refresh failed: ${message}`);
            });
            const responseStatusParams = new URLSearchParams();
            responseStatusParams.set('applicationId', responseApplicationId);
            responseStatusParams.set('view', 'status');
            const responseAcademicYear =
                readText(statusViewParams.get('academicYear'))
                || readText(statusViewApplicationSummary?.academicYear)
                || readText(activeApplicationSummary?.academicYear);
            if (responseAcademicYear) {
                responseStatusParams.set('academicYear', responseAcademicYear);
            }
            navigate(`/parent/portal/financialaid?${responseStatusParams.toString()}`);
        } catch (error) {
            setSaveDraftMessage(null);
            setSubmissionError(readFinancialAidActionErrorReport(error, 'Unable to send this response right now.'));
        }
    };

    const handleSubmitApplication = async () => {
        if (submitInFlightRef.current || submissionCompletedRef.current || currentStep >= 9 || submittedReference) {
            return;
        }

        submitInFlightRef.current = true;
        setSubmissionError(null);

        try {
            setValidationRequested(true);
            if (!isFinancialAidAcademicYearConfigured) {
                setSubmissionError(createFinancialAidActionErrorReport('Financial aid is not configured for a school year yet. Please contact the school office.'));
                scrollToTop();
                return;
            }
            const currentValidationIssues = getParentFinancialAidValidationIssues(formData, financialAidValidationOptions);
            if (currentValidationIssues.length > 0) {
                const firstIssue = currentValidationIssues[0];
                setCurrentStep(getParentFinancialAidFirstIssueStep(currentValidationIssues));
                setSubmissionError(createFinancialAidActionErrorReport(`Please complete ${firstIssue.label} before submitting.`));
                scrollToTop();
                return;
            }
            const applicantId = readText(profile?.userSub);
            if (!applicantId) {
                setSubmissionError(createFinancialAidActionErrorReport('Unable to determine your parent identity. Please sign in again.'));
                return;
            }
            if (!areRequiredParentFinancialAidDocumentsUploaded(formData.documents)) {
                setSubmissionError(createFinancialAidActionErrorReport('All required documents must be confirmed before submission.'));
                return;
            }
            if (isFinancialAidDocumentSettingsPending) {
                setSubmissionError(createFinancialAidActionErrorReport('We are still loading the school document requirements. Please wait a moment and try again.'));
                return;
            }
            if (isGenericInviteCycleClosed) {
                setSubmissionError(createFinancialAidActionErrorReport('The financial aid cycle is currently closed for this academic year.'));
                return;
            }

            const signature = resolveParentFinancialAidCopyCertificationName({
                fatherName: `${readText(formData.father.firstName)} ${readText(formData.father.lastName)}`,
                fatherComplete: isParentFinancialAidParentInfoComplete(formData, 'father'),
                fatherSigned: Boolean(formData.signatures.father),
                motherName: `${readText(formData.mother.firstName)} ${readText(formData.mother.lastName)}`,
                motherComplete: isParentFinancialAidParentInfoComplete(formData, 'mother'),
                motherSigned: Boolean(formData.signatures.mother),
            });
            const attestations = formData.signatures.attestations;
            const completedAttestations = didParentFinancialAidCopyCompleteAttestations(attestations, null);
            const certification: ParentFinancialAidCopyCertificationContext = {
                certifiedAccurate: Boolean(attestations.certifiedAccurate),
                agreedToTerms: completedAttestations,
                electronicSignature: signature,
                certificationDate: new Date().toISOString(),
            };
            const applicationId = await ensureBackendDraftApplication({
                persistSections: true,
                suppressHiddenFields: true,
                certification,
            });
            // The backend draft snapshot does not round-trip the agreement
            // checklist, so capture it locally before the submit mutation can
            // fail (e.g. the registration-fee gate). A blocked submit must
            // leave the checked attestations exactly as they were, including
            // across a reload or save/resume of this draft.
            writeParentFinancialAidStoredAttestations(getFinancialAidPointerStorage(), applicationId, attestations);
            if (draftApplicationId) {
                await documentStatus.refetch().catch(() => undefined);
            }
            const submitted = await submitApplication.mutateAsync({
                id: applicationId,
                certification: {
                    certifiedAccurate: Boolean(certification.certifiedAccurate),
                    certifiedBy: applicantId,
                    electronicSignature: certification.electronicSignature ?? signature,
                    agreedToTerms: Boolean(certification.agreedToTerms),
                },
            });
            const submittedCode = readText((submitted as unknown as Record<string, unknown>).code);
            submissionCompletedRef.current = true;
            setCompletionStatus('submitted');
            clearParentFinancialAidPointerStorageFrom(getFinancialAidPointerStorage());
            clearParentFinancialAidPointerStorageFrom(getFinancialAidSessionPointerStorage());
            setSubmissionError(null);
            setDownloadError(null);
            setDraftApplicationId(null);
            setSubmittedApplicationId(applicationId);
            setSubmittedCertification(certification);
            setSubmittedReference(submittedCode || applicationId);
            setCurrentStep(9);
            scrollToTop();
        } catch (error) {
            if (submissionCompletedRef.current) return;
            setSubmissionError(readParentFinancialAidSubmitErrorReport(error, 'Unable to submit financial aid application right now.'));
        } finally {
            submitInFlightRef.current = false;
        }
    };

    const handleDownloadCopy = async (options: {
        summary?: ParentFinancialAidApplicationSummary | null;
        application?: FinancialAidApplicationRecord | null;
        requirePersistedApplication?: boolean;
        mode?: FinancialAidCopyOpenMode;
    } = {}) => {
        setDownloadError(null);
        const copyMode = options.mode ?? 'download';

        try {
            const application = options.application ?? null;
            const persistedApplicationId =
                readText(options.summary?.id)
                || readText(application?.id)
                || submittedApplicationId
                || draftApplicationId;
            if (persistedApplicationId) {
                const popupWindow = openPendingFinancialAidReportWindow();
                try {
                    await openGeneratedFinancialAidApplicationReport({
                        applicationId: persistedApplicationId,
                        disposition: copyMode === 'download' ? 'attachment' : 'inline',
                        popupWindow,
                    });
                } catch (error) {
                    closePendingFinancialAidReportWindow(popupWindow);
                    throw error;
                }
                return;
            }
            if (options.requirePersistedApplication && !application) {
                throw new Error('Unable to load the saved financial aid application copy yet.');
            }
            const rawCopyData = application
                ? buildParentFinancialAidDataFromApplication(application, profile, activeConfiguredRequiredDocuments)
                : formData;
            const copyData = clearParentFinancialAidHiddenRequirementValues(rawCopyData, financialAidRequirementPolicy);
            const certification = application
                ? readParentFinancialAidCopyCertificationContext(application.sections)
                : submittedCertification;
            const reference =
                options.summary?.code
                || readText(application?.code)
                || submittedReference
                || submittedApplicationId
                || draftApplicationId
                || 'FA-PENDING';
            const requestedAcademicYear = options.summary?.academicYear || readText(application?.academicYear);
            const requestedDisplayAcademicYear = resolveFinancialAidDisplayYears(requestedAcademicYear).academicYear;
            const academicYear = requestedAcademicYear
                ? requestedDisplayAcademicYear || ''
                : financialAidDisplayAcademicYear || '';
            const academicYearLabel = financialAidAcademicYearCopyLabel(academicYear);
            const opened = await openSectionedPdfInNewTab({
                title: 'Financial Aid Application Submitted',
                subtitle: `Reference ${reference} - Academic year ${academicYearLabel}`,
                sections: buildFinancialAidCopyPdfSections(copyData, reference, academicYear, certification),
                fileName: `financial-aid-application-${reference}.pdf`,
                targetWindow: null,
                openMode: copyMode === 'download' ? 'download' : 'preview',
            });
            if (!opened) {
                const fallbackMessage = copyMode === 'download'
                    ? 'Unable to download a copy. Please try again.'
                    : 'Unable to prepare a printable copy. Please try again.';
                setDownloadError(createFinancialAidActionErrorReport(fallbackMessage));
            }
        } catch (error) {
            const fallbackMessage = copyMode === 'download'
                ? 'Unable to prepare/download a copy right now.'
                : 'Unable to prepare a printable copy right now.';
            setDownloadError(readFinancialAidActionErrorReport(error, fallbackMessage));
        }
    };

    const openGeneratedFinancialAidApplicationReport = async ({
        applicationId,
        disposition,
        popupWindow,
    }: {
        applicationId: string;
        disposition: 'inline' | 'attachment';
        popupWindow: FinancialAidReportPopupWindow;
    }): Promise<void> => {
        const response = await generateApplicationReport.mutateAsync({
            applicationId,
            variant: 'yst',
        });
        const responseRecord = asRecord(response);
        const artifactId = readText(responseRecord.artifactId);
        let downloadUrl = readText(responseRecord.downloadUrl);
        let fileName = readText(responseRecord.fileName);
        if ((disposition === 'attachment' || !downloadUrl) && artifactId) {
            const signedUrlResponse = await getReportDownloadUrl.mutateAsync({
                applicationId,
                artifactId,
                disposition,
            });
            const signedUrlRecord = asRecord(signedUrlResponse);
            downloadUrl = readText(signedUrlRecord.downloadUrl) || downloadUrl;
            fileName = readText(signedUrlRecord.fileName) || fileName;
        }
        if (!downloadUrl) {
            throw new Error('Report generation did not return a PDF URL.');
        }
        const opened = openPdfUrlInFallbackShell({
            url: downloadUrl,
            fileName: fileName || `financial-aid-application-${applicationId}.pdf`,
            targetWindow: popupWindow,
        });
        if (!opened) {
            throw new Error('Unable to open generated PDF.');
        }
    };

    const handleOpenStatusYstReport = async (summary: ParentFinancialAidApplicationSummary) => {
        setDownloadError(null);
        const applicationId = readText(summary.id);
        if (!applicationId) {
            setDownloadError(createFinancialAidActionErrorReport('Unable to identify this financial aid application.'));
            return;
        }
        const popupWindow = openPendingFinancialAidReportWindow();
        try {
            await openGeneratedFinancialAidApplicationReport({
                applicationId,
                disposition: 'inline',
                popupWindow,
            });
        } catch (error) {
            closePendingFinancialAidReportWindow(popupWindow);
            setDownloadError(readParentYstReportErrorReport(error, 'Unable to generate/open the YST Financial Aid Report right now.'));
        }
    };

    const renderApplicationLanding = () => {
        const summary = activeApplicationSummary;
        const summaryIsFutureOfEffectiveYear = isParentFinancialAidAcademicYearAfter(
            summary?.academicYear,
            effectiveFinancialAidAcademicYear,
        );
        // INT-568: bind the landing actions to the draft's own year when the
        // saved draft belongs to an earlier still-open cycle, so Continue is
        // offered for that draft instead of falling through to a blank
        // wrong-year Start.
        const stateAcademicYear = summaryIsFutureOfEffectiveYear || activeApplicationIsResumablePriorYearDraft
            ? summary?.academicYear
            : effectiveFinancialAidAcademicYear;
        const existingApplicationState = resolveParentFinancialAidExistingApplicationState(
            summary,
            stateAcademicYear,
        );
        const summaryMatchesLandingYear = doFinancialAidAcademicYearsMatch(
            summary?.academicYear,
            stateAcademicYear,
        );
        const parentStatusRecord = asRecord(parentStatusView.data);
        const missingDocuments = Array.isArray(parentStatusRecord.missingDocuments)
            ? parentStatusRecord.missingDocuments
                .map((document) => resolveParentFinancialAidDocumentName(document, activeConfiguredRequiredDocuments))
                .filter(Boolean)
            : [];
        const matchingStatusViewApplicationRecord =
            summary && readText(statusViewApplicationRecord?.id) === summary.id
                ? statusViewApplicationRecord
                : null;
        const matchingLoadedApplicationRecord =
            summary && readText(loadedDraftApplicationRecord?.id) === summary.id
                ? loadedDraftApplicationRecord
                : null;
        const parentStatusDecisionRecord = asRecord(parentStatusRecord.decision);
        const moreInfoDecisionRequest = readText(parentStatusDecisionRecord.decisionType) === 'request_more_info'
            ? parentStatusDecisionRecord
            : null;
        const statusViewApplicationDetailRecord = asRecord(matchingStatusViewApplicationRecord);
        const loadedApplicationDetailRecord = asRecord(matchingLoadedApplicationRecord);
        const isSelectedMoreInfoStatusView = Boolean(
            summary
            && isParentStatusViewRequested
            && routeApplicationId === summary.id
        );
        const selectedMoreInfoResponseLocallyCompleted = Boolean(
            summary
            && isMoreInfoResponseLocallyCompleted(summary.id)
        );
        const selectedMoreInfoResponseMarker = summary
            ? readCompletedMoreInfoResponseMarker(summary.id)
            : null;
        const parentStatusRecordMatchesSummary =
            summary
            && readText(parentStatusRecord.applicationId) === summary.id;
        const parentStatusTokenForSummary =
            isSelectedMoreInfoStatusView && parentStatusRecordMatchesSummary
                ? readText(parentStatusRecord.status)
                : '';
        const parentStatusObservedAtMs =
            parentStatusTokenForSummary ? readFinancialAidStatusObservationTimestamp(parentStatusRecord) : 0;
        const statusViewApplicationStatusTokenForSummary =
            summary && readText(statusViewApplicationRecord?.id) === summary.id
                ? readText(statusViewApplicationRecord?.status)
                : '';
        const statusViewApplicationObservedAtMs =
            statusViewApplicationStatusTokenForSummary
                ? readFinancialAidStatusObservationTimestamp(statusViewApplicationDetailRecord)
                : 0;
        const loadedApplicationStatusTokenForSummary =
            summary && readText(loadedDraftApplicationRecord?.id) === summary.id
                ? readText(loadedDraftApplicationRecord?.status)
                : '';
        const loadedApplicationObservedAtMs =
            loadedApplicationStatusTokenForSummary
                ? readFinancialAidStatusObservationTimestamp(loadedApplicationDetailRecord)
                : 0;
        const summaryObservedAtMs =
            summary && observedParentListApplicationId === summary.id
                ? observedParentListApplicationAtMs
                : 0;
        const selectedParentStatusMoreInfoRequestKey =
            normalizeParentFinancialAidStatusToken(parentStatusTokenForSummary) === 'more_info_needed'
                ? readFinancialAidMoreInfoRequestKey(parentStatusRecord)
                : '';
        const selectedStatusViewApplicationMoreInfoRequestKey =
            normalizeParentFinancialAidStatusToken(statusViewApplicationStatusTokenForSummary) === 'more_info_needed'
                ? readFinancialAidMoreInfoRequestKey(statusViewApplicationDetailRecord)
                : '';
        const selectedLoadedApplicationMoreInfoRequestKey =
            normalizeParentFinancialAidStatusToken(loadedApplicationStatusTokenForSummary) === 'more_info_needed'
                ? readFinancialAidMoreInfoRequestKey(loadedApplicationDetailRecord)
                : '';
        const selectedSummaryMoreInfoRequestKey = readSummaryMoreInfoRequestKey(summary);
        const newestStatusCandidate = selectNewestFinancialAidStatusCandidate([
            {
                source: 'parent-status',
                statusToken: parentStatusTokenForSummary,
                observedAtMs: parentStatusObservedAtMs,
                allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedParentStatusMoreInfoRequestKey,
                ),
                preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedParentStatusMoreInfoRequestKey,
                ),
                hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedParentStatusMoreInfoRequestKey),
            },
            {
                source: 'status-view-application',
                statusToken: statusViewApplicationStatusTokenForSummary,
                observedAtMs: statusViewApplicationObservedAtMs,
                allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedStatusViewApplicationMoreInfoRequestKey,
                ),
                preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedStatusViewApplicationMoreInfoRequestKey,
                ),
                hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedStatusViewApplicationMoreInfoRequestKey),
            },
            {
                source: 'loaded-application-detail',
                statusToken: loadedApplicationStatusTokenForSummary,
                observedAtMs: loadedApplicationObservedAtMs,
                allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedLoadedApplicationMoreInfoRequestKey,
                ),
                preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedLoadedApplicationMoreInfoRequestKey,
                ),
                hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedLoadedApplicationMoreInfoRequestKey),
            },
            {
                source: 'application-summary',
                statusToken: summary?.statusToken,
                observedAtMs: summaryObservedAtMs,
                allowTimestamplessOverride: !selectedMoreInfoResponseMarker || isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedSummaryMoreInfoRequestKey,
                ),
                preferOnEqualTimestamp: isDifferentCompletedMoreInfoRequest(
                    selectedMoreInfoResponseMarker,
                    selectedSummaryMoreInfoRequestKey,
                ),
                hasMoreInfoRequestContext: hasFinancialAidMoreInfoRequestContextToken(selectedSummaryMoreInfoRequestKey),
            },
        ]);
        const selectedCandidateMoreInfoRequestKey = newestStatusCandidate?.source === 'parent-status'
            ? selectedParentStatusMoreInfoRequestKey
            : newestStatusCandidate?.source === 'status-view-application'
                ? selectedStatusViewApplicationMoreInfoRequestKey
                : newestStatusCandidate?.source === 'loaded-application-detail'
                    ? selectedLoadedApplicationMoreInfoRequestKey
                    : selectedSummaryMoreInfoRequestKey;
        const hasDifferentSelectedMoreInfoRequest = hasDifferentCompletedMoreInfoRequest(
            selectedMoreInfoResponseMarker,
            [
                selectedParentStatusMoreInfoRequestKey,
                selectedStatusViewApplicationMoreInfoRequestKey,
                selectedLoadedApplicationMoreInfoRequestKey,
                selectedSummaryMoreInfoRequestKey,
            ],
        );
        const selectedMoreInfoResponseMatchesSummaryRequest = doesCompletedMoreInfoResponseMatchRequest(
            selectedMoreInfoResponseMarker,
            selectedCandidateMoreInfoRequestKey,
        );
        const statusViewStatusToken = normalizeParentFinancialAidStatusToken(
            (
                hasDifferentSelectedMoreInfoRequest
                    ? 'more_info_needed'
                    : ''
            )
            || (
                selectedMoreInfoResponseLocallyCompleted && selectedMoreInfoResponseMatchesSummaryRequest
                    ? resolveCompletedMoreInfoResponseStatusToken(
                        newestStatusCandidate?.statusToken,
                        parentStatusTokenForSummary,
                        statusViewApplicationStatusTokenForSummary,
                        loadedApplicationStatusTokenForSummary,
                        summary?.statusToken,
                    )
                    : ''
            )
            || newestStatusCandidate?.statusToken
            || summary?.statusToken,
        );
        const statusViewStatusSource = newestStatusCandidate?.source ?? 'application-summary';
        const isSelectedMoreInfoCompletionSuppressingStatus =
            selectedMoreInfoResponseLocallyCompleted
            && selectedMoreInfoResponseMatchesSummaryRequest
            && statusViewStatusToken === 'under_review';
        const parentStatusNextSteps = Array.isArray(parentStatusRecord.nextSteps)
            ? parentStatusRecord.nextSteps.map((step) => readText(step)).filter(Boolean)
            : [];
        const canUseParentStatusNextSteps =
            statusViewStatusSource === 'parent-status'
            && (
                !selectedMoreInfoResponseLocallyCompleted
                || !selectedMoreInfoResponseMatchesSummaryRequest
                || normalizeParentFinancialAidStatusToken(parentStatusTokenForSummary) === statusViewStatusToken
            );
        const statusNextSteps = isSelectedMoreInfoCompletionSuppressingStatus
            ? ['The application is back under review.']
            : canUseParentStatusNextSteps
                ? parentStatusNextSteps
                : [];
        const statusViewDisplayStatusLabel =
            (
                hasDifferentSelectedMoreInfoRequest
                    ? formatParentFinancialAidStatusLabel(statusViewStatusToken)
                    : ''
            )
            || (
                selectedMoreInfoResponseLocallyCompleted && selectedMoreInfoResponseMatchesSummaryRequest
                    ? formatParentFinancialAidStatusLabel(statusViewStatusToken)
                    : ''
            )
            || (statusViewStatusSource === 'parent-status' && parentStatusRecordMatchesSummary
                ? readText(parentStatusRecord.statusLabel) || formatParentFinancialAidStatusLabel(statusViewStatusToken)
                : ''
            )
            || (
                matchingStatusViewApplicationRecord || matchingLoadedApplicationRecord
                    ? formatParentFinancialAidStatusLabel(statusViewStatusToken)
                    : ''
            )
            || summary?.statusLabel
            || 'Status Pending';
        const isMoreInfoNeededStatusView = statusViewStatusToken === 'more_info_needed';
        const canShowMoreInfoStatusBanner =
            isMoreInfoNeededStatusView
            && !(selectedMoreInfoResponseLocallyCompleted && selectedMoreInfoResponseMatchesSummaryRequest);
        // INT-568: before submission the parent simply has not reached the
        // documents step yet — present the document list neutrally instead of
        // as an alarming "missing documents" warning.
        const isStatusViewPreSubmission = isParentFinancialAidApplicationResumable(statusViewStatusToken);
        const canDownloadStatusViewApplicationCopy =
            canDownloadParentFinancialAidApplicationCopy(statusViewStatusToken)
            && !isStatusViewPreSubmission;
        const summaryRequestedInfo = readText(summary?.requestedInfo);
        const moreInfoRequestedInfo = statusViewStatusSource === 'status-view-application'
            ? (
                readText(statusViewApplicationDetailRecord.requestedInfo)
                || readText(loadedApplicationDetailRecord.requestedInfo)
                || readText(parentStatusRecord.requestedInfo)
                || readText(moreInfoDecisionRequest?.requestedInfo)
                || summaryRequestedInfo
            )
            : statusViewStatusSource === 'loaded-application-detail'
                ? (
                    readText(loadedApplicationDetailRecord.requestedInfo)
                    || readText(statusViewApplicationDetailRecord.requestedInfo)
                    || readText(parentStatusRecord.requestedInfo)
                    || readText(moreInfoDecisionRequest?.requestedInfo)
                    || summaryRequestedInfo
                )
            : statusViewStatusSource === 'application-summary'
                ? (
                    summaryRequestedInfo
                    || readText(loadedApplicationDetailRecord.requestedInfo)
                    || readText(parentStatusRecord.requestedInfo)
                    || readText(moreInfoDecisionRequest?.requestedInfo)
                    || readText(statusViewApplicationDetailRecord.requestedInfo)
                )
                : (
                    readText(parentStatusRecord.requestedInfo)
                    || readText(moreInfoDecisionRequest?.requestedInfo)
                    || readText(loadedApplicationDetailRecord.requestedInfo)
                    || readText(statusViewApplicationDetailRecord.requestedInfo)
                    || summaryRequestedInfo
                );
        const summaryResponseDeadlineValue = summary?.responseDeadline;
        const moreInfoResponseDeadlineValue =
            statusViewStatusSource === 'status-view-application'
                ? (
                    statusViewApplicationDetailRecord.responseDeadline
                    ?? loadedApplicationDetailRecord.responseDeadline
                    ?? parentStatusRecord.responseDeadline
                    ?? moreInfoDecisionRequest?.responseDeadline
                    ?? summaryResponseDeadlineValue
                )
                : statusViewStatusSource === 'loaded-application-detail'
                    ? (
                        loadedApplicationDetailRecord.responseDeadline
                        ?? statusViewApplicationDetailRecord.responseDeadline
                        ?? parentStatusRecord.responseDeadline
                        ?? moreInfoDecisionRequest?.responseDeadline
                        ?? summaryResponseDeadlineValue
                    )
                : statusViewStatusSource === 'application-summary'
                    ? (
                        summaryResponseDeadlineValue
                        ?? loadedApplicationDetailRecord.responseDeadline
                        ?? parentStatusRecord.responseDeadline
                        ?? moreInfoDecisionRequest?.responseDeadline
                        ?? statusViewApplicationDetailRecord.responseDeadline
                    )
                    : (
                        parentStatusRecord.responseDeadline
                        ?? moreInfoDecisionRequest?.responseDeadline
                        ?? loadedApplicationDetailRecord.responseDeadline
                        ?? statusViewApplicationDetailRecord.responseDeadline
                        ?? summaryResponseDeadlineValue
                    );
        const moreInfoResponseDeadlineLabel = formatParentFinancialAidDeadlineLabel(
            moreInfoResponseDeadlineValue,
        );
        const isStatusViewApplicationLoading = Boolean(
            (statusViewApplication as { isLoading?: unknown; isFetching?: unknown }).isLoading
            || (statusViewApplication as { isLoading?: unknown; isFetching?: unknown }).isFetching,
        );
        const canOpenMoreInfoStatusResponse = Boolean(
            summary
            && canShowMoreInfoStatusBanner
            && (
                summary.statusToken === 'more_info_needed'
                || isSelectedMoreInfoStatusView
            )
            && (summaryMatchesLandingYear || isSelectedMoreInfoStatusView)
            && !summary.canResume
            && !isInviteClaimPending
            && !isInviteEntryBlocked
            && !isParentFinancialAidLandingLookupPending
            && !isStatusViewApplicationDetailsPending
        );
        return (
            <section
                className="animate-in slide-in-from-bottom-4 fade-in duration-500"
                data-testid="parent-financial-aid-landing"
            >
                <Card className="mb-8">
                    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <section>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-primary/70">Financial Aid</p>
                            <h2 className="mt-2 text-2xl font-extrabold text-irongrey">
                                {existingApplicationState?.title ?? 'Application status'}
                            </h2>
                            <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-irongrey/60">
                                {existingApplicationState?.body ?? 'Review your current financial aid application before starting or continuing.'}
                            </p>
                        </section>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            {canBeginCurrentYearApplication ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    data-testid="parent-financial-aid-new-application"
                                    onClick={() => beginParentFinancialAidForm(null)}
                                >
                                    + New Application
                                </Button>
                            ) : null}
                            {summary ? <StatusBadge status={statusViewDisplayStatusLabel} /> : null}
                        </div>
                    </header>

                    {isParentFinancialAidLandingLookupPending || isInviteClaimPending ? (
                        <p className="mt-6 text-sm font-semibold text-irongrey/55" data-testid="parent-financial-aid-landing-loading">
                            {isInviteClaimPending
                                ? 'Linking your financial aid invitation...'
                                : 'Checking application status...'}
                        </p>
                    ) : null}

                    {summary ? (
                        <section
                            className="mt-6 rounded-2xl border border-primary/10 bg-primary/5 p-5"
                            data-testid="parent-financial-aid-current-application"
                        >
                            <dl className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Reference</dt>
                                    <dd className="mt-1 text-sm font-extrabold text-irongrey" data-testid="parent-financial-aid-current-reference">
                                        {summary.code}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Academic Year</dt>
                                    <dd className="mt-1 text-sm font-extrabold text-irongrey" data-testid="parent-financial-aid-current-academic-year">
                                        {summary.academicYear}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Status</dt>
                                    <dd className="mt-1 text-sm font-extrabold text-irongrey" data-testid="parent-financial-aid-current-status">
                                        {statusViewDisplayStatusLabel}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                                        {summary.submittedDateLabel ? 'Submitted' : 'Updated'}
                                    </dt>
                                    <dd className="mt-1 text-sm font-extrabold text-irongrey" data-testid="parent-financial-aid-current-submitted">
                                        {summary.submittedDateLabel || summary.updatedDateLabel || 'Pending'}
                                    </dd>
                                </div>
                            </dl>
                            <p className="mt-5 text-sm font-semibold leading-6 text-irongrey/65" data-testid="parent-financial-aid-current-next-step">
                                {summary.nextStep}
                            </p>
                            {existingApplicationState ? (
                                <p
                                    className="mt-3 text-sm font-semibold leading-6 text-primary"
                                    data-testid="parent-financial-aid-existing-application-state"
                                >
                                    {existingApplicationState.body}
                                </p>
                            ) : null}
                        </section>
                    ) : (
                        <section className="mt-6 rounded-2xl border border-gray-100 bg-gray-50/80 p-5" data-testid="parent-financial-aid-no-current-application">
                            <p className="text-sm font-semibold text-irongrey/65">
                                {isInviteClaimPending
                                    ? 'We are linking this financial aid invitation to your parent account.'
                                    : `No financial aid application is on file for ${financialAidCycleInlineLabel}.`}
                            </p>
                        </section>
                    )}

                    {parentFinancialAidApplicationSummaries.length > 0 ? (
                        <section
                            className="mt-6 rounded-2xl border border-gray-100 bg-white p-5"
                            data-testid="parent-financial-aid-application-history"
                        >
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                <h3 className="text-sm font-extrabold text-irongrey">Application history</h3>
                                <p className="text-xs font-semibold text-irongrey/50">Current, invited, and prior school years</p>
                            </div>
                            <div className="mt-4 divide-y divide-gray-100">
                                {parentFinancialAidApplicationSummaries.map((historySummary) => (
                                    <div
                                        key={historySummary.id}
                                        className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                                        data-testid={`parent-financial-aid-history-row-${historySummary.id}`}
                                    >
                                        <div>
                                            <p className="text-sm font-extrabold text-irongrey">
                                                {historySummary.academicYear} · {historySummary.code}
                                            </p>
                                            <p className="mt-1 text-xs font-semibold text-irongrey/55">
                                                {historySummary.statusLabel}
                                                {' · '}
                                                {historySummary.submittedDateLabel
                                                    ? `Submitted ${historySummary.submittedDateLabel}`
                                                    : `Updated ${historySummary.updatedDateLabel || 'pending'}`}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                            {historySummary.id === summary?.id ? (
                                                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-extrabold text-primary">
                                                    Selected
                                                </span>
                                            ) : null}
                                            {isResumablePriorYearParentFinancialAidSummary(historySummary)
                                                && historySummary.id !== summary?.id
                                                && !isInviteClaimPending
                                                && !isInviteEntryBlocked
                                                && !isParentFinancialAidLandingLookupPending
                                                && !isGenericInviteCycleClosed ? (
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    data-testid={`parent-financial-aid-history-continue-${historySummary.id}`}
                                                    onClick={() => beginParentFinancialAidForm(historySummary)}
                                                >
                                                    Continue Application <ChevronRight size={16} />
                                                </Button>
                                            ) : null}
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                data-testid={`parent-financial-aid-history-view-${historySummary.id}`}
                                                onClick={() => handleViewApplicationStatus(historySummary)}
                                            >
                                                View Status
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null}

                    {isParentStatusViewRequested ? (
                        <section
                            className="mt-6 rounded-2xl border border-gray-100 bg-white p-5"
                            data-testid="parent-financial-aid-status-view"
                        >
                            <h3 className="text-sm font-extrabold text-irongrey">Application status details</h3>
                            {parentStatusView.isLoading ? (
                                <p className="mt-3 text-sm font-semibold text-irongrey/55">Loading status details...</p>
                            ) : parentStatusView.error ? (
                                <p className="mt-3 text-sm font-semibold text-strawberry">
                                    Unable to load status details right now.
                                </p>
                            ) : (
                                <div className="mt-4 space-y-4">
                                    {canShowMoreInfoStatusBanner ? (
                                        <section
                                            className="rounded-2xl border border-amber-300 bg-amber-50 p-4"
                                            data-testid="parent-financial-aid-more-info-banner"
                                        >
                                            <p className="text-sm font-extrabold text-amber-900">
                                                More information needed
                                            </p>
                                            <p className="mt-1 text-sm font-semibold leading-6 text-amber-900/80">
                                                The financial aid office needs more information before it can finish
                                                reviewing this application.
                                            </p>
                                            {moreInfoRequestedInfo ? (
                                                <p
                                                    className="mt-3 whitespace-pre-line rounded-xl border border-amber-200 bg-white p-3 text-sm font-semibold leading-6 text-irongrey"
                                                    data-testid="parent-financial-aid-more-info-message"
                                                >
                                                    {moreInfoRequestedInfo}
                                                </p>
                                            ) : null}
                                            {moreInfoResponseDeadlineLabel ? (
                                                <p
                                                    className="mt-3 text-sm font-bold text-amber-900"
                                                    data-testid="parent-financial-aid-more-info-deadline"
                                                >
                                                    Please respond by {moreInfoResponseDeadlineLabel}.
                                                </p>
                                            ) : null}
                                            <p className="mt-2 text-xs font-semibold leading-5 text-amber-900/70">
                                                Upload any requested documents below or contact the school office if
                                                you have questions.
                                            </p>
                                            {canOpenMoreInfoStatusResponse ? (
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="mt-4"
                                                    data-testid="parent-financial-aid-more-info-upload-action"
                                                    onClick={() => {
                                                        if (summary) beginParentFinancialAidMoreInfoResponse(summary);
                                                    }}
                                                >
                                                    Upload requested documents <ChevronRight size={16} />
                                                </Button>
                                            ) : null}
                                        </section>
                                    ) : null}
                                    <p className="text-sm font-semibold leading-6 text-irongrey/65">
                                        {statusViewDisplayStatusLabel}
                                    </p>
                                    {statusNextSteps.length > 0 ? (
                                        <ul className="list-disc space-y-1 pl-5 text-sm font-semibold text-irongrey/65">
                                            {statusNextSteps.map((step) => <li key={step}>{step}</li>)}
                                        </ul>
                                    ) : null}
                                    {missingDocuments.length > 0 ? (
                                        isStatusViewPreSubmission ? (
                                            <p
                                                className="text-sm font-semibold text-irongrey/65"
                                                data-testid="parent-financial-aid-status-documents-pending"
                                            >
                                                Documents not started yet. You can upload these when you continue
                                                your application: {missingDocuments.join(', ')}
                                            </p>
                                        ) : (
                                            <p
                                                className="text-sm font-semibold text-amber-800"
                                                data-testid="parent-financial-aid-status-documents-missing"
                                            >
                                                Missing documents: {missingDocuments.join(', ')}
                                            </p>
                                        )
                                    ) : null}
                                    {summary && !isStatusViewApplicationLoading && !matchingStatusViewApplicationRecord ? (
                                        <p className="text-sm font-semibold text-irongrey/55">
                                            We are still getting the saved application copy ready. Please try again in a moment.
                                        </p>
                                    ) : null}
                                </div>
                            )}
                        </section>
                    ) : null}

                    {downloadError ? (
                        <SupportErrorMessage className="mt-6">
                            <span>{downloadError.userMessage}</span>
                            <FinancialAidTechnicalSupportDetails report={downloadError} />
                        </SupportErrorMessage>
                    ) : null}

                    {saveDraftError ? (
                        <SupportErrorMessage className="mt-6">
                            {saveDraftError.userMessage}
                            <FinancialAidTechnicalSupportDetails report={saveDraftError} />
                        </SupportErrorMessage>
                    ) : null}

                    <footer className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                        {isParentStatusViewRequested && summary && canDownloadStatusViewApplicationCopy ? (
                            <Button
                                type="button"
                                variant="secondary"
                                icon={Download}
                                data-testid="parent-financial-aid-download-existing-copy"
                                disabled={generateApplicationReport.isPending || getReportDownloadUrl.isPending}
                                onClick={() => { void handleOpenStatusYstReport(summary); }}
                            >
                                {generateApplicationReport.isPending || getReportDownloadUrl.isPending ? 'Generating PDF...' : 'Download PDF'}
                            </Button>
                        ) : null}
                        {summary?.canResume && summaryMatchesLandingYear && !isInviteClaimPending && !isInviteEntryBlocked && !isParentFinancialAidLandingLookupPending && !isStatusViewApplicationDetailsPending && !isGenericInviteCycleClosed ? (
                            <Button
                                type="button"
                                variant="primary"
                                data-testid="parent-financial-aid-continue-application"
                                onClick={() => beginParentFinancialAidForm(summary)}
                            >
                                {existingApplicationState?.primaryAction === 'continue'
                                    ? existingApplicationState.primaryLabel
                                    : 'Continue Application'} <ChevronRight size={16} />
                            </Button>
                        ) : null}
                        {existingApplicationState?.primaryAction === 'view_status' && summary && summaryMatchesLandingYear && !isParentFinancialAidLandingLookupPending ? (
                            <Button
                                type="button"
                                variant="primary"
                                data-testid="parent-financial-aid-view-status"
                                onClick={() => handleViewApplicationStatus(summary)}
                            >
                                {existingApplicationState.primaryLabel} <ChevronRight size={16} />
                            </Button>
                        ) : null}
                        {canBeginCurrentYearApplication ? (
                            <Button
                                type="button"
                                variant="primary"
                                data-testid="parent-financial-aid-start-application"
                                onClick={() => beginParentFinancialAidForm(null)}
                            >
                                Start Application <ChevronRight size={16} />
                            </Button>
                        ) : null}
                        {existingApplicationState ? (
                            <Button
                                type="button"
                                variant="secondary"
                                data-testid="parent-financial-aid-return-home"
                                onClick={handleReturnToPortalHome}
                            >
                                {existingApplicationState.secondaryLabel}
                            </Button>
                        ) : null}
                    </footer>
                </Card>
            </section>
        );
    };

    // --- Steps ---

    const renderParentsStep = () => {
        const householdComposition = resolveParentGuardianHouseholdComposition(formData);
        const activeParentKeys = resolveParentGuardianActiveKeys(householdComposition);
        return (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
            <SectionHeader icon={User} title="Parent / Guardian Information" subtitle="Choose the household setup for this application, then complete the visible parent or guardian details." />
            <Card className="mb-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div data-testid="parent-financial-aid-household-mode">
                        <InputLabel>Household setup</InputLabel>
                        <Select
                            value={householdComposition.mode}
                            onChange={(value) => updateHouseholdComposition({
                                mode: value === 'single_parent_guardian' ? 'single_parent_guardian' : 'two_parent',
                                primaryKey: householdComposition.primaryKey,
                                primaryRole: householdComposition.primaryRole,
                            })}
                            options={[
                                { label: 'Two parents / guardians', value: 'two_parent' },
                                { label: 'Single parent / guardian', value: 'single_parent_guardian' },
                            ]}
                        />
                    </div>
                    {householdComposition.mode === 'single_parent_guardian' ? (
                        <>
                            <div data-testid="parent-financial-aid-household-primary-key">
                                <InputLabel>Who is completing this?</InputLabel>
                                <Select
                                    value={householdComposition.primaryKey}
                                    onChange={(value) => updateHouseholdComposition({
                                        primaryKey: value === 'mother' ? 'mother' : 'father',
                                        primaryRole: value === 'mother' ? 'mother' : 'father',
                                    })}
                                    options={[
                                        { label: 'Parent / guardian 1', value: 'father' },
                                        { label: 'Parent / guardian 2', value: 'mother' },
                                    ]}
                                />
                            </div>
                            <div data-testid="parent-financial-aid-household-primary-role">
                                <InputLabel>Relationship</InputLabel>
                                <Select
                                    value={householdComposition.primaryRole}
                                    onChange={(value) => updateHouseholdComposition({
                                        primaryRole: (PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS.some((option) => option.value === value)
                                            ? value
                                            : 'legal_guardian') as ParentGuardianDisplayRole,
                                    })}
                                    options={PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS}
                                />
                            </div>
                            {householdComposition.primaryRole === 'other' ? (
                                <div className="md:col-span-3">
                                    <InputLabel>Relationship description</InputLabel>
                                    <Input
                                        data-testid="parent-financial-aid-household-other-role-label"
                                        value={householdComposition.otherRoleLabel ?? ''}
                                        onChange={(event) => updateHouseholdComposition({ otherRoleLabel: event.target.value })}
                                    />
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
                <p className="mt-4 text-xs font-semibold text-irongrey/55">
                    Hidden parent or guardian fields stay saved if you switch back, but they will not be required or submitted for this application.
                </p>
            </Card>
            {activeParentKeys.map((type) => {
                const pType = type as ParentGuardianFormKey;
                const data = formData[pType];
                const init = initialDataRef.current[pType];
                const showParentRequired = shouldParentFinancialAidParentShowRequiredLabels(formData, pType);
                const parentLabel = resolveParentGuardianDisplayLabel(householdComposition, pType);
                return (
                    <Card key={type} className="mb-6">
                        <div className="mb-6 flex items-center gap-2 border-b border-gray-50 pb-2">
                            <span className="text-xs font-bold uppercase tracking-widest text-primary">{parentLabel}'s Information</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex gap-2">
                                <div className="w-24"><InputLabel>Title</InputLabel><Input value={data.title} onChange={e => update([type, 'title'], e.target.value)} className={getInputClass(data.title, init.title)} /></div>
                                <div className="flex-1">
                                    <InputLabel required={showParentRequired}>First Name</InputLabel>
                                    <Input
                                        data-testid={`parent-financial-aid-${type}-first-name`}
                                        value={data.firstName}
                                        onChange={e => update([type, 'firstName'], e.target.value)}
                                        className={getValidatedInputClass(`${type}.firstName`, getInputClass(data.firstName, init.firstName))}
                                        {...getValidationProps(`${type}.firstName`)}
                                    />
                                    {renderFieldError(`${type}.firstName`)}
                                </div>
                            </div>
                            <div>
                                <InputLabel required={showParentRequired}>Last Name</InputLabel>
                                <Input
                                    value={data.lastName}
                                    onChange={e => update([type, 'lastName'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.lastName`, getInputClass(data.lastName, init.lastName))}
                                    {...getValidationProps(`${type}.lastName`)}
                                />
                                {renderFieldError(`${type}.lastName`)}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {isConfiguredVisible(`${type}.occupation`) ? <div>
                                <InputLabel required={isConfiguredRequired(`${type}.occupation`)}>Occupation</InputLabel>
                                <Input
                                    data-testid={`parent-financial-aid-${type}-occupation`}
                                    value={data.occupation}
                                    onChange={e => update([type, 'occupation'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.occupation`, getInputClass(data.occupation, init.occupation))}
                                    {...getValidationProps(`${type}.occupation`)}
                                />
                                {renderFieldError(`${type}.occupation`)}
                            </div> : null}
                            {isConfiguredVisible(`${type}.employer`) ? <div>
                                <InputLabel required={isConfiguredRequired(`${type}.employer`)}>Employer</InputLabel>
                                <Input
                                    data-testid={`parent-financial-aid-${type}-employer`}
                                    value={data.employer}
                                    onChange={e => update([type, 'employer'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.employer`, getInputClass(data.employer, init.employer))}
                                    {...getValidationProps(`${type}.employer`)}
                                />
                                {renderFieldError(`${type}.employer`)}
                            </div> : null}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <InputLabel required={showParentRequired}>Cell Phone</InputLabel>
                                <Input
                                    type="tel"
                                    value={data.cellPhone}
                                    onChange={e => update([type, 'cellPhone'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.cellPhone`, getInputClass(data.cellPhone, init.cellPhone))}
                                    {...getValidationProps(`${type}.cellPhone`)}
                                />
                                {renderFieldError(`${type}.cellPhone`)}
                            </div>
                            <div className="md:col-span-2">
                                <InputLabel required={showParentRequired}>Email</InputLabel>
                                <Input
                                    value={data.email}
                                    onChange={e => update([type, 'email'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.email`, getInputClass(data.email, init.email))}
                                    {...getValidationProps(`${type}.email`)}
                                />
                                {renderFieldError(`${type}.email`)}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div className="md:col-span-2">
                                <InputLabel required={showParentRequired}>Home Address</InputLabel>
                                <Input
                                    value={data.address}
                                    onChange={e => update([type, 'address'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.address`, getInputClass(data.address, init.address))}
                                    {...getValidationProps(`${type}.address`)}
                                />
                                {renderFieldError(`${type}.address`)}
                            </div>
                            <div>
                                <InputLabel required={showParentRequired}>City</InputLabel>
                                <Input
                                    value={data.city}
                                    onChange={e => update([type, 'city'], e.target.value)}
                                    className={getValidatedInputClass(`${type}.city`, getInputClass(data.city, init.city))}
                                    {...getValidationProps(`${type}.city`)}
                                />
                                {renderFieldError(`${type}.city`)}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <InputLabel required={showParentRequired}>State</InputLabel>
                                    <Input
                                        value={data.state}
                                        onChange={e => update([type, 'state'], e.target.value)}
                                        className={getValidatedInputClass(`${type}.state`, getInputClass(data.state, init.state))}
                                        {...getValidationProps(`${type}.state`)}
                                    />
                                    {renderFieldError(`${type}.state`)}
                                </div>
                                <div>
                                    <InputLabel required={showParentRequired}>ZIP</InputLabel>
                                    <Input
                                        value={data.zip}
                                        onChange={e => update([type, 'zip'], e.target.value)}
                                        className={getValidatedInputClass(`${type}.zip`, getInputClass(data.zip, init.zip))}
                                        {...getValidationProps(`${type}.zip`)}
                                    />
                                    {renderFieldError(`${type}.zip`)}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div><InputLabel>Shul</InputLabel><Input value={data.shul} onChange={e => update([type, 'shul'], e.target.value)} className={getInputClass(data.shul, init.shul)} /></div>
                            <div><InputLabel>Rabbi</InputLabel><Input value={data.rabbi} onChange={e => update([type, 'rabbi'], e.target.value)} className={getInputClass(data.rabbi, init.rabbi)} /></div>
                        </div>
                        <div className="mb-4">
                            <InputLabel>Social Security Number</InputLabel>
                            <div className="relative">
                                <Input
                                    value={data.ssn}
                                    type={isSsnVisible[pType] ? 'text' : 'password'}
                                    onChange={e => {
                                        const formattedSsn = formatFinancialAidSocialSecurityNumberInput(e.target.value);
                                        update([type, 'ssn'], formattedSsn);
                                        if (isValidFinancialAidSocialSecurityNumber(formattedSsn)) {
                                            update([type, 'ssnLast4ClearRequested'], false);
                                        }
                                    }}
                                    inputMode="numeric"
                                    className={getValidatedInputClass(`${type}.ssn`, `${getInputClass(data.ssn, init.ssn)} !pr-10`)}
                                    {...getValidationProps(`${type}.ssn`)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setIsSsnVisible(prev => ({ ...prev, [pType]: !prev[pType] }))}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/50 hover:text-primary transition-colors"
                                >
                                    {isSsnVisible[pType] ? <EyeClosed size={16} /> : <Eye size={16} />}
                                    <span className="sr-only">
                                        {isSsnVisible[pType] ? 'Hide social security number' : 'Show social security number'}
                                    </span>
                                </button>
                            </div>
                            {renderFieldError(`${type}.ssn`)}
                            {data.ssnLast4OnFile && !data.ssn && (
                                <div
                                    className="mt-2 flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-irongrey/70 sm:flex-row sm:items-center sm:justify-between"
                                    data-testid={`parent-financial-aid-${type}-ssn-preserved-hint`}
                                >
                                    <span>
                                        {data.ssnLast4ClearRequested
                                            ? 'Saved SSN will be removed when you save or submit.'
                                            : `An SSN ending in ${data.ssnLast4OnFile} is already saved. Leave this blank to keep it, or enter the full SSN to replace it.`}
                                    </span>
                                    <button
                                        type="button"
                                        className="self-start text-primary underline underline-offset-2 hover:text-primary sm:self-auto"
                                        onClick={() => update([type, 'ssnLast4ClearRequested'], !data.ssnLast4ClearRequested)}
                                    >
                                        {data.ssnLast4ClearRequested ? 'Keep saved SSN' : 'Remove saved SSN'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={data.isUnemployed}
                                        onChange={e => update([type, 'isUnemployed'], e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                    />
                                    <span className="text-sm font-semibold text-irongrey">Currently Unemployed?</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={data.isDivorced}
                                        onChange={e => update([type, 'isDivorced'], e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                    />
                                    <span className="text-sm font-semibold text-irongrey">Divorced?</span>
                                </label>
                            </div>
                            {data.isUnemployed && (
                                <div className="animate-in slide-in-from-top-2">
                                    <InputLabel required>Unemployment Benefits Amount ($)</InputLabel>
                                    <CurrencyInput
                                        value={data.unemploymentBenefits}
                                        onChange={e => update([type, 'unemploymentBenefits'], e.target.value)}
                                        className={getValidatedInputClass(`${type}.unemploymentBenefits`)}
                                        {...getValidationProps(`${type}.unemploymentBenefits`)}
                                    />
                                    {renderFieldError(`${type}.unemploymentBenefits`)}
                                </div>
                            )}
                        </div>
                    </Card>
                );
            })}
        </div>
        );
    };

    const renderIncomeStep = () => {
        const householdComposition = resolveParentGuardianHouseholdComposition(formData);
        const activeIncomeParents: Array<{
            parentKey: ParentGuardianFormKey;
            label: string;
            incomeKey: 'incomeFather' | 'incomeMother';
        }> = resolveParentGuardianActiveKeys(householdComposition).map((parentKey) => ({
            parentKey,
            label: resolveParentGuardianDisplayLabel(householdComposition, parentKey),
            incomeKey: parentKey === 'father' ? 'incomeFather' : 'incomeMother',
        }));
        const calcNet = (i: IncomeInfo) => {
            const gross = cleanNumber(i.gross2024);
            const parsonage = cleanNumber(i.parsonage);
            const taxes = cleanNumber(i.fedTax) + cleanNumber(i.stateTax) + cleanNumber(i.ficaTax);
            const refunds = cleanNumber(i.refunds);
            return (gross + parsonage - taxes + refunds).toLocaleString();
        };

        return (
            <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
                <SectionHeader
                    icon={DollarSign}
                    title="Income Information"
                    subtitle={financialAidIncomeSubtitle(financialAidDisplayYears.actualYear, financialAidDisplayYears.estimateYear)}
                />
                <div className="mb-4 rounded-xl border border-primary/10 bg-primary/5 px-4 py-3 text-sm font-semibold text-irongrey/70">
                    <p id={householdGrossIncomeDescriptionId}>
                        At least one parent gross income amount is required. Enter 0 if there was no income.
                    </p>
                    {renderFieldError('income.householdGross2024')}
                </div>

                <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" aria-describedby={householdGrossIncomeDescriptionId}>
                    <legend className="sr-only">Parent gross income</legend>
                    {activeIncomeParents.map(({ parentKey, label: p, incomeKey }) => {
                        const key = incomeKey;
                        const data = (formData as any)[key];
                        const grossValidationKey = resolveIncomeGrossDisplayValidationKey(key);
                        return (
                            <Card key={parentKey}>
                                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4">{p}'s Income</div>
                                <div className="space-y-4">
                                    {isConfiguredVisible(`${key}.gross2024`) ? <div>
                                        <InputLabel required={isConfiguredRequired(`${key}.gross2024`)}>{financialAidYearLabel('Gross Income', financialAidDisplayYears.actualYear)}</InputLabel>
                                        <CurrencyInput
                                            data-testid={`parent-financial-aid-${key}-gross`}
                                            value={data.gross2024}
                                            onChange={e => update([key, 'gross2024'], e.target.value)}
                                            className={getValidatedInputClass(grossValidationKey)}
                                            {...getIncomeGrossValidationProps(key)}
                                        />
                                        {grossValidationKey === 'income.householdGross2024' ? null : renderFieldError(grossValidationKey)}
                                    </div> : null}
                                    {isConfiguredVisible(`${key}.parsonage`) ? <div>
                                        <InputLabel required={isConfiguredRequired(`${key}.parsonage`)}>Parsonage</InputLabel>
                                        <CurrencyInput
                                            data-testid={`parent-financial-aid-${key}-parsonage`}
                                            value={data.parsonage}
                                            onChange={e => update([key, 'parsonage'], e.target.value)}
                                            className={getValidatedInputClass(`${key}.parsonage`)}
                                            {...getValidationProps(`${key}.parsonage`)}
                                        />
                                        {renderFieldError(`${key}.parsonage`)}
                                    </div> : null}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        {isConfiguredVisible(`${key}.fedTax`) ? <div>
                                            <InputLabel required={isConfiguredRequired(`${key}.fedTax`)}>Federal Tax Withheld</InputLabel>
                                            <CurrencyInput
                                                data-testid={`parent-financial-aid-${key}-fed-tax`}
                                                value={data.fedTax}
                                                onChange={e => update([key, 'fedTax'], e.target.value)}
                                                className={getValidatedInputClass(`${key}.fedTax`)}
                                                {...getValidationProps(`${key}.fedTax`)}
                                            />
                                            {renderFieldError(`${key}.fedTax`)}
                                        </div> : null}
                                        {isConfiguredVisible(`${key}.stateTax`) ? <div>
                                            <InputLabel required={isConfiguredRequired(`${key}.stateTax`)}>State Tax Withheld</InputLabel>
                                            <CurrencyInput
                                                data-testid={`parent-financial-aid-${key}-state-tax`}
                                                value={data.stateTax}
                                                onChange={e => update([key, 'stateTax'], e.target.value)}
                                                className={getValidatedInputClass(`${key}.stateTax`)}
                                                {...getValidationProps(`${key}.stateTax`)}
                                            />
                                            {renderFieldError(`${key}.stateTax`)}
                                        </div> : null}
                                        {isConfiguredVisible(`${key}.ficaTax`) ? <div>
                                            <InputLabel required={isConfiguredRequired(`${key}.ficaTax`)}>FICA Withheld</InputLabel>
                                            <CurrencyInput
                                                data-testid={`parent-financial-aid-${key}-fica-tax`}
                                                value={data.ficaTax}
                                                onChange={e => update([key, 'ficaTax'], e.target.value)}
                                                className={getValidatedInputClass(`${key}.ficaTax`)}
                                                {...getValidationProps(`${key}.ficaTax`)}
                                            />
                                            {renderFieldError(`${key}.ficaTax`)}
                                        </div> : null}
                                    </div>
                                    {isConfiguredVisible(`${key}.refunds`) ? <div>
                                        <InputLabel required={isConfiguredRequired(`${key}.refunds`)}>Tax Refunds / Credits</InputLabel>
                                        <CurrencyInput
                                            data-testid={`parent-financial-aid-${key}-refunds`}
                                            value={data.refunds}
                                            onChange={e => update([key, 'refunds'], e.target.value)}
                                            className={getValidatedInputClass(`${key}.refunds`)}
                                            {...getValidationProps(`${key}.refunds`)}
                                        />
                                        {renderFieldError(`${key}.refunds`)}
                                    </div> : null}
                                    <div className="bg-jungle/5 p-3 rounded-lg border border-jungle/10 flex justify-between items-center">
                                        <span className="text-xs font-bold text-jungle">NET INCOME</span>
                                        <span className="font-bold text-jungle">${calcNet(data)}</span>
                                    </div>
                                    {isConfiguredVisible(`${key}.estimated2025`) ? <div className="pt-4 border-t border-gray-100">
                                        <InputLabel required={isConfiguredRequired(`${key}.estimated2025`)}>{financialAidYearLabel('Estimated Income', financialAidDisplayYears.estimateYear)}</InputLabel>
                                        <CurrencyInput
                                            data-testid={`parent-financial-aid-${key}-estimated`}
                                            value={data.estimated2025}
                                            onChange={e => update([key, 'estimated2025'], e.target.value)}
                                            className={getValidatedInputClass(`${key}.estimated2025`)}
                                            {...getValidationProps(`${key}.estimated2025`)}
                                        />
                                        {renderFieldError(`${key}.estimated2025`)}
                                    </div> : null}
                                </div>
                            </Card>
                        )
                    })}
                </fieldset>

                <Card>
                    <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Company Benefits & Gifts</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {([
                            { fieldKey: 'benefits.healthInsurance', dataKey: 'healthInsurance', label: 'Employer Paid Health Insurance' },
                            { fieldKey: 'benefits.tuition', dataKey: 'tuition', label: 'Employer Paid Tuition' },
                            { fieldKey: 'benefits.auto', dataKey: 'auto', label: 'Auto Allowance' },
                            { fieldKey: 'benefits.other', dataKey: 'other', label: 'Other Benefits' },
                        ] as const).map(({ fieldKey, dataKey, label }) => (
                            isConfiguredVisible(fieldKey) ? <div key={fieldKey}>
                                <InputLabel required={isConfiguredRequired(fieldKey)}>{label}</InputLabel>
                                <CurrencyInput
                                    data-testid={`parent-financial-aid-${fieldKey.replace('.', '-')}`}
                                    value={formData.benefits[dataKey]}
                                    onChange={e => update(['benefits', dataKey], e.target.value)}
                                    className={getValidatedInputClass(fieldKey)}
                                    {...getValidationProps(fieldKey)}
                                />
                                {renderFieldError(fieldKey)}
                            </div> : null
                        ))}
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.benefits.gifts}
                            onChange={e => update(['benefits', 'gifts'], e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                        />
                        <span className="text-sm font-semibold text-irongrey">Do you expect gifts/funding from parents or family members &gt; $2,500?</span>
                    </label>
                    {formData.benefits.gifts && (
                        <div className="mt-3 w-48">
                            <InputLabel required>Amount</InputLabel>
                            <CurrencyInput
                                value={formData.benefits.giftsAmount}
                                onChange={e => update(['benefits', 'giftsAmount'], e.target.value)}
                                className={getValidatedInputClass('benefits.giftsAmount')}
                                {...getValidationProps('benefits.giftsAmount')}
                            />
                            {renderFieldError('benefits.giftsAmount')}
                        </div>
                    )}
                </Card>
            </div>
        );
    };

    const renderAssetsStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500 space-y-6">
            <SectionHeader icon={Home} title="Assets" subtitle="Real Estate, Vehicles, and Accounts." />

            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary">Home Asset</div>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        <button onClick={() => update(['assets', 'housingType'], 'own')} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${formData.assets.housingType === 'own' ? 'bg-white shadow-sm text-primary' : 'text-irongrey/50'}`}>OWN</button>
                        <button onClick={() => update(['assets', 'housingType'], 'rent')} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${formData.assets.housingType === 'rent' ? 'bg-white shadow-sm text-primary' : 'text-irongrey/50'}`}>RENT</button>
                    </div>
                </div>
                {formData.assets.housingType === 'own' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><InputLabel required>Purchase Price</InputLabel><CurrencyInput value={formData.assets.purchasePrice} onChange={e => update(['assets', 'purchasePrice'], e.target.value)} className={getValidatedInputClass('assets.purchasePrice')} {...getValidationProps('assets.purchasePrice')} />{renderFieldError('assets.purchasePrice')}</div>
                        <div><InputLabel>Date of Purchase</InputLabel><Input type="date" value={formData.assets.purchaseDate} onChange={e => update(['assets', 'purchaseDate'], e.target.value)} /></div>
                        <div><InputLabel>Mortgage Balance</InputLabel><CurrencyInput value={formData.assets.mortgageBalance} onChange={e => update(['assets', 'mortgageBalance'], e.target.value)} className={getValidatedInputClass('assets.mortgageBalance')} {...getValidationProps('assets.mortgageBalance')} />{renderFieldError('assets.mortgageBalance')}</div>
                        <div><InputLabel required>Monthly Payment</InputLabel><CurrencyInput value={formData.assets.mortgagePayment} onChange={e => update(['assets', 'mortgagePayment'], e.target.value)} className={getValidatedInputClass('assets.mortgagePayment')} {...getValidationProps('assets.mortgagePayment')} />{renderFieldError('assets.mortgagePayment')}</div>
                        {isConfiguredVisible('assets.mortgageYears') ? <div><InputLabel required={isConfiguredRequired('assets.mortgageYears')}>Total Years of Mortgage</InputLabel><Input type="number" min="0" max="100" step="1" value={formData.assets.mortgageYears} onChange={e => update(['assets', 'mortgageYears'], e.target.value)} className={getValidatedInputClass('assets.mortgageYears')} {...getValidationProps('assets.mortgageYears')} />{renderFieldError('assets.mortgageYears')}</div> : null}
                        <div><InputLabel>Property Taxes (Annual)</InputLabel><CurrencyInput value={formData.assets.propertyTaxes} onChange={e => update(['assets', 'propertyTaxes'], e.target.value)} className={getValidatedInputClass('assets.propertyTaxes')} {...getValidationProps('assets.propertyTaxes')} />{renderFieldError('assets.propertyTaxes')}</div>
                        <div><InputLabel>Interest Rate (%)</InputLabel><Input type="number" value={formData.assets.interestRate} onChange={e => update(['assets', 'interestRate'], e.target.value)} className={getValidatedInputClass('assets.interestRate')} {...getValidationProps('assets.interestRate')} />{renderFieldError('assets.interestRate')}</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><InputLabel required>Monthly Rent</InputLabel><CurrencyInput value={formData.assets.rentPayment} onChange={e => update(['assets', 'rentPayment'], e.target.value)} className={getValidatedInputClass('assets.rentPayment')} {...getValidationProps('assets.rentPayment')} />{renderFieldError('assets.rentPayment')}</div>
                        <div><InputLabel>Monthly Utilities</InputLabel><CurrencyInput value={formData.assets.utilities} onChange={e => update(['assets', 'utilities'], e.target.value)} className={getValidatedInputClass('assets.utilities')} {...getValidationProps('assets.utilities')} />{renderFieldError('assets.utilities')}</div>
                    </div>
                )}
            </Card>

            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary">Vehicles</div>
                    <Button variant="secondary" onClick={() => addRow('vehicles')} icon={Plus} className="!py-1.5 !px-3 !text-xs">Add Vehicle</Button>
                </div>
                {formData.vehicles.map((v, i) => (
                    <div key={v.id} className="bg-gray-50 rounded-xl p-4 mb-4 relative">
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                            <div><InputLabel>Year</InputLabel><Input value={v.year} onChange={e => updateRow('vehicles', v.id, 'year', e.target.value)} /></div>
                            <div className="col-span-2"><InputLabel>Make & Model</InputLabel><Input value={v.make} placeholder="e.g. Honda Odyssey" onChange={e => updateRow('vehicles', v.id, 'make', e.target.value)} /></div>
                            {(() => {
                                const vehicleValueKey = getArrayFieldValidationKey('vehicles', i, 'value');
                                return (
                                    <div>
                                        <InputLabel>Value</InputLabel>
                                        <CurrencyInput
                                            value={v.value}
                                            onChange={e => updateRow('vehicles', v.id, 'value', e.target.value)}
                                            className={getValidatedInputClass(vehicleValueKey)}
                                            {...getValidationProps(vehicleValueKey)}
                                        />
                                        {renderFieldError(vehicleValueKey)}
                                    </div>
                                );
                            })()}
                            {(() => {
                                const vehicleDebtKey = getArrayFieldValidationKey('vehicles', i, 'debt');
                                return (
                                    <div>
                                        <InputLabel>Debt</InputLabel>
                                        <CurrencyInput
                                            value={v.debt}
                                            onChange={e => updateRow('vehicles', v.id, 'debt', e.target.value)}
                                            className={getValidatedInputClass(vehicleDebtKey)}
                                            {...getValidationProps(vehicleDebtKey)}
                                        />
                                        {renderFieldError(vehicleDebtKey)}
                                    </div>
                                );
                            })()}
                            {(() => {
                                const vehiclePaymentKey = getArrayFieldValidationKey('vehicles', i, 'payment');
                                return (
                                    <div>
                                        <InputLabel>Payment</InputLabel>
                                        <CurrencyInput
                                            value={v.payment}
                                            onChange={e => updateRow('vehicles', v.id, 'payment', e.target.value)}
                                            className={getValidatedInputClass(vehiclePaymentKey)}
                                            {...getValidationProps(vehiclePaymentKey)}
                                        />
                                        {renderFieldError(vehiclePaymentKey)}
                                    </div>
                                );
                            })()}
                        </div>
                        <button onClick={() => removeRow('vehicles', v.id)} className="absolute top-2 right-2 text-irongrey/30 hover:text-strawberry"><Trash2 size={16} /></button>
                    </div>
                ))}
            </Card>

            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-primary">Investments</div>
                        <p className="mt-1 max-w-2xl text-xs text-irongrey/60">
                            List any vacation homes, rentals, investment properties, stocks, bonds, savings accounts or money markets.
                        </p>
                    </div>
                    <Button variant="secondary" onClick={() => addRow('investments')} icon={Plus} className="!py-1.5 !px-3 !text-xs">Add Investment</Button>
                </div>
                {formData.investments.map((investment, i) => {
                    const investmentValueKey = getArrayFieldValidationKey('investments', i, 'value');
                    return (
                        <div key={investment.id} className="mb-3 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 md:grid-cols-[1fr_12rem_2.75rem] md:items-start">
                            <div>
                                <InputLabel>Description</InputLabel>
                                <Input
                                    value={investment.description}
                                    onChange={e => updateRow('investments', investment.id, 'description', e.target.value)}
                                />
                            </div>
                            <div>
                                <InputLabel>Value</InputLabel>
                                <CurrencyInput
                                    value={investment.value}
                                    onChange={e => updateRow('investments', investment.id, 'value', e.target.value)}
                                    className={getValidatedInputClass(investmentValueKey)}
                                    data-testid={`parent-financial-aid-investment-value-${i}`}
                                    {...getValidationProps(investmentValueKey)}
                                />
                                {renderFieldError(investmentValueKey)}
                            </div>
                            <button
                                type="button"
                                onClick={() => removeRow('investments', investment.id)}
                                className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-strawberry/5 text-strawberry hover:bg-strawberry/10 transition-colors md:mt-5"
                                aria-label="Remove investment"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    );
                })}
            </Card>

            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary">Children's Assets</div>
                    <Button variant="secondary" onClick={() => addRow('childrenAssets')} icon={Plus} className="!py-1.5 !px-3 !text-xs">Add Asset</Button>
                </div>
                {formData.childrenAssets.map((asset, i) => {
                    const childAssetValueKey = getArrayFieldValidationKey('childrenAssets', i, 'value');
                    return (
                        <div key={asset.id} className="mb-3 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 md:grid-cols-[1fr_12rem_2.75rem] md:items-start">
                            <div>
                                <InputLabel>Description</InputLabel>
                                <Input
                                    value={asset.description}
                                    onChange={e => updateRow('childrenAssets', asset.id, 'description', e.target.value)}
                                />
                            </div>
                            <div>
                                <InputLabel>Value</InputLabel>
                                <CurrencyInput
                                    value={asset.value}
                                    onChange={e => updateRow('childrenAssets', asset.id, 'value', e.target.value)}
                                    className={getValidatedInputClass(childAssetValueKey)}
                                    data-testid={`parent-financial-aid-child-asset-value-${i}`}
                                    {...getValidationProps(childAssetValueKey)}
                                />
                                {renderFieldError(childAssetValueKey)}
                            </div>
                            <button
                                type="button"
                                onClick={() => removeRow('childrenAssets', asset.id)}
                                className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-strawberry/5 text-strawberry hover:bg-strawberry/10 transition-colors md:mt-5"
                                aria-label="Remove child asset"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    );
                })}
            </Card>

            <Card>
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Bank Accounts</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                        <div className="font-bold text-irongrey text-sm">Savings</div>
                        <InputLabel>Bank Name</InputLabel><Input value={formData.savings.bank} onChange={e => update(['savings', 'bank'], e.target.value)} />
                        <InputLabel>Account #</InputLabel><Input value={formData.savings.accountNumber} onChange={e => update(['savings', 'accountNumber'], e.target.value)} />
                        <InputLabel required>Balance</InputLabel><CurrencyInput value={formData.savings.balance} onChange={e => update(['savings', 'balance'], e.target.value)} className={getValidatedInputClass('savings.balance')} {...getValidationProps('savings.balance')} />{renderFieldError('savings.balance')}
                    </div>
                    <div className="space-y-3">
                        <div className="font-bold text-irongrey text-sm">Checking</div>
                        <InputLabel>Bank Name</InputLabel><Input value={formData.checking.bank} onChange={e => update(['checking', 'bank'], e.target.value)} />
                        <InputLabel>Account #</InputLabel><Input value={formData.checking.accountNumber} onChange={e => update(['checking', 'accountNumber'], e.target.value)} />
                        <InputLabel required>Balance</InputLabel><CurrencyInput value={formData.checking.balance} onChange={e => update(['checking', 'balance'], e.target.value)} className={getValidatedInputClass('checking.balance')} {...getValidationProps('checking.balance')} />{renderFieldError('checking.balance')}
                    </div>
                </div>
            </Card>
        </div>
    );

    const renderExpensesStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
            <SectionHeader icon={TrendingUp} title="Monthly Expenses" subtitle="Enter your average monthly household expenses." />
            <Card>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div><InputLabel required>Housing</InputLabel><CurrencyInput value={formData.expenses.housing} onChange={e => update(['expenses', 'housing'], e.target.value)} className={getValidatedInputClass('expenses.housing')} {...getValidationProps('expenses.housing')} />{renderFieldError('expenses.housing')}</div>
                            <div className="flex items-center pt-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.expenses.taxesIncluded}
                                        onChange={e => update(['expenses', 'taxesIncluded'], e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                    />
                                    <span className="text-sm font-semibold text-irongrey">Taxes included?</span>
                                </label>
                            </div>
                    <div><InputLabel required>Utilities</InputLabel><CurrencyInput value={formData.expenses.utilities} onChange={e => update(['expenses', 'utilities'], e.target.value)} className={getValidatedInputClass('expenses.utilities')} {...getValidationProps('expenses.utilities')} />{renderFieldError('expenses.utilities')}</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div><InputLabel required>Food</InputLabel><CurrencyInput value={formData.expenses.food} onChange={e => update(['expenses', 'food'], e.target.value)} className={getValidatedInputClass('expenses.food')} {...getValidationProps('expenses.food')} />{renderFieldError('expenses.food')}</div>
                    {isConfiguredVisible('expenses.studentLoans') ? <div><InputLabel required={isConfiguredRequired('expenses.studentLoans')}>Student Loans</InputLabel><CurrencyInput value={formData.expenses.studentLoans} onChange={e => update(['expenses', 'studentLoans'], e.target.value)} className={getValidatedInputClass('expenses.studentLoans')} {...getValidationProps('expenses.studentLoans')} />{renderFieldError('expenses.studentLoans')}</div> : null}
                    {isConfiguredVisible('expenses.carInsurance') ? <div><InputLabel required={isConfiguredRequired('expenses.carInsurance')}>Car Insurance</InputLabel><CurrencyInput value={formData.expenses.carInsurance} onChange={e => update(['expenses', 'carInsurance'], e.target.value)} className={getValidatedInputClass('expenses.carInsurance')} {...getValidationProps('expenses.carInsurance')} />{renderFieldError('expenses.carInsurance')}</div> : null}
                    {isConfiguredVisible('expenses.healthInsurance') ? <div><InputLabel required={isConfiguredRequired('expenses.healthInsurance')}>Health Insurance</InputLabel><CurrencyInput value={formData.expenses.healthInsurance} onChange={e => update(['expenses', 'healthInsurance'], e.target.value)} className={getValidatedInputClass('expenses.healthInsurance')} {...getValidationProps('expenses.healthInsurance')} />{renderFieldError('expenses.healthInsurance')}</div> : null}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {isConfiguredVisible('expenses.lifeInsurance') ? <div><InputLabel required={isConfiguredRequired('expenses.lifeInsurance')}>Life Insurance</InputLabel><CurrencyInput value={formData.expenses.lifeInsurance} onChange={e => update(['expenses', 'lifeInsurance'], e.target.value)} className={getValidatedInputClass('expenses.lifeInsurance')} {...getValidationProps('expenses.lifeInsurance')} />{renderFieldError('expenses.lifeInsurance')}</div> : null}
                    {isConfiguredVisible('expenses.shulDues') ? <div><InputLabel required={isConfiguredRequired('expenses.shulDues')}>Shul Dues</InputLabel><CurrencyInput value={formData.expenses.shulDues} onChange={e => update(['expenses', 'shulDues'], e.target.value)} className={getValidatedInputClass('expenses.shulDues')} {...getValidationProps('expenses.shulDues')} />{renderFieldError('expenses.shulDues')}</div> : null}
                    {isConfiguredVisible('expenses.cleaningHelp') ? <div><InputLabel required={isConfiguredRequired('expenses.cleaningHelp')}>Cleaning Help</InputLabel><CurrencyInput value={formData.expenses.cleaningHelp} onChange={e => update(['expenses', 'cleaningHelp'], e.target.value)} className={getValidatedInputClass('expenses.cleaningHelp')} {...getValidationProps('expenses.cleaningHelp')} />{renderFieldError('expenses.cleaningHelp')}</div> : null}
                    {isConfiguredVisible('expenses.other') ? <div><InputLabel required={isConfiguredRequired('expenses.other')}>Other</InputLabel><CurrencyInput data-testid="parent-financial-aid-expenses-other" value={formData.expenses.other} onChange={e => update(['expenses', 'other'], e.target.value)} className={getValidatedInputClass('expenses.other')} {...getValidationProps('expenses.other')} />{renderFieldError('expenses.other')}</div> : null}
                </div>
                {isConfiguredVisible('expenses.medicalCoverage') ? <div>
                    <InputLabel required={isConfiguredRequired('expenses.medicalCoverage')}>What type of medical coverage do you have?</InputLabel>
                    <Input
                        value={formData.expenses.medicalCoverage}
                        onChange={e => update(['expenses', 'medicalCoverage'], e.target.value)}
                        className={getValidatedInputClass('expenses.medicalCoverage')}
                        {...getValidationProps('expenses.medicalCoverage')}
                    />
                    {renderFieldError('expenses.medicalCoverage')}
                </div> : null}
            </Card>
        </div>
    );

    const renderSiblingsStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
            <SectionHeader icon={Baby} title="Other Children" subtitle="Children not enrolled at this school." />
            <Card>
                <p className="mb-5 rounded-lg border border-primary/15 bg-primary/5 px-4 py-3 text-sm font-semibold text-irongrey">
                    Children not enrolled at this school
                </p>
                {formData.otherChildren.map((c, i) => (
                    <div key={c.id} className="flex flex-wrap md:flex-nowrap gap-3 items-end border-b border-gray-100 pb-6 mb-6 md:pb-4 md:mb-4 last:border-0 last:pb-0">
                        {/* Name - Wide - Order 1 */}
                        <div className="order-1 w-[calc(65%-0.4rem)] md:w-auto md:flex-1">
                            <InputLabel>Name</InputLabel>
                            <Input value={c.name} onChange={e => updateRow('otherChildren', c.id, 'name', e.target.value)} />
                        </div>
                        {/* Age - Narrow - Order 2 */}
                        <div className="order-2 w-[calc(35%-0.4rem)] md:w-20">
                            <InputLabel>Age</InputLabel>
                            <Input value={c.age} onChange={e => updateRow('otherChildren', c.id, 'age', e.target.value)} />
                        </div>

                        {/* Grade - Narrow - Order 3 */}
                        <div className="order-3 w-[calc(35%-0.4rem)] md:w-24">
                            <InputLabel>Grade</InputLabel>
                            <Input value={c.grade} onChange={e => updateRow('otherChildren', c.id, 'grade', e.target.value)} />
                        </div>
                        {/* School - Wide - Order 4 */}
                        <div className="order-4 w-[calc(65%-0.4rem)] md:w-auto md:flex-1">
                            <InputLabel>School</InputLabel>
                            <Input value={c.school} onChange={e => updateRow('otherChildren', c.id, 'school', e.target.value)} />
                        </div>

                        {/* Tuition - 50% - Order 5 */}
                        <div className="order-5 w-[calc(50%-0.4rem)] md:w-32">
                            <InputLabel>Full Tuition</InputLabel>
                            {(() => {
                                const tuitionKey = getArrayFieldValidationKey('otherChildren', i, 'fullTuition');
                                return (
                                    <>
                                        <CurrencyInput
                                            value={c.fullTuition}
                                            onChange={e => updateRow('otherChildren', c.id, 'fullTuition', e.target.value)}
                                            className={getValidatedInputClass(tuitionKey)}
                                            {...getValidationProps(tuitionKey)}
                                        />
                                        {renderFieldError(tuitionKey)}
                                    </>
                                );
                            })()}
                        </div>
                        {/* Scholarship - 50% - Order 6 */}
                        <div className="order-6 w-[calc(50%-0.4rem)] md:w-32">
                            <InputLabel>Scholarship</InputLabel>
                            {(() => {
                                const scholarshipKey = getArrayFieldValidationKey('otherChildren', i, 'scholarship');
                                return (
                                    <>
                                        <CurrencyInput
                                            value={c.scholarship}
                                            onChange={e => updateRow('otherChildren', c.id, 'scholarship', e.target.value)}
                                            className={getValidatedInputClass(scholarshipKey)}
                                            {...getValidationProps(scholarshipKey)}
                                        />
                                        {renderFieldError(scholarshipKey)}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Trash - Order 7 */}
                        <div className="order-7 w-full md:w-auto flex justify-end md:block mt-2 md:mt-0">
                            <button onClick={() => removeRow('otherChildren', c.id)} className="h-[42px] w-[42px] flex items-center justify-center rounded-xl bg-strawberry/5 text-strawberry hover:bg-strawberry/10 transition-colors">
                                <Trash2 size={18}/>
                            </button>
                        </div>
                    </div>
                ))}
                <Button variant="secondary" onClick={() => addRow('otherChildren')} icon={Plus} className="w-full mt-4 border-dashed">Add Child</Button>
            </Card>
        </div>
    );

    const renderLifestyleStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500 space-y-6">
            <SectionHeader icon={CreditCard} title="Lifestyle & Expenditures" />
            <Card>
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Major Expenditures</div>
                <div className="space-y-4">
                    <div>
                        {isConfiguredVisible('majorExp.homeImprovementsDetails') ? <>
                            <InputLabel required={isConfiguredRequired('majorExp.homeImprovementsDetails')}>Have you made any home improvements above $5000 over the past three years or planned in the next 12 months? If so please describe.</InputLabel>
                            <textarea
                                className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm"
                                rows={2}
                                data-testid="parent-financial-aid-home-improvements-details"
                                value={formData.majorExp.homeImprovementsDetails}
                                onChange={e => update(['majorExp', 'homeImprovementsDetails'], e.target.value)}
                            />
                            {renderFieldError('majorExp.homeImprovementsDetails')}
                        </> : null}
                        {isConfiguredVisible('majorExp.homeImprovements') ? <div className="mt-3">
                            <InputLabel required={isConfiguredRequired('majorExp.homeImprovements')}>Home improvement amount</InputLabel>
                            <CurrencyInput
                                data-testid="parent-financial-aid-home-improvements-amount"
                                value={formData.majorExp.homeImprovements}
                                onChange={e => update(['majorExp', 'homeImprovements'], e.target.value)}
                                className={getValidatedInputClass('majorExp.homeImprovements')}
                                {...getValidationProps('majorExp.homeImprovements')}
                            />
                            {renderFieldError('majorExp.homeImprovements')}
                        </div> : null}
                    </div>
                    <div>
                        {isConfiguredVisible('majorExp.largePurchasesDetails') ? <>
                            <InputLabel required={isConfiguredRequired('majorExp.largePurchasesDetails')}>{financialAidLargePurchasesLabel(financialAidDisplayYears.actualYear, financialAidDisplayYears.estimateYear)}</InputLabel>
                            <textarea
                                className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm"
                                rows={2}
                                data-testid="parent-financial-aid-large-purchases-details"
                                value={formData.majorExp.largePurchasesDetails}
                                onChange={e => update(['majorExp', 'largePurchasesDetails'], e.target.value)}
                            />
                            {renderFieldError('majorExp.largePurchasesDetails')}
                        </> : null}
                        {isConfiguredVisible('majorExp.largePurchases') ? <div className="mt-3">
                            <InputLabel required={isConfiguredRequired('majorExp.largePurchases')}>Purchase or expenditure amount</InputLabel>
                            <CurrencyInput
                                data-testid="parent-financial-aid-large-purchases-amount"
                                value={formData.majorExp.largePurchases}
                                onChange={e => update(['majorExp', 'largePurchases'], e.target.value)}
                                className={getValidatedInputClass('majorExp.largePurchases')}
                                {...getValidationProps('majorExp.largePurchases')}
                            />
                            {renderFieldError('majorExp.largePurchases')}
                        </div> : null}
                    </div>
                    <div className="flex gap-4">
                        {isConfiguredVisible('majorExp.simchaType') ? <div className="flex-1">
                            <InputLabel required={isConfiguredRequired('majorExp.simchaType')}>Are you planning on making a bar/bas mitzvah and/or wedding this year?</InputLabel>
                            <Input
                                data-testid="parent-financial-aid-simcha-type"
                                value={formData.majorExp.simchaType}
                                onChange={e => update(['majorExp', 'simchaType'], e.target.value)}
                                className={getValidatedInputClass('majorExp.simchaType')}
                                {...getValidationProps('majorExp.simchaType')}
                            />
                            {renderFieldError('majorExp.simchaType')}
                        </div> : null}
                        {isConfiguredVisible('majorExp.simchaCost') ? <div className="w-1/3">
                            <InputLabel required={isConfiguredRequired('majorExp.simchaCost')}>Approximate cost.</InputLabel>
                            <CurrencyInput
                                data-testid="parent-financial-aid-simcha-cost"
                                value={formData.majorExp.simchaCost}
                                onChange={e => update(['majorExp', 'simchaCost'], e.target.value)}
                                className={getValidatedInputClass('majorExp.simchaCost')}
                                {...getValidationProps('majorExp.simchaCost')}
                            />
                            {renderFieldError('majorExp.simchaCost')}
                        </div> : null}
                    </div>
                    {isConfiguredVisible('majorExp.loanAmount') ? <div>
                        <InputLabel required={isConfiguredRequired('majorExp.loanAmount')}>Amount of scholarship you would be willing to accept as an interest-free loan</InputLabel>
                        <CurrencyInput
                            data-testid="parent-financial-aid-loan-amount"
                            value={formData.majorExp.loanAmount}
                            onChange={e => update(['majorExp', 'loanAmount'], e.target.value)}
                            className={getValidatedInputClass('majorExp.loanAmount')}
                            {...getValidationProps('majorExp.loanAmount')}
                        />
                        {renderFieldError('majorExp.loanAmount')}
                    </div> : null}
                    {isConfiguredVisible('majorExp.extraordinaryObligations') ? <div>
                        <InputLabel required={isConfiguredRequired('majorExp.extraordinaryObligations')}>Please list any extra obligations or circumstances you want the scholarship committee to consider.</InputLabel>
                        <textarea
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm"
                            rows={3}
                            data-testid="parent-financial-aid-extra-obligations"
                            value={formData.majorExp.extraordinaryObligations}
                            onChange={e => update(['majorExp', 'extraordinaryObligations'], e.target.value)}
                        />
                        {renderFieldError('majorExp.extraordinaryObligations')}
                    </div> : null}
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.majorExp.servicesToYST}
                                onChange={e => update(['majorExp', 'servicesToYST'], e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                            />
                            <span className="text-sm font-semibold text-irongrey">Are you willing to provide services to the school during the school year as part of your tuition assistance arrangement?</span>
                        </label>
                        {formData.majorExp.servicesToYST && (
                            <textarea
                                className="mt-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm"
                                rows={2}
                                value={formData.majorExp.servicesDescription}
                                onChange={e => update(['majorExp', 'servicesDescription'], e.target.value)}
                            />
                        )}
                    </div>
                </div>
            </Card>

            {['camps2025.0.childName', 'camps2025.0.campName', 'camps2025.0.fee'].some(isConfiguredVisible) ? <Card>
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4 flex items-center gap-2"><Sun size={16}/> {financialAidSummerCampLabel(financialAidDisplayYears.currentCampYear, 'Summer Camps')}</div>
                {formData.camps2025.map((c, i) => (
                    <div key={c.id} className="flex flex-wrap gap-4 mb-2">
                        {(() => {
                            const childNameKey = getArrayFieldValidationKey('camps2025', i, 'childName');
                            const campNameKey = getArrayFieldValidationKey('camps2025', i, 'campName');
                            const campFeeKey = getArrayFieldValidationKey('camps2025', i, 'fee');
                            const isStarterRow = i === 0;
                            const isChildNameVisible = isConfiguredVisible('camps2025.0.childName');
                            const isCampNameVisible = isConfiguredVisible('camps2025.0.campName');
                            const isCampFeeVisible = isConfiguredVisible('camps2025.0.fee');
                            return (
                                <>
                                    {isChildNameVisible ? <div className="flex-1">
                                        <Input
                                            className={`w-full ${getValidatedInputClass(childNameKey, '')}`}
                                            placeholder={isStarterRow && isConfiguredRequired('camps2025.0.childName') ? 'Child Name *' : 'Child Name'}
                                            data-testid={`parent-financial-aid-current-year-camp-child-name-${i}`}
                                            value={c.childName}
                                            onChange={e => updateRow('camps2025', c.id, 'childName', e.target.value)}
                                            {...getValidationProps(childNameKey)}
                                        />
                                        {renderFieldError(childNameKey)}
                                    </div> : null}
                                    {isCampNameVisible ? <div className="flex-1">
                                        <Input
                                            className={`w-full ${getValidatedInputClass(campNameKey, '')}`}
                                            placeholder={isStarterRow && isConfiguredRequired('camps2025.0.campName') ? 'Camp *' : 'Camp'}
                                            data-testid={`parent-financial-aid-current-year-camp-name-${i}`}
                                            value={c.campName}
                                            onChange={e => updateRow('camps2025', c.id, 'campName', e.target.value)}
                                            {...getValidationProps(campNameKey)}
                                        />
                                        {renderFieldError(campNameKey)}
                                    </div> : null}
                                    {isCampFeeVisible ? <div className="w-32">
                                        <CurrencyInput
                                            className={getValidatedInputClass(campFeeKey)}
                                            placeholder={isStarterRow && isConfiguredRequired('camps2025.0.fee') ? 'Fee *' : 'Fee'}
                                            data-testid={`parent-financial-aid-current-year-camp-fee-${i}`}
                                            value={c.fee}
                                            onChange={e => updateRow('camps2025', c.id, 'fee', e.target.value)}
                                            {...getValidationProps(campFeeKey)}
                                        />
                                        {renderFieldError(campFeeKey)}
                                    </div> : null}
                                </>
                            );
                        })()}
                        <button onClick={() => removeRow('camps2025', c.id)}><Trash2 className="text-strawberry" size={18}/></button>
                    </div>
                ))}
                <Button variant="secondary" onClick={() => addRow('camps2025')} icon={Plus} className="w-full mt-2 !py-2 text-xs">Add Camp</Button>
            </Card> : null}

            {['camps2024.0.childName', 'camps2024.0.campName', 'camps2024.0.fee'].some(isConfiguredVisible) ? (formData.camps2024.length > 0 ? (
                <div className="space-y-2">
                    <div className="px-1 text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2"><Sun size={16}/> {financialAidSummerCampLabel(financialAidDisplayYears.priorCampYear, 'Prior-Year Summer Camps')}</div>
                    {formData.camps2024.map((c, i) => (
                        <div key={c.id} className="flex flex-wrap gap-4 mb-2">
                            {(() => {
                                const childNameKey = getArrayFieldValidationKey('camps2024', i, 'childName');
                                const campNameKey = getArrayFieldValidationKey('camps2024', i, 'campName');
                                const campFeeKey = getArrayFieldValidationKey('camps2024', i, 'fee');
                                const isStarterRow = i === 0;
                                const isChildNameVisible = isConfiguredVisible('camps2024.0.childName');
                                const isCampNameVisible = isConfiguredVisible('camps2024.0.campName');
                                const isCampFeeVisible = isConfiguredVisible('camps2024.0.fee');
                                return (
                                    <>
                                        {isChildNameVisible ? <div className="flex-1">
                                            <Input
                                                className={`w-full ${getValidatedInputClass(childNameKey, '')}`}
                                                placeholder={isStarterRow && isConfiguredRequired('camps2024.0.childName') ? 'Child Name *' : 'Child Name'}
                                                data-testid={`parent-financial-aid-prior-year-camp-child-name-${i}`}
                                                value={c.childName}
                                                onChange={e => updateRow('camps2024', c.id, 'childName', e.target.value)}
                                                {...getValidationProps(childNameKey)}
                                            />
                                            {renderFieldError(childNameKey)}
                                        </div> : null}
                                        {isCampNameVisible ? <div className="flex-1">
                                            <Input
                                                className={`w-full ${getValidatedInputClass(campNameKey, '')}`}
                                                placeholder={isStarterRow && isConfiguredRequired('camps2024.0.campName') ? 'Camp *' : 'Camp'}
                                                data-testid={`parent-financial-aid-prior-year-camp-name-${i}`}
                                                value={c.campName}
                                                onChange={e => updateRow('camps2024', c.id, 'campName', e.target.value)}
                                                {...getValidationProps(campNameKey)}
                                            />
                                            {renderFieldError(campNameKey)}
                                        </div> : null}
                                        {isCampFeeVisible ? <div className="w-32">
                                            <CurrencyInput
                                                className={getValidatedInputClass(campFeeKey)}
                                                placeholder={isStarterRow && isConfiguredRequired('camps2024.0.fee') ? 'Fee *' : 'Fee'}
                                                value={c.fee}
                                                onChange={e => updateRow('camps2024', c.id, 'fee', e.target.value)}
                                                data-testid={`parent-financial-aid-prior-year-camp-fee-${i}`}
                                                {...getValidationProps(campFeeKey)}
                                            />
                                            {renderFieldError(campFeeKey)}
                                        </div> : null}
                                    </>
                                );
                            })()}
                            <button onClick={() => removeRow('camps2024', c.id)}><Trash2 className="text-strawberry" size={18}/></button>
                        </div>
                    ))}
                    <Button variant="secondary" onClick={() => addRow('camps2024')} icon={Plus} className="w-full mt-2 !py-2 text-xs">Add Prior-Year Camp</Button>
                </div>
            ) : (
                <Button variant="secondary" onClick={() => addRow('camps2024')} icon={Plus} className="w-full !py-3 text-xs border-dashed">Add Prior-Year Camp</Button>
            )) : null}

            <Card>
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4 flex items-center gap-2"><Plane size={16}/> Vacation</div>
                 <label className="flex items-center gap-3 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={formData.vacation.traveled}
                      onChange={e => update(['vacation', 'traveled'], e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                    />
                    <span className="text-sm font-semibold text-irongrey">Traveled out of state/country in last 12 months?</span>
                </label>
                {formData.vacation.traveled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2"><InputLabel>Destinations & Dates</InputLabel><Input value={formData.vacation.destinations} onChange={e => update(['vacation', 'destinations'], e.target.value)} /></div>
                        {isConfiguredVisible('vacation.cost') ? <div>
                            <InputLabel required={isConfiguredRequired('vacation.cost')}>Total Cost</InputLabel>
                            <CurrencyInput
                                value={formData.vacation.cost}
                                onChange={e => update(['vacation', 'cost'], e.target.value)}
                                className={getValidatedInputClass('vacation.cost')}
                                {...getValidationProps('vacation.cost')}
                            />
                            {renderFieldError('vacation.cost')}
                        </div> : null}
                        <div className="flex gap-6 items-end pb-2">
                            <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={formData.vacation.parentsTraveled}
                                  onChange={e => update(['vacation', 'parentsTraveled'], e.target.checked)}
                                  className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                />
                                <span className="text-sm">Parents</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={formData.vacation.childrenTraveled}
                                  onChange={e => update(['vacation', 'childrenTraveled'], e.target.checked)}
                                  className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                />
                                <span className="text-sm">Children</span>
                            </label>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );

    const renderTuitionStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500 space-y-6">
            <SectionHeader icon={FileText} title="Tuition Contract & Request" />
            <Card className="bg-jungle/5 border-jungle/10">
                <div className="flex justify-between items-center mb-6">
                    <div className="text-lg font-bold text-jungle">Tuition Contract Reference</div>
                    <StatusBadge status="Verified" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    <div><div className="text-xs font-bold text-irongrey/40 uppercase">Children</div><div className="text-2xl font-bold text-irongrey">{formData.contract.childrenCount}</div></div>
                    <div><div className="text-xs font-bold text-irongrey/40 uppercase">Total Tuition</div><div className="text-2xl font-bold text-irongrey">${cleanNumber(formData.contract.totalTuition).toLocaleString()}</div></div>
                    <div><div className="text-xs font-bold text-irongrey/40 uppercase">Fees</div><div className="text-2xl font-bold text-irongrey">${cleanNumber(formData.contract.totalFees).toLocaleString()}</div></div>
                    <div><div className="text-xs font-bold text-jungle uppercase">Less Preschool tuition</div><div className="text-2xl font-bold text-jungle">${cleanNumber(formData.contract.eligibleTuition).toLocaleString()}</div></div>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 text-left">
                    <div>
                        <InputLabel required>Children</InputLabel>
                        <Input
                            type="number"
                            min={0}
                            value={formData.contract.childrenCount}
                            onChange={e => update(['contract', 'childrenCount'], Number(e.target.value))}
                            className={getValidatedInputClass('contract.childrenCount')}
                            data-testid="parent-financial-aid-contract-children-count"
                            {...getValidationProps('contract.childrenCount')}
                        />
                        {renderFieldError('contract.childrenCount')}
                    </div>
                    <div>
                        <InputLabel required>Total Tuition</InputLabel>
                        <CurrencyInput
                            value={formData.contract.totalTuition}
                            onChange={e => update(['contract', 'totalTuition'], e.target.value)}
                            className={getValidatedInputClass('contract.totalTuition')}
                            data-testid="parent-financial-aid-contract-total-tuition"
                            {...getValidationProps('contract.totalTuition')}
                        />
                        {renderFieldError('contract.totalTuition')}
                    </div>
                    <div>
                        <InputLabel required>Total Fees</InputLabel>
                        <CurrencyInput
                            value={formData.contract.totalFees}
                            onChange={e => update(['contract', 'totalFees'], e.target.value)}
                            className={getValidatedInputClass('contract.totalFees')}
                            data-testid="parent-financial-aid-contract-total-fees"
                            {...getValidationProps('contract.totalFees')}
                        />
                        {renderFieldError('contract.totalFees')}
                    </div>
                    <div>
                        <InputLabel required>Less Preschool tuition</InputLabel>
                        <CurrencyInput
                            value={formData.contract.eligibleTuition}
                            onChange={e => update(['contract', 'eligibleTuition'], e.target.value)}
                            className={getValidatedInputClass('contract.eligibleTuition')}
                            data-testid="parent-financial-aid-contract-less-preschool-tuition"
                            {...getValidationProps('contract.eligibleTuition')}
                        />
                        {renderFieldError('contract.eligibleTuition')}
                    </div>
                </div>
            </Card>

            <Card>
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Scholarship Request</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <InputLabel required>Total Scholarship Requested</InputLabel>
                        <CurrencyInput
                            value={formData.request.requestedAmount}
                            onChange={e => update(['request', 'requestedAmount'], e.target.value)}
                            className={getValidatedInputClass('request.requestedAmount')}
                            data-testid="parent-financial-aid-requested-amount"
                            {...getValidationProps('request.requestedAmount')}
                        />
                        {renderFieldError('request.requestedAmount')}
                    </div>
                    {(isConfiguredVisible('request.offeredTuition') || isConfiguredVisible('request.offeredFees')) ? <div className="bg-amber-50 p-4 rounded-xl">
                        <p className="text-xs text-amber-600 font-bold mb-1">We hereby offer to make payments in the amount of:</p>
                        {isConfiguredVisible('request.offeredTuition') ? <>
                            <div className="flex justify-between items-end mb-2 gap-4">
                                <span className="text-sm font-medium">Tuition Payment{isConfiguredRequired('request.offeredTuition') ? <> <span className="text-strawberry">*</span></> : null}</span>
                                <div className="w-28">
                                    <CurrencyInput
                                        className={getValidatedInputClass('request.offeredTuition', 'bg-transparent text-right w-full outline-none border border-[#ffd9afc4] rounded-[4px] px-2 py-1 focus:ring-amber-200')}
                                        value={formData.request.offeredTuition}
                                        onChange={e => update(['request', 'offeredTuition'], e.target.value)}
                                        placeholder="0"
                                        data-testid="parent-financial-aid-offered-tuition"
                                        {...getValidationProps('request.offeredTuition')}
                                    />
                                </div>
                            </div>
                            {renderFieldError('request.offeredTuition')}
                        </> : null}
                        {isConfiguredVisible('request.offeredFees') ? <>
                            <div className="flex justify-between items-end">
                                <span className="text-sm font-medium">Fees Payment{isConfiguredRequired('request.offeredFees') ? <> <span className="text-strawberry">*</span></> : null}</span>
                                <div className="w-28">
                                    <CurrencyInput
                                        className={getValidatedInputClass('request.offeredFees', 'bg-transparent text-right w-full outline-none border border-[#ffd9afc4] rounded-[4px] px-2 py-1 focus:ring-amber-200')}
                                        value={formData.request.offeredFees}
                                        onChange={e => update(['request', 'offeredFees'], e.target.value)}
                                        placeholder="0"
                                        data-testid="parent-financial-aid-offered-fees"
                                        {...getValidationProps('request.offeredFees')}
                                    />
                                </div>
                            </div>
                            {renderFieldError('request.offeredFees')}
                        </> : null}
                        <div className="mt-3 flex justify-between items-end border-t border-amber-200 pt-2">
                            <span className="text-sm font-semibold">Total Tuition and Fees:</span>
                            <span className="text-sm font-bold" data-testid="parent-financial-aid-offered-total">
                                ${(cleanNumber(formData.request.offeredTuition) + cleanNumber(formData.request.offeredFees)).toLocaleString()}
                            </span>
                        </div>
                    </div> : null}
                </div>
            </Card>
        </div>
    );

    const renderDocsStep = () => {
        const visibleDocuments = reconcileParentFinancialAidDocuments(
            formData.documents,
            activeConfiguredRequiredDocuments,
        );
        return (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500" data-testid="parent-financial-aid-documents-step">
            <SectionHeader icon={Upload} title="Required Documents" subtitle="Please upload PDF or Image files for each requirement." />
            {uploadError ? (
                <SupportErrorMessage className="mb-4">
                    <span>{uploadError.userMessage}</span>
                    <FinancialAidTechnicalSupportDetails report={uploadError} />
                </SupportErrorMessage>
            ) : null}
            {settingsModuleConfigQuery.isError ? (
                <SupportErrorMessage className="mb-4">
                    Unable to load school document settings right now. If the form asks for something unexpected, please save and try again in a moment.
                </SupportErrorMessage>
            ) : null}
            {isParentMoreInfoResponseMode ? (
                <section
                    className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4"
                    data-testid="parent-financial-aid-more-info-response-panel"
                >
                    <p className="text-sm font-extrabold text-amber-900">
                        Send your More Info response
                    </p>
                    {activeMoreInfoResponseRequestedInfo ? (
                        <p
                            className="mt-3 whitespace-pre-line rounded-xl border border-amber-200 bg-white p-3 text-sm font-semibold leading-6 text-irongrey"
                            data-testid="parent-financial-aid-more-info-response-request"
                        >
                            {activeMoreInfoResponseRequestedInfo}
                        </p>
                    ) : null}
                    <label className="mt-4 block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-900/70">
                            Message to financial aid office
                        </span>
                        <textarea
                            value={moreInfoResponseMessage}
                            onChange={(event) => setMoreInfoResponseMessage(event.target.value)}
                            rows={3}
                            className="mt-2 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-semibold leading-6 text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
                            data-testid="parent-financial-aid-more-info-response-message"
                            placeholder="Add a short note for the school, or leave blank after uploading the requested files."
                        />
                    </label>
                    <Button
                        type="button"
                        variant="primary"
                        className="mt-4 !py-2"
                        data-testid="parent-financial-aid-more-info-submit-response"
                        disabled={isSubmitting || !draftApplicationId}
                        onClick={() => { void handleSubmitMoreInfoResponse(); }}
                    >
                        {respondToInfoRequest.isPending ? 'Sending...' : 'Submit response'} <ChevronRight size={16} />
                    </Button>
                </section>
            ) : null}

            <div className="space-y-4">
                {isFinancialAidDocumentSettingsPending ? (
                    <p className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-xs font-semibold text-primary">
                        Loading the school document requirements...
                    </p>
                ) : null}
                {!isFinancialAidDocumentSettingsPending && visibleDocuments.length === 0 ? (
                    <p className="rounded-lg border border-jungle/20 bg-jungle/5 px-4 py-3 text-xs font-semibold text-jungle">
                        No financial aid documents are currently required by the school.
                    </p>
                ) : null}
                {visibleDocuments.map((doc) => {
                    const isUploading = uploadingDocId === doc.id;
                    const fileInputTestId = `parent-financial-aid-document-file-input-${doc.id}`;
                    const validationKey = `documents.${doc.id}`;
                    return (
                    <div
                        key={doc.id}
                        className={`bg-white border p-4 rounded-xl flex items-center justify-between shadow-sm gap-4 ${getValidationIssue(validationKey) ? 'border-strawberry/30 bg-strawberry/5' : 'border-gray-100'}`}
                        data-testid={`parent-financial-aid-document-slot-${doc.id}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${doc.uploaded ? 'bg-jungle/10 text-jungle' : 'bg-gray-100 text-irongrey/30'}`}>
                                {doc.uploaded ? <CheckCircle size={20} /> : <FileText size={20} />}
                            </div>
                            <div>
                                <div className="font-bold text-sm text-irongrey">{doc.name}</div>
                                <div
                                    className="text-xs text-irongrey/40"
                                    data-testid={`parent-financial-aid-document-status-${doc.id}`}
                                >
                                    {isUploading ? 'Uploading...' : doc.uploaded ? 'Uploaded successfully' : doc.required === false ? 'Optional' : 'Required'}
                                </div>
                                <div
                                    className="text-xs text-irongrey/40"
                                    data-testid={`parent-financial-aid-document-file-name-${doc.id}`}
                                >
                                    {doc.fileName ?? 'No file selected'}
                                </div>
                                {renderFieldError(validationKey)}
                            </div>
                        </div>
                        <div>
                            <input
                                id={fileInputTestId}
                                type="file"
                                className="hidden"
                                accept="application/pdf,image/jpeg,image/png"
                                data-testid={fileInputTestId}
                                onChange={(event) => {
                                    const selectedFile = event.currentTarget.files?.[0] ?? null;
                                    void handleDocumentFileChange(doc.id, selectedFile);
                                    event.currentTarget.value = '';
                                }}
                            />
                            <Button
                                variant={doc.uploaded ? 'secondary' : 'primary'}
                                className="!py-1.5 !px-4 !text-xs"
                                data-testid={`parent-financial-aid-document-upload-button-${doc.id}`}
                                disabled={isSubmitting}
                                onClick={() => {
                                    const input = document.getElementById(fileInputTestId) as HTMLInputElement | null;
                                    input?.click();
                                }}
                            >
                                {isUploading ? 'Uploading...' : doc.uploaded ? 'Replace' : 'Upload'}
                            </Button>
                        </div>
                    </div>
                )})}
            </div>
        </div>
        );
    };

    const renderReviewStep = () => {
        // Summary calculations for the review panel.
        const householdComposition = resolveParentGuardianHouseholdComposition(formData);
        const isFatherActive = isParentGuardianFormKeyActive(householdComposition, 'father');
        const isMotherActive = isParentGuardianFormKeyActive(householdComposition, 'mother');
        const totalIncome =
            (isFatherActive ? cleanNumber(formData.incomeFather.gross2024) : 0)
            + (isMotherActive ? cleanNumber(formData.incomeMother.gross2024) : 0);
        const totalExpenses = cleanNumber(formData.expenses.housing) + cleanNumber(formData.expenses.food) + cleanNumber(formData.expenses.tuition);
        const totalAssets =
            cleanNumber(formData.assets.purchasePrice) +
            cleanNumber(formData.savings.balance) +
            cleanNumber(formData.checking.balance) +
            formData.vehicles.reduce((sum, vehicle) => sum + cleanNumber(vehicle.value), 0) +
            formData.investments.reduce((sum, investment) => sum + cleanNumber(investment.value), 0) +
            formData.childrenAssets.reduce((sum, investment) => sum + cleanNumber(investment.value), 0);
        const totalLiabilities =
            cleanNumber(formData.assets.mortgageBalance) +
            cleanNumber(formData.expenses.studentLoans) +
            cleanNumber(formData.expenses.bankLoans) +
            cleanNumber(formData.majorExp.loanAmount) +
            formData.vehicles.reduce((sum, vehicle) => sum + cleanNumber(vehicle.debt), 0);
        const netWorth = totalAssets - totalLiabilities;
        const attestationKeys = [
            'signatures.attestations.certifiedAccurate',
            'signatures.attestations.authorizedVerification',
            'signatures.attestations.incomeChangeAgreement',
        ];
        const hasAttestationIssue = attestationKeys.some((key) => Boolean(getValidationIssue(key)));
        const fatherLabel = resolveParentGuardianDisplayLabel(householdComposition, 'father');
        const motherLabel = resolveParentGuardianDisplayLabel(householdComposition, 'mother');
        const fatherSignatureRequired = shouldParentFinancialAidParentRequireSignature(formData, 'father');
        const motherSignatureRequired = shouldParentFinancialAidParentRequireSignature(formData, 'mother');
        const showFatherSignature = fatherSignatureRequired || Boolean(formData.signatures.father);
        const showMotherSignature = motherSignatureRequired || Boolean(formData.signatures.mother);
        const fatherSignatureNeedsRedraw =
            fatherSignatureRequired && Boolean(formData.signatures.fatherCaptured) && !formData.signatures.father;
        const motherSignatureNeedsRedraw =
            motherSignatureRequired && Boolean(formData.signatures.motherCaptured) && !formData.signatures.mother;

        return (
            <div className="animate-in slide-in-from-bottom-4 fade-in duration-500 space-y-6">
                <SectionHeader icon={ShieldAlert} title="Review Application" subtitle="Please confirm all details before signing." />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="text-center !p-4">
                        <div className="text-xs font-bold text-irongrey/40 uppercase">Total Income</div>
                        <div className="text-xl font-bold text-jungle">${totalIncome.toLocaleString()}</div>
                    </Card>
                    <Card className="text-center !p-4">
                        <div className="text-xs font-bold text-irongrey/40 uppercase">Net Worth</div>
                        <div className="text-xl font-bold text-irongrey">${Math.round(netWorth).toLocaleString()}</div>
                    </Card>
                    <Card className="text-center !p-4">
                        <div className="text-xs font-bold text-irongrey/40 uppercase">Monthly Exp</div>
                        <div className="text-xl font-bold text-vibrant-coral">${totalExpenses.toLocaleString()}</div>
                    </Card>
                    <Card className="text-center !p-4">
                        <div className="text-xs font-bold text-irongrey/40 uppercase">Tuition Burden</div>
                        <div className="text-xl font-bold text-irongrey">${cleanNumber(formData.contract.totalTuition).toLocaleString()}</div>
                    </Card>
                </div>

                <div className={`bg-gray-50 rounded-xl p-6 border space-y-4 ${hasAttestationIssue ? 'border-strawberry/30' : 'border-gray-200'}`}>
                    <div>
                        <label className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.signatures.attestations.certifiedAccurate)}
                                onChange={e => update(['signatures', 'attestations', 'certifiedAccurate'], e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                {...getValidationProps('signatures.attestations.certifiedAccurate')}
                            />
                            <span className="text-sm font-medium">{resolveParentFinancialAidAttestationText(financialAidCertificationStatements, 'certifiedAccurate')} <span className="text-strawberry">*</span></span>
                        </label>
                        {renderFieldError('signatures.attestations.certifiedAccurate')}
                    </div>
                    <div>
                        <label className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.signatures.attestations.authorizedVerification)}
                                onChange={e => update(['signatures', 'attestations', 'authorizedVerification'], e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                {...getValidationProps('signatures.attestations.authorizedVerification')}
                            />
                            <span className="text-sm font-medium">{resolveParentFinancialAidAttestationText(financialAidCertificationStatements, 'authorizedVerification')} <span className="text-strawberry">*</span></span>
                        </label>
                        {renderFieldError('signatures.attestations.authorizedVerification')}
                    </div>
                    <div>
                        <label className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.signatures.attestations.incomeChangeAgreement)}
                                onChange={e => update(['signatures', 'attestations', 'incomeChangeAgreement'], e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                                {...getValidationProps('signatures.attestations.incomeChangeAgreement')}
                            />
                            <span className="text-sm font-medium">{resolveParentFinancialAidAttestationText(financialAidCertificationStatements, 'incomeChangeAgreement')} <span className="text-strawberry">*</span></span>
                        </label>
                        {renderFieldError('signatures.attestations.incomeChangeAgreement')}
                    </div>
                </div>

                {(!showFatherSignature || !showMotherSignature) ? (
                    <p className="text-xs font-medium text-irongrey/60">
                        Only completed parent or guardian sections require a signature. Blank second-parent sections are not required.
                    </p>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {showFatherSignature ? <div>
                        <SignaturePad label={`${fatherLabel}'s Signature${fatherSignatureRequired ? ' *' : ''}`} value={formData.signatures.father} onChange={v => update(['signatures', 'father'], v)} />
                        {fatherSignatureNeedsRedraw ? (
                            <p className="mt-2 text-xs font-semibold text-irongrey/60">
                                Please sign again before submitting. Saved drafts do not keep the signature drawing.
                            </p>
                        ) : null}
                        {renderFieldError('signatures.father')}
                    </div> : null}
                    {showMotherSignature ? <div>
                        <SignaturePad label={`${motherLabel}'s Signature${motherSignatureRequired ? ' *' : ''}`} value={formData.signatures.mother} onChange={v => update(['signatures', 'mother'], v)} />
                        {motherSignatureNeedsRedraw ? (
                            <p className="mt-2 text-xs font-semibold text-irongrey/60">
                                Please sign again before submitting. Saved drafts do not keep the signature drawing.
                            </p>
                        ) : null}
                        {renderFieldError('signatures.mother')}
                    </div> : null}
                </div>
            </div>
        );
    };

    const renderSuccess = () => (
        <div className="text-center py-20 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle size={48} className="text-primary" /></div>
            <h2 className="text-3xl font-bold text-irongrey mb-4 font-heading">
                {completionStatus === 'decision' ? 'Financial Aid Review Complete' : 'Application Submitted!'}
            </h2>
            <p className="text-irongrey/60 max-w-md mx-auto mb-8">
                {completionStatus === 'decision'
                    ? 'The school has reviewed this financial aid application. Please check the parent portal or contact the school office for next steps.'
                    : `Ref: ${submittedReference || 'FA-PENDING'}`}
            </p>
            <Button
                variant="secondary"
                icon={Download}
                className="w-full md:w-auto"
                data-testid="parent-financial-aid-download-copy-button"
                onClick={() => { void handleDownloadCopy(); }}
            >
                Download Copy
            </Button>
            {downloadError ? (
                <SupportErrorMessage className="mx-auto mt-6 max-w-xl text-left">
                    <span>{downloadError.userMessage}</span>
                    <FinancialAidTechnicalSupportDetails report={downloadError} />
                </SupportErrorMessage>
            ) : null}
        </div>
    );

    const steps = [
        renderParentsStep,
        renderIncomeStep,
        renderAssetsStep,
        renderExpensesStep,
        renderSiblingsStep,
        renderLifestyleStep,
        renderTuitionStep,
        renderDocsStep,
        renderReviewStep,
        renderSuccess
    ];
    const existingApplicationState = resolveParentFinancialAidExistingApplicationState(
        activeApplicationSummary,
        effectiveFinancialAidAcademicYear,
    );
    const showExistingApplicationLanding = Boolean(existingApplicationState) && currentStep < 9 && !isFormStarted;
    const showApplicationLanding =
        showExistingApplicationLanding ||
        (currentStep < 9 && !isFormStarted);
    const effectiveYearApplicationSummary = parentFinancialAidApplicationSummaries.find((summary) =>
        doFinancialAidAcademicYearsMatch(summary.academicYear, effectiveFinancialAidAcademicYear),
    ) ?? null;
    const canBeginCurrentYearApplication =
        !hasInviteEntryToken
        && !isInviteEntryBlocked
        && !isParentFinancialAidLandingLookupPending
        && !effectiveYearApplicationSummary
        && isFinancialAidAcademicYearConfigured
        && !isFinancialAidEntryCycleClosed
        // INT-568: while the family's saved draft from an earlier still-open
        // cycle is the actionable item, the primary action must resume that
        // draft. A blank start here would create an application for a
        // different school year than the one the parent is working on.
        && !activeApplicationIsResumablePriorYearDraft;
    const isSavingDraft = saveDraftMessage === 'Saving draft...';

    return (
        <div className="max-w-5xl mx-auto md:px-6 md:py-10 pb-32">
            <Modal
                isOpen={browserBackGuard.isPromptOpen}
                onClose={browserBackGuard.cancelNavigation}
                title="Leave this financial aid form?"
                description="Browser back can clear unsaved form data."
                footer={(
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={browserBackGuard.cancelNavigation}>
                            Stay on form
                        </Button>
                        <Button variant="danger" onClick={browserBackGuard.confirmNavigation}>
                            Leave page
                        </Button>
                    </div>
                )}
            >
                <p className="text-sm font-semibold leading-6 text-irongrey/70">
                    Using the browser back button can clear the information you have entered.
                    Use the form Back button when you want to move to an earlier step safely.
                </p>
            </Modal>
            {isFinancialAidEntryCycleClosed && !inviteEntryError ? (
                <SupportErrorMessage className="mb-6" data-testid="parent-financial-aid-closed-cycle-message">
                    The financial aid cycle is currently closed for {financialAidCycleClosedInlineLabel}. Please contact the school office if you need help.
                </SupportErrorMessage>
            ) : null}
            {inviteEntryError ? (
                <SupportErrorMessage className="mb-6" technicalDetails={inviteEntryError.technicalReport}>
                    <div>Invitation link issue: {inviteEntryError.userMessage}</div>
                    {isInviteCycleRetryableError ? (
                        <Button
                            type="button"
                            variant="secondary"
                            className="mt-3"
                            data-testid="parent-financial-aid-invite-cycle-retry"
                            disabled={isInviteCycleRetryPending}
                            onClick={handleRetryInviteCycleStatus}
                        >
                            {isInviteCycleRetryPending ? 'Checking...' : 'Try again'}
                        </Button>
                    ) : null}
                </SupportErrorMessage>
            ) : null}
            {inviteEntryMessage && !inviteEntryError ? (
                <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 text-sm font-semibold text-primary" data-testid="parent-financial-aid-invite-entry-banner">
                    {inviteEntryMessage}
                </div>
            ) : null}
            {hydrationBannerMessage ? (
                <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-800" data-testid="parent-financial-aid-hydration-banner">
                    <div className="flex items-start justify-between gap-4">
                        <span>{hydrationBannerMessage}</span>
                        <button
                            type="button"
                            aria-label="Dismiss prior application notice"
                            className="rounded-lg p-1 text-amber-800/70 transition hover:bg-amber-100 hover:text-amber-900"
                            data-testid="parent-financial-aid-hydration-banner-dismiss"
                            onClick={dismissHydrationBanner}
                        >
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            ) : null}
            {showApplicationLanding ? renderApplicationLanding() : (
                <>
                    {currentStep < 9 && !isParentMoreInfoResponseMode ? (
                        <ProgressBar currentStep={currentStep} onStepClick={goToProgressStep} />
                    ) : null}
                    {validationRequested && visibleValidationIssues.length > 0 ? (
                        <div
                            role="alert"
                            className="mb-6 rounded-2xl border border-strawberry/25 bg-strawberry/5 px-5 py-4 text-sm text-strawberry"
                            data-testid="parent-financial-aid-validation-summary"
                        >
                            <p className="font-extrabold">Please complete the required financial aid information before submitting.</p>
                            <ul className="mt-2 list-disc space-y-1 pl-5 font-semibold">
                                {visibleValidationIssues.slice(0, 6).map((issue) => (
                                    <li key={issue.key}>{issue.message}</li>
                                ))}
                            </ul>
                            {visibleValidationIssues.length > 6 ? (
                                <p className="mt-2 font-semibold">{visibleValidationIssues.length - 6} more required items need attention.</p>
                            ) : null}
                        </div>
                    ) : null}
                    {steps[currentStep]()}
                    <div className="border-t border-gray-100 mb-8 mt-12"></div>
                    {currentStep < 9 && saveDraftMessage ? (
                        <div
                            className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 text-sm font-semibold text-primary"
                            data-testid="parent-financial-aid-save-state"
                        >
                            {saveDraftMessage}
                        </div>
                    ) : null}
                    {currentStep < 9 && saveDraftError ? (
                        <SupportErrorMessage className="mb-6" data-testid="parent-financial-aid-save-state">
                            <span>{saveDraftError.userMessage}</span>
                            <FinancialAidTechnicalSupportDetails report={saveDraftError} />
                        </SupportErrorMessage>
                    ) : null}
                    {currentStep < 9 && submissionError ? (
                        <SupportErrorMessage className="mb-6">
                            <span>{formatParentFinancialAidSubmitErrorHeadline(submissionError)}</span>
                            <FinancialAidTechnicalSupportDetails report={submissionError} />
                        </SupportErrorMessage>
                    ) : null}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        {currentStep > 0 && currentStep < 9 && !isParentMoreInfoResponseMode ? (
                            <button onClick={prevStep} className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-200 rounded-xl text-irongrey font-semibold text-sm hover:bg-gray-50 transition-colors"><ChevronLeft size={16}/> Back</button>
                        ) : <div />}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                            {currentStep < 9 ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    data-testid="parent-financial-aid-save-continue-later"
                                    onClick={() => { void handleSaveAndContinueLater(); }}
                                    className="!py-3"
                                    disabled={isSubmitting || isSavingDraft || (!isParentMoreInfoResponseMode && isGenericInviteCycleClosed)}
                                >
                                    {isParentMoreInfoResponseMode
                                        ? 'Return to portal'
                                        : isSavingDraft ? 'Saving...' : 'Save & Continue Later'}
                                </Button>
                            ) : null}
                            {currentStep < 8 && !isParentMoreInfoResponseMode && (
                                <Button
                                    variant="primary"
                                    onClick={nextStep}
                                    className="!py-3"
                                    title={currentStepValidationIssues.length > 0 ? 'Complete the required fields on this step to continue' : undefined}
                                >
                                    Save & Continue <ChevronRight size={16}/>
                                </Button>
                            )}
                            {currentStep === 8 && !isParentMoreInfoResponseMode && (
                                <Button
                                    variant="primary"
                                    onClick={() => { void handleSubmitApplication(); }}
                                    className="!bg-jungle hover:!bg-primary !py-3"
                                    disabled={isSubmitting || isGenericInviteCycleClosed || (validationRequested && validationIssues.length > 0)}
                                >
                                    {isSubmitting ? 'Submitting…' : 'Submit Application'} <CheckCircle size={16}/>
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default StudentForm;
